import React, { useState, useEffect, useRef, useMemo } from 'react';
import { dispatchShare } from './utils/invoiceSharing';
import CanvasWorkspace from './components/CanvasWorkspace';
import GeneratorHeader from './components/GeneratorHeader';
import RightSidebar from './components/RightSidebar';
import { marketingApi } from './utils/api';
import { PromoCampaignPayload } from './components/PromoCampaignModal';
import MobileInvoicePreview from './components/MobileInvoicePreview';
import QRCode from 'qrcode';

import PagedTest from './components/PagedTest';
import LedgerView from './components/LedgerView';
import BarcodeScannerModal from './components/BarcodeScannerModal';
import { getInvoiceFormatConfig, buildFormatVariables, formatInvoiceNumber, getNextSerialNumber, SERIAL_TYPES } from './utils/invoiceNumber';
import { getDefaultSchemeForCountryAndRegion, computeTaxBreakdown } from './utils/taxSchemes';
import { amountToWords } from './utils/numberToWords';
 import PrintPreviewModal from './components/PrintPreviewModal';
 import TeamLoginScreen from './components/TeamLoginScreen';
 import LandingPage from './components/LandingPage';
 import POSView from './components/POSView';
 import TokenWaitPage from './components/TokenWaitPage';
 import EmployeesView from './components/EmployeesView';
 import QueueTrackPage from './components/QueueTrackPage';
 import QueueDisplayPage from './components/QueueDisplayPage';
 import InventoryView from './components/InventoryView';
import ClinicView from './components/Clinicview';
import ScanSessionPage from './components/ScanSessionPage';
import ClinicWaitPage from './components/Clinicwaitpage';
import QueueDeskPage from './components/QueueDeskPage';
import SubscriptionPlansView from './components/SubscriptionPlansView';
import KitchenDashboard from './components/KitchenDashboard';
import DisplayPage from './components/DisplayPage';
import PayBillPage from './components/PayBillPage';
import GymKiosk from './components/GymKiosk';
import EmployeeKiosk from './components/EmployeeKiosk';
import RemoteEnrollPage from './components/RemoteEnrollPage';
import RemoteAttendancePage from './components/RemoteAttendancePage';
import { getPermissions, ModuleId, ModuleStatus } from './utils/permissions';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import AboutPage from './components/AboutPage';
import PublicInvoiceView from './components/PublicInvoiceView';
import PrintInvoicePage from './components/PrintInvoicePage';
import BilingualInvoiceRenderer from './components/BilingualInvoiceRenderer';
import {
  printReceiptUSB,
  isUSBPrinterConnected,
  connectUSBPrinter,
  tryAutoReconnectUSB,
  getUSBPrinterName,
} from './utils/usbPrinter';
import { resolveClientLineAr, printReceiptBT, connectBluetoothPrinter, getBTPrinterName } from './utils/bluetoothPrinter';
import TableLayoutView from './components/TableLayoutView';
import { CheckCircle, Printer, ChevronDown, Plus, Smartphone, LogIn } from 'lucide-react';
import {
  ElementType,
  ColumnDefinition,
  TableRow,
  CanvasElement,
  PageSizePreset,
  PageConfig,
  PAGE_PRESETS,
  ClientAccount,
  InvoiceRecord,
  ActivityLog,
  ActiveTemplate,
  DiscountRule
} from './types';
import QuickInvoiceView, { getPageCss } from './components/QuickInvoiceView';
import BatchInvoiceGenerator from './components/BatchInvoiceGenerator';
import ProductCatalogView from './components/ProductCatalogView';
import Navbar from './components/Navbar';
import AuthScreen from './components/AuthScreen';
import {  getCurrencyDisplay, isAsciiSafeSymbol, getCurrencyFallbackName } from './utils/currency';
import { getStoredToken, getStoredUser, clearSession, fetchMe, fetchMeWithRefresh, AuthUser, saveSession, postLog } from './utils/authApi';
import { COUNTRY_CODES } from './utils/countryCodes';
import { getCountryCurrency } from './utils/countryCurrency';
import { CURRENCY_MAP } from './utils/currency';
import { TEMPLATE_PRESETS, getFreshPresets } from './data/templates';
import { generateQRMatrix } from './utils/generators';
import {
  INDUSTRY_CATEGORIES_METADATA,
  generateIndustryTemplate
} from './data/industryCategories';
import LeftSidebar from './components/LeftSidebar';
import ProfileView from './components/ProfileView';
import OnboardingPage from './components/OnboardingPage';
import WelcomeTrialModal from './components/WelcomeTrialModal';
import StorageOptionScreen from './components/StorageOptionScreen';
// Modular Webpage Dashboard Subviews
import { computeGroupedPageShifts, enforceNoOverlapBelowTable } from './utils/pdfLayout';
import BillsView from './components/BillsView';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import AccountsView from './components/AccountsView';
import LogsView from './components/LogsView';
import IAMView from './components/IAMView';
import nlp from 'compromise';
(window as any).__compromise_nlp__ = nlp;
import { 
  saveData, 
  loadData, 
  StorageKeys, 
  restoreFolderPermission 
} from './utils/storage';


// Google Drive Auth/Service Utilities
import {
  googleSignIn,
  googleSignOut,
  getOrCreateFolder,
  uploadInvoicePDF,
  uploadExcelToDrive,
  initAuth
} from './utils/drive';

import * as XLSX from 'xlsx';

import { addLogItem } from './utils/localDB';
const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
const APP_URL = window.location.origin; 
const loadCompanyProfile = () => {
  try {
    const raw = localStorage.getItem('cloudairambo_company_profile');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
// Clears one table's order directly in localStorage — TableLayoutView is
// unmounted while POS is open, so there's no live component to call
// setOrders() on. This mirrors the key it reads from (ORDERS_KEY_PREFIX +
// 'v1' in TableLayoutView.tsx) so the next time that screen mounts, the
// table reads back as vacant.
const clearTableOrderInStorage = (tableId: string) => {
  try {
    const raw = localStorage.getItem('cloudairambo_table_orders_v1');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && tableId in parsed) {
      delete parsed[tableId];
      localStorage.setItem('cloudairambo_table_orders_v1', JSON.stringify(parsed));
    }
  } catch {}
};
function UpgradeGate({ feature }: { feature: string }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#94a3b8', minHeight: 400 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{feature}</div>
      <div style={{ fontSize: 13 }}>This feature requires a Pro or Ultra plan.</div>
      <button
        onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}
        style={{ marginTop: 8, padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
      >
        ✨ Upgrade Plan
      </button>
    </div>
  );
}
// Helper to automatically identify if a column represents a serial number
const isSerialNoColumn = (col: ColumnDefinition) => {
  if (!col || !col.key || !col.header) return false;
  const k = col.key.toLowerCase();
  const h = col.header.toLowerCase();
  return k === 'sno' || k === 'serial' || k === 'index' || h.includes('s.no') || h.includes('sl.no') || h.includes('sr.no') || h.includes('serial no') || h === 'no';
};

// Generic safety net for PDF export — guarantees every element below the
// table maintains a minimum vertical gap from whatever sits directly
// above it in the same horizontal band. Runs independently of the
// caption-pairing/grouping heuristic, so even if two elements get
// wrongly merged into one flow group, they still can't render on top
// of each other.
function enforceSequentialSpacing(elements: any[], minGap = 12) {
  const overlapsX = (a: any, b: any) => {
    const aLeft = a.x || 0, aRight = aLeft + (a.width || 0);
    const bLeft = b.x || 0, bRight = bLeft + (b.width || 0);
    return aLeft < bRight && bLeft < aRight;
  };

  const sorted = [...elements].sort((a, b) => (a.y || 0) - (b.y || 0));
  const placed: any[] = [];

  sorted.forEach(el => {
    const elTop = el.y || 0;
    let requiredTop = elTop;
    placed.forEach(p => {
      if (overlapsX(el, p)) {
        const pBottom = (p.y || 0) + (p.height || 0);
        requiredTop = Math.max(requiredTop, pBottom + minGap);
      }
    });
    if (requiredTop > elTop) el.y = requiredTop;
    placed.push(el);
  });

  return elements;
}



export default function App() {

  const invoiceEmbedMode =
    new URLSearchParams(window.location.search).get('embed');

  const isInvoiceCanvasEmbed =
    invoiceEmbedMode === 'invoice-canvas';

  const isInvoiceLeftEmbed =
    invoiceEmbedMode === 'invoice-left';

  const isInvoiceRightEmbed =
  invoiceEmbedMode === 'invoice-right';

const isInvoiceHeaderEmbed =
  invoiceEmbedMode === 'invoice-header';

const isBatchGeneratorEmbed =
  invoiceEmbedMode === 'batch-generator';

const isAnyInvoiceEmbed =
  isInvoiceCanvasEmbed ||
  isInvoiceLeftEmbed ||
  isInvoiceRightEmbed ||
  isInvoiceHeaderEmbed;

const isAnyMobileEmbed =
  isAnyInvoiceEmbed ||
  isBatchGeneratorEmbed;

  // --- Master Tab Navigation ---
const [currentTab, setCurrentTab] = useState<'generator' | 'quick_invoice' | 'batch_invoice' | 'bills' | 'accounts' | 'logs' | 'profile' | 'products' | 'iam' | 'ledger' | 'pos' | 'tables' | 'dashboard' | 'employees' | 'clinic' | 'inventory' | 'fitness' | 'food'>(() => {
  if (isBatchGeneratorEmbed) {
    return 'batch_invoice';
  }

  if (isAnyInvoiceEmbed) {
    return 'generator';
  }

  const saved = localStorage.getItem('cloudairambo_last_tab');
  return (saved as any) || 'generator';
});
const [authUser, setAuthUser] = useState<AuthUser | null>(null);
const [pendingTableOrder, setPendingTableOrder] = useState<{ tableId: string; tableNumber: string; items: any[] } | null>(null);
const [selectedOutletFilter, setSelectedOutletFilter] = useState<number | null>(() => {
  const saved = localStorage.getItem('cloudairambo_outlet_filter');
  return saved ? Number(saved) : null;
});
const [outletsData, setOutletsData] = useState<{ id: number; name?: string; currency?: string; currency_display_mode?: string; modules?: string[]; suite_tier?: string | null }[]>([]);
const [outletsChecked, setOutletsChecked] = useState(false);
const [newOutletName, setNewOutletName] = useState('');
const [newOutletCountry, setNewOutletCountry] = useState('IN');
const [newOutletCurrency, setNewOutletCurrency] = useState('INR');
const [creatingOutlet, setCreatingOutlet] = useState(false);
const [moduleOutletGateFor, setModuleOutletGateFor] = useState<ModuleId | null>(null);
const [moduleOutletGateMissingAll, setModuleOutletGateMissingAll] = useState<ModuleId[]>([]);
const [selectedGateModules, setSelectedGateModules] = useState<ModuleId[]>([]);
const [newModuleOutletName, setNewModuleOutletName] = useState('');
const [creatingModuleOutlet, setCreatingModuleOutlet] = useState(false);
// Persisted per-account on the backend (User.skipped_module_gates) — not
// localStorage — so a resolved/skipped module gate never resurfaces on
// any browser or device once it's been handled once.
const [skippedModuleGates, setSkippedModuleGates] = useState<string[]>([]);
useEffect(() => {
  if ((authUser as any)?.skipped_module_gates) {
    setSkippedModuleGates((authUser as any).skipped_module_gates);
  }
}, [(authUser as any)?.skipped_module_gates]);

const persistSkippedModuleGate = async (moduleId: string) => {
  setSkippedModuleGates(prev => Array.from(new Set([...prev, moduleId])));
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  if (!jwt) return;
  try {
    await fetch(`${BACKEND}/auth/me/skipped-module-gates`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ module_id: moduleId }),
    });
  } catch (e) {
    console.warn('Failed to persist skipped module gate:', e);
  }
};

useEffect(() => {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  if (!jwt) return;
  fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${jwt}` } })
    .then(r => {
      if (!r.ok) throw new Error(`outlets fetch failed: ${r.status}`);
      return r.json();
    })
    .then(d => {
      if (Array.isArray(d)) setOutletsData(d);
      // Only mark "checked" on a real, successful response — otherwise
      // a failed/blocked request (403, network error, etc.) leaves
      // outletsData at [] and outletsChecked=true, which the onboarding
      // gate below reads as "this org genuinely has zero outlets" and
      // wrongly shows "Create your first outlet" even when outlets exist.
      setOutletsChecked(true);
    })
    .catch(err => {
      console.error('[outlets] fetch failed — NOT marking outletsChecked, so the onboarding gate stays closed instead of showing a false "no outlets" screen:', err);
    });
}, [authUser?.id]);

const handleCreateFirstOutlet = async () => {
  if (!newOutletName.trim()) return alert('Enter an outlet name.');
  setCreatingOutlet(true);
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/outlets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        name: newOutletName.trim(),
        country: newOutletCountry,
        currency: newOutletCurrency,
        invoice_prefix: '',
        modules: ['suite'],
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.id) throw new Error(data?.detail || 'Failed to create outlet');
    setOutletsData(prev => [...prev, { ...data, modules: data.modules_assigned || [] }]);
    setSelectedOutletFilter(data.id);
  } catch (e: any) {
    alert(e.message || 'Could not create outlet. Try again.');
  } finally {
    setCreatingOutlet(false);
  }
};

const handleCreateModuleOutlet = async () => {
  if (selectedGateModules.length === 0) return alert('Select at least one module for this outlet.');
  const trimmedName = newModuleOutletName.trim();
  if (!trimmedName) return alert('Enter an outlet name.');
  if (selectedGateModules.length > 1) {
    const moduleNames = selectedGateModules.join(', ');
    const confirmed = window.confirm(
      `This outlet will be tagged to MULTIPLE modules: ${moduleNames}.\n\n` +
      `Only do this if this outlet genuinely serves all of them (e.g. one physical branch running everything). ` +
      `If you meant to create separate outlets per module, click Cancel and create them one at a time.`
    );
    if (!confirmed) return;
  }
  if (outletsData.some(o => (o as any).name?.trim().toLowerCase() === trimmedName.toLowerCase())) {
    return alert(`An outlet named "${trimmedName}" already exists in your organization. Choose a different name.`);
  }
  setCreatingModuleOutlet(true);
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/outlets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        name: newModuleOutletName.trim(),
        country: newOutletCountry,
        currency: newOutletCurrency,
        invoice_prefix: '',
        modules: selectedGateModules,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.id) throw new Error(data?.detail || 'Failed to create outlet');
    setOutletsData(prev => [...prev, { ...data, modules: data.modules_assigned || [] }]);
    // Permanently mark every module set up on this outlet as resolved —
    // persisted on the account itself, so the gate can never resurface for
    // these modules again on any browser/device, even if outletsData ever
    // fails to reflect the assignment on a later refresh.
    selectedGateModules.forEach(persistSkippedModuleGate);
    setNewModuleOutletName('');
    // moduleOutletGateFor is recomputed by the gating effect below once
    // outletsData updates — don't clear it manually, so it can advance to
    // the next missing module (if any) instead of just closing.
  } catch (e: any) {
    alert(e.message || 'Could not create outlet. Try again.');
  } finally {
    setCreatingModuleOutlet(false);
  }
};

const handleSkipModuleOutletGate = () => {
  if (!moduleOutletGateFor) return;
  persistSkippedModuleGate(moduleOutletGateFor);
};

// Guards any outlet-scoped action (save invoice, add client, switch into
// a tab that needs an outlet) against running with outlet_id: null.
// owner/individual are already blocked by the full-screen "Create your
// first outlet" gate before any tab renders, so nothing more to do for
// them here — this only needs to catch non-owner roles (who can't create
// outlets themselves), plus the edge case where outletsData goes stale
// mid-session (e.g. the last outlet is deleted by another tab/user while
// someone is already sitting inside a tab).
const requireAtLeastOneOutlet = () => {
  if (!outletsChecked || outletsData.length > 0) return true;
  if (authUser?.role === 'owner' || authUser?.role === 'individual') return true;
  alert('There are no outlets set up yet. Ask your outlet owner or admin to create one before you can use this.');
  return false;
};

// Maps a tab to the module it needs an outlet for. Only owner/individual
// get force-blocked into creating one on open — staff can't create
// outlets, so they still just hit the plain alert from
// requireAtLeastOneOutlet() above instead of this gate.
const TAB_MODULE_MAP: Record<string, ModuleId> = {
  ledger: 'ledger' as ModuleId,
  clinic: 'queue' as ModuleId,
  employees: 'employees' as ModuleId,
  fitness: 'gym' as ModuleId,
  food: 'canteen' as ModuleId,
  pos: 'pos' as ModuleId,
};

// Forces outlet creation the instant an owner/individual opens a module
// tab that has no outlet assigned to it yet. This is separate from
// moduleOutletGateFor (which only fires for modules billing already
// marked active/trial, and offers a skip) — this one fires purely off
// "does any outlet carry this module", regardless of billing status,
// and never offers a skip, since the tab would otherwise render with
// nothing to scope data to.
const [tabOutletGateFor, setTabOutletGateFor] = useState<{ tab: string; moduleId: ModuleId } | null>(null);
const [tabGateOutletName, setTabGateOutletName] = useState('');
const [creatingTabGateOutlet, setCreatingTabGateOutlet] = useState(false);

const handleCreateTabGateOutlet = async () => {
  if (!tabOutletGateFor) return;
  if (!tabGateOutletName.trim()) return alert('Enter an outlet name.');
  setCreatingTabGateOutlet(true);
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/outlets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        name: tabGateOutletName.trim(),
        country: newOutletCountry,
        currency: newOutletCurrency,
        invoice_prefix: '',
        modules: [tabOutletGateFor.moduleId],
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.id) throw new Error(data?.detail || 'Failed to create outlet');
    setOutletsData(prev => [...prev, { ...data, modules: data.modules_assigned || [] }]);
    setSelectedOutletFilter(data.id);

    // Continue straight into the tab that triggered this, now that it
    // actually has an outlet to scope to.
    const targetTab = tabOutletGateFor.tab;
    setTabOutletGateFor(null);
    setTabGateOutletName('');
    localStorage.setItem('cloudairambo_last_tab', targetTab);
    setCurrentTab(targetTab as any);
    setSelectedElementId(null);
  } catch (e: any) {
    alert(e.message || 'Could not create outlet. Try again.');
  } finally {
    setCreatingTabGateOutlet(false);
  }
};

const [authChecked, setAuthChecked] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(() => new URLSearchParams(window.location.search).get('login') === '1');
  const [sessionTerminated, setSessionTerminated] = useState(false);

  // ── Global Multi-Device Session Heartbeat & Listener ─────────────────────
  useEffect(() => {
    // 1. Listen for immediate 401 events from any API call
    const handleSuperseded = () => setSessionTerminated(true);
    window.addEventListener('session-superseded', handleSuperseded);

    // 2. Background heartbeat check every 20 seconds
    const interval = setInterval(async () => {
      const token = localStorage.getItem('cloudairambo_jwt');
      if (!token) return;

      try {
        const res = await fetch(`${BACKEND}/auth/heartbeat`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 401) {
          const data = await res.json().catch(() => ({}));
          if (data.detail === 'SESSION_SUPERSEDED') {
            clearSession();
            setSessionTerminated(true);
          }
        }
      } catch {
        // Network offline — do not kick
      }
    }, 20000);

    return () => {
      window.removeEventListener('session-superseded', handleSuperseded);
      clearInterval(interval);
    };
  }, []);
const [showStorageOptions, setShowStorageOptions] = useState(false);
const [showOnboarding, setShowOnboarding] = useState(false);
const [onboardingChecked, setOnboardingChecked] = useState(false);
const [storageOption, setStorageOption] = useState<'local' | 'drive' | 'cloud' | null>(null);
useEffect(() => {
  const bootstrapApp = async () => {
    try {
      const freshUser = await fetchMeWithRefresh();
      if (!freshUser) {
        setAuthChecked(true);
        setOnboardingChecked(true);
        clearSession();
        setShowLanding(true);
        return;
      }

      setAuthUser(freshUser);
      if ((freshUser.role === 'owner' || freshUser.role === 'individual') && !freshUser.onboarding_completed) {
        setShowOnboarding(true);
      }

      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      if (jwt) {
        // Await company profile and modules TOGETHER before revealing the UI
        const [profileRes, modulesRes] = await Promise.all([
          freshUser.org_id
            ? fetch(`${BACKEND}/auth/company-profile`, { headers: { Authorization: `Bearer ${jwt}` } }).catch(() => null)
            : null,
          fetch(`${BACKEND}/billing/modules/status`, { headers: { Authorization: `Bearer ${jwt}` } }).catch(() => null),
        ]);

        if (profileRes && profileRes.ok) {
          const profile = await profileRes.json().catch(() => ({}));
          if (profile?.currency) {
            setActiveCurrency(profile.currency);
            localStorage.setItem('cloudairambo_active_currency', profile.currency);
          }
        }

        if (modulesRes && modulesRes.ok) {
          const data = await modulesRes.json().catch(() => ({}));
          const statuses = data?.statuses || {};
          if (data?.statuses) {
            setModuleStatuses(statuses);
            localStorage.setItem('cloudairambo_cached_module_statuses', JSON.stringify(statuses));
          }
          if (data?.trial_end_dates) setModuleTrialEndDates(data.trial_end_dates);
          if (data?.period_end_dates) setModulePeriodEndDates(data.period_end_dates);
          if (data?.outlet_counts) setModuleOutletCounts(data.outlet_counts);
          if (data?.seats_included && data?.seat_counts) {
            const limits: Record<string, number> = {};
            for (const id of Object.keys(data.seats_included)) {
              limits[id] = (data.seats_included[id] ?? 0) + (data.seat_counts[id] ?? 0);
            }
            setModuleSeatLimits(limits);
          }
          if (data?.suite) {
            setSuiteSeatLimit((data.suite.seats_included ?? 0) + (data.suite.seat_count ?? 0));
          } else {
            setSuiteSeatLimit(null);
          }

          const p = getPermissions(
            freshUser.role, freshUser.permissions,
            (freshUser as any)?.plan, (freshUser as any)?.plan_tier,
            (freshUser as any)?.plan_status, statuses
          );
          if (!p.canAccessGenerator) {
            if (freshUser.role === 'queue_member')         setCurrentTab('clinic');
            else if (freshUser.role === 'ledger_member')   setCurrentTab('ledger');
            else if (freshUser.role === 'employee_member') setCurrentTab('employees');
            else if (freshUser.role === 'gym_member')      setCurrentTab('fitness');
            else if (freshUser.role === 'canteen_member')  setCurrentTab('food');
            else if (freshUser.role === 'pos_member')      setCurrentTab('pos');
            else if (p.canAccessLedger)    setCurrentTab('ledger');
            else if (p.canAccessBills)     setCurrentTab('bills');
            else if (p.canAccessProducts)  setCurrentTab('products');
            else if (p.canAccessAccounts)  setCurrentTab('accounts');
          }
        }
      }
    } catch {
      clearSession();
      setShowLanding(true);
    } finally {
      // Only reveal the UI once ALL user, org, and module settings are loaded
      setAuthChecked(true);
      setOnboardingChecked(true);
    }
  };

  bootstrapApp();
}, []);
  // --- Bank Actions State ---

const [showLanding, setShowLanding] = useState(() => !localStorage.getItem('cloudairambo_jwt'));
const [showPlans, setShowPlans] = useState(false);
const [justFinishedOnboarding, setJustFinishedOnboarding] = useState(false);
const [showWelcomeTrial, setShowWelcomeTrial] = useState(false);
const [moduleStatuses, setModuleStatuses] = useState<Partial<Record<ModuleId, ModuleStatus>>>(() => {
  try {
    const cached = localStorage.getItem('cloudairambo_cached_module_statuses');
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
});

// If a module is active/trial but no outlet has been assigned to it yet,
// the module is functionally dead — nothing to bill against. Force setup.
useEffect(() => {
  if (!outletsChecked) return;
  if (!authUser || (authUser.role !== 'owner' && authUser.role !== 'individual')) return;
  const activeModuleIds = (Object.entries(moduleStatuses || {}) as [string, ModuleStatus][])
    .filter(([, s]) => s === 'active' || s === 'trial')
    .map(([id]) => id);
  const missing = activeModuleIds.filter(
    id => !outletsData.some(o => (o.modules || []).includes(id)) && !skippedModuleGates.includes(id)
  );
  setModuleOutletGateMissingAll(missing as ModuleId[]);
  setModuleOutletGateFor((missing[0] as ModuleId) || null);
}, [moduleStatuses, outletsData, outletsChecked, authUser?.id, skippedModuleGates]);

// Default the picker to just the module that triggered the gate.
useEffect(() => {
  setSelectedGateModules(moduleOutletGateFor ? [moduleOutletGateFor] : []);
}, [moduleOutletGateFor]);
const [moduleTrialEndDates, setModuleTrialEndDates] = useState<Record<string, string>>({});
const [modulePeriodEndDates, setModulePeriodEndDates] = useState<Record<string, string>>({});
const [moduleOutletCounts, setModuleOutletCounts] = useState<Record<string, number>>({});
const [moduleSeatLimits, setModuleSeatLimits] = useState<Record<string, number>>({});
const [suiteSeatLimit, setSuiteSeatLimit] = useState<number | null>(null);
// ── HOW THE PLANS MODAL OPENS ─────────────────────────────────────────────
// 1. Profile tab → ✨ Upgrade Plan button → onShowPlans={() => setShowPlans(true)}
// 2. QuickInvoiceView → upgrade nudge banner → Upgrade ✨ button
//    fires window.dispatchEvent(new CustomEvent('show-upgrade-plans'))
// 3. BillsView → locked feature buttons (Merge PDF, Excel, WhatsApp, Drive)
//    show alert — can be upgraded to fire the same custom event
// 4. Floating pill in bottom-right corner → onClick={() => setShowPlans(true)}
// 5. Landing page footer → Pricing link → onEnterApp() then Profile tab
// ─────────────────────────────────────────────────────────────────────────

useEffect(() => {
  const handler = () => setShowPlans(true);
  window.addEventListener('show-upgrade-plans', handler);
  return () => window.removeEventListener('show-upgrade-plans', handler);
}, []);

useEffect(() => {
  const handler = (e: any) => {
    const { count, limit, resetDate, plan, tier } = e.detail;
    const reset = new Date(resetDate.endsWith('Z') || resetDate.includes('+') ? resetDate : resetDate + 'Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const isBasic = plan === 'basic';
    const msg = isBasic
      ? `You've used all ${limit} invoices for this month.\n\nResets on ${reset}.\n\nUpgrade to Pro for unlimited invoices.`
      : `You've reached your ${limit} invoice limit this month (Pro · Individual).\n\nResets on ${reset}.\n\nUpgrade to Pro SMB/Enterprise for unlimited invoices.`;
    if (window.confirm(msg + '\n\nUpgrade now?')) {
      setShowPlans(true);
    }
  };
  window.addEventListener('invoice-limit-reached', handler);
  return () => window.removeEventListener('invoice-limit-reached', handler);
}, []);
const [showScannerModal, setShowScannerModal] = useState(false);
  // --- Client Accounts State ---
  const [accounts, setAccounts] = useState<ClientAccount[]>([]);
  // --- Global Discount Rules State ---
  const [discountRules, setDiscountRules] = useState<DiscountRule[]>([]);
const [quickInvoiceSelectedTemplate, setQuickInvoiceSelectedTemplate] = useState<string>('');
  // --- Bills & Invoices State ---
 const [bills, setBills] = useState<InvoiceRecord[]>([]);
 const [editingBillId, setEditingBillId] = useState<string | null>(null);

  // --- Activity Audit Logs State ---
  const [logs, setLogs] = useState<ActivityLog[]>([]);

  // --- Google Sync Authentication ---
  const [googleUser, setGoogleUser] = useState<any | null>(null);
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [isSyncingId, setIsSyncingId] = useState<string | null>(null);

  // --- Local Persistence Auto-sync Hook Observer ---
const [postSaveModal, setPostSaveModal] = useState<{
  invoice: InvoiceRecord;
  clientEmail: string;
  clientPhone: string;
} | null>(null);
// Set when the cashier clicks "+ Add Item" on the post-save modal — closes
// the modal and drops QuickInvoiceView into its existing edit-mode flow
// (same isEditMode/onUpdateInvoice path already built for editing saved
// bills), so the user sees the live preview and an "Update Invoice"
// button instead of a separate inline row editor.
const [editingQuickInvoice, setEditingQuickInvoice] = useState<InvoiceRecord | null>(null);
const [postSavePrinters, setPostSavePrinters] = useState<string[]>([]);
const [postSaveSelectedPrinter, setPostSaveSelectedPrinter] = useState('');
const [postSaveUseUSB, setPostSaveUseUSB] = useState(() => localStorage.getItem('cloudairambo_qi_usb_print') === 'true');
const [postSaveUseBT, setPostSaveUseBT] = useState(() => localStorage.getItem('cloudairambo_qi_bt_print') === 'true');
const [postSaveUsbConnected, setPostSaveUsbConnected] = useState(false);
const [postSaveBtConnected, setPostSaveBtConnected] = useState(() => !!getBTPrinterName());
const [showQIPrinterPicker, setShowQIPrinterPicker] = useState(false);
const [showAddItemEditor, setShowAddItemEditor] = useState(false);
const [addItemRows, setAddItemRows] = useState<{ id: string; description: string; qty: number; rate: number }[]>([]);
const [savingAddedItems, setSavingAddedItems] = useState(false);

// Patches the already-saved invoice's table rows/total in place, then
// updates BOTH bills state and postSaveModal.invoice so the Print/
// WhatsApp/Email buttons already in this modal pick up the corrected
// version next click — and invalidates the shared PDF cache so they
// don't re-send the stale, already-downloaded PDF.
const handleSavePostSaveItems = async () => {
  if (!postSaveModal) return;
  const invoice = postSaveModal.invoice;
  const tableEl = (invoice.elements || []).find((el: any) => el.type === 'table');
  if (!tableEl) return;
  setSavingAddedItems(true);
  try {
    const newRows = addItemRows
      .filter(r => r.description.trim())
      .map((r, i) => ({
        id: r.id || `r-${Date.now()}-${i}`,
        values: { description: r.description, qty: r.qty, rate: r.rate, total: r.qty * r.rate },
      }));
    const updatedTableEl = { ...tableEl, rows: newRows };
    const newTotal = computeTableTotals(updatedTableEl as any).total;
    const updatedElements = (invoice.elements || []).map((el: any) => (el.type === 'table' ? updatedTableEl : el));

    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    await fetch(`${BACKEND}/invoices/${invoice.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ elements: updatedElements, amount: newTotal }),
    });
    invalidateInvoicePdfCache(invoice.id);
    setBills(prev => prev.map(b => (b.id === invoice.id ? { ...b, elements: updatedElements, amount: newTotal } : b)));
    setPostSaveModal(prev => prev ? { ...prev, invoice: { ...prev.invoice, elements: updatedElements, amount: newTotal } } : prev);
    setShowAddItemEditor(false);
  } catch (e) {
    alert('Could not update the invoice — try again.');
  } finally {
    setSavingAddedItems(false);
  }
};

useEffect(() => {
  if (!postSaveModal) return;
  fetch(`${BACKEND}/print/printers`, {
    headers: { Authorization: `Bearer ${getStoredToken()}` },
  })
    .then(r => r.json())
    .then(data => setPostSavePrinters(data.printers || []))
    .catch(() => {});
  tryAutoReconnectUSB().then(ok => setPostSaveUsbConnected(ok || isUSBPrinterConnected()));
  setPostSaveBtConnected(!!getBTPrinterName());
}, [postSaveModal]);

  // --- Custom Reusable Templates ---
const [customTemplates, setCustomTemplates] = useState<ActiveTemplate[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

useEffect(() => {
  const saved = localStorage.getItem('cloudairambo_storage_option');
  if (saved) setStorageOption(saved as any);
}, []);




useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const shortId = params.get('i');
  if (shortId) {
    fetch(`${BACKEND}/invoices/share/${shortId}`)
      .then(res => res.json())
      .then(data => setSharedInvoiceData(data))
      .catch(e => console.error('Failed to load invoice'));
    return;
  }
  // Old long URL fallback
  const invoiceParam = params.get('invoice');
  if (invoiceParam) {
    try {
      const decoded = JSON.parse(decodeURIComponent(atob(invoiceParam)));
      setSharedInvoiceData(decoded);
    } catch (e) {
      console.error('Invalid invoice link');
    }
  }
}, []);
// ── Load all data on app startup ──────────────────────────────────────────
useEffect(() => {
    const loadAll = async () => {
      // Restore computer folder permission if previously granted
      await restoreFolderPermission();

      const [
  savedBills,
  savedAccounts,
  savedLogs,
  savedTemplates,
  savedProfiles,
  savedDiscountRules,
] = await Promise.all([
  loadData(StorageKeys.INVOICES),
  loadData(StorageKeys.CLIENTS),
  loadData(StorageKeys.LOGS),
  loadData('cloudairambo_custom_templates'),
  loadData('cloudairambo_marketplace_profiles'),
  loadData('cloudairambo_discount_rules'),
]);



      // Load invoices from backend DB if user is logged in, else use localStorage
      const token = getStoredToken();
      if (token) {
        try {
          const { invoicesApi } = await import('./utils/api');
          const dbInvoices = await invoicesApi.list(selectedOutletFilter ?? undefined);
          if (dbInvoices?.length) {
           const mapped = dbInvoices.map((inv: any) => ({
  id: String(inv.id),
  invoiceNumber: inv.invoice_number,
  clientName: inv.client_name || 'General Client',
  clientEmail: inv.client_email || '',
  clientPhone: inv.client_phone || '',
  clientAddress: inv.client_address || '',
  amount: inv.amount || inv.total || 0,
  status: inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : 'Draft',
  currency: inv.currency || 'USD',
  amountPaid: inv.amount_paid || 0,
  balanceDue: inv.balance_due || 0,
  paymentDate: inv.payment_date || '',
  lastEditedAt: inv.last_edited_at || '',
  editLog: inv.edit_log || [],
  createdAt: inv.created_at,
  elements: inv.elements || [],
  pageSize: inv.page_size || 'A4',
  payment_mode: inv.payment_mode || '',
  isBilingual: inv.is_bilingual || false,
  translated_lang: inv.translated_lang || inv.translatedLang || undefined,
  translatedLang: inv.translated_lang || inv.translatedLang || undefined,
  line_items: inv.line_items || inv.items || [],
  outlet_id: inv.outlet_id ?? null,
  cancellation_reason: inv.cancellation_reason || '',
  cancelled_by: inv.cancelled_by || null,
  dynamicValuesAr: inv.dynamic_values_ar || {},
  dynamicFieldLabelsAr: inv.dynamic_field_labels_ar || {},
  // Bills-view thermal reprint needs these to render bilingual totals
  // (Subtotal/Tax/Discount/TOTAL in Arabic) — POS saves them on the
  // invoice at save time, but this mapping never picked them up, so
  // every bill loaded into Bills view was missing them even though the
  // backend had them all along.
  row_labels_ar: inv.row_labels_ar || undefined,
  tax_lines_ar: inv.tax_lines_ar || undefined,
  discount_lines_ar: inv.discount_lines_ar || undefined,
  // Same missing-mapping bug as above — backend has always returned
  // these (confirmed via psql), but this whitelist never picked them
  // up, so BillsView's getSellerBuyerInfo() always saw '' regardless
  // of what was actually saved, and silently fell back to the blended
  // "Total Tax" line instead of named CGST/SGST/IGST.
  seller_country: inv.seller_country || undefined,
  seller_region: inv.seller_region || undefined,
  buyer_region: inv.buyer_region || undefined,
  buyerTaxId: inv.buyer_tax_id || inv.buyerTaxId || undefined,
  buyer_tax_id: inv.buyer_tax_id || inv.buyerTaxId || undefined,
  publicToken: inv.public_token || undefined,
  showAmountInWords: inv.show_amount_in_words || false,
  qr_position: inv.qr_position || inv.auto_qr_position || undefined,
}));
            setBills(mapped);
          }
        } catch (e) {
          console.warn('DB invoice load failed, falling back to local:', e);
          if (savedBills?.length) setBills(savedBills);
        }
      } else {
        if (savedBills?.length) setBills(savedBills);
      }
      if (token) {
        try {
          const { clientsApi } = await import('./utils/api');
          const dbClients = await clientsApi.list(selectedOutletFilter ?? undefined);
          if (dbClients?.length) {
            const mapped = dbClients.map((c: any) => ({
              id: String(c.id),
              name: c.name,
              email: c.email || '',
              phone: c.phone || '',
              company: c.company || '',
              address: c.address || '',
              createdAt: c.created_at,
              visitCount: c.visit_count || 0,
              totalSpend: c.total_spend || 0,
              exemptFromAutoDiscount: c.exempt_from_auto_discount || false,
            }));
            setAccounts(mapped);
          }
        } catch (e) {
          if (savedAccounts?.length) setAccounts(savedAccounts);
        }
      } else {
        if (savedAccounts?.length) setAccounts(savedAccounts);
      }
      if (token) {
        try {
          const res = await fetch(`${BACKEND}/discount-rules/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const dbRules = await res.json();
            if (Array.isArray(dbRules)) {
              const mapped = dbRules.map((r: any) => ({
                id: String(r.id),
                label: r.label || '',
                metric: r.metric,
                threshold: r.threshold,
                type: r.type,
                value: r.value,
                visibleOnBill: r.visible_on_bill,
                createdAt: r.created_at,
                outletId: r.outlet_id ?? null,
              }));
              setDiscountRules(mapped);
            }
          }
        } catch (e) {
          console.warn('DB discount rules load failed:', e);
        }
      }

      if (savedLogs?.length)         setLogs(savedLogs);

      // Templates from DB take priority over local storage — mirrors the
      // bills/clients/discount-rules pattern above. Without this fetch,
      // handleSaveAsTemplate's backend POST was write-only: it saved to
      // the DB but nothing ever read templates back from it, so a refresh
      // only saw whatever was last written to local storage (which itself
      // was never being written to on save — see fix #2 below).
      if (token) {
        try {
          const tplRes = await fetch(`${BACKEND}/invoice-templates/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (tplRes.ok) {
            const dbTemplates = await tplRes.json();
            if (Array.isArray(dbTemplates) && dbTemplates.length) {
              const mappedTemplates: ActiveTemplate[] = dbTemplates.map((t: any) => ({
                id: String(t.id),
                name: t.name,
                elements: t.elements || [],
                pageSize: t.page_size || 'A4',
                category: t.business_type || 'Custom',
                description: 'User saved template',
                preview: '',
              }));
              setCustomTemplates(mappedTemplates);
              saveData('cloudairambo_custom_templates', mappedTemplates);
            } else if (savedTemplates?.length) {
              setCustomTemplates(savedTemplates);
            }
          } else if (savedTemplates?.length) {
            setCustomTemplates(savedTemplates);
          }
        } catch (e) {
          console.warn('Template DB load failed, falling back to local:', e);
          if (savedTemplates?.length) setCustomTemplates(savedTemplates);
        }
      } else if (savedTemplates?.length) {
        setCustomTemplates(savedTemplates);
      }
      if (savedProfiles) setMarketplaceProfiles(savedProfiles);
      setIsDataLoaded(true);
    };

    loadAll();
  }, []);

  useEffect(() => {
  if (currentTab !== 'bills') return;
  const token = getStoredToken();
  if (!token) return;
  import('./utils/api').then(({ invoicesApi }) => {
    invoicesApi.list(selectedOutletFilter ?? undefined).then((dbInvoices: any[]) => {
      if (dbInvoices?.length) {
        setBills(dbInvoices.map((inv: any) => ({
          id: String(inv.id),
          invoiceNumber: inv.invoice_number,
          clientName: inv.client_name || 'General Client',
          clientEmail: inv.client_email || '',
          clientPhone: inv.client_phone || '',
          clientAddress: inv.client_address || '',
          amount: inv.amount || inv.total || 0,
          status: inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : 'Draft',
          currency: inv.currency || 'USD',
          amountPaid: inv.amount_paid || 0,
          balanceDue: inv.balance_due || 0,
          paymentDate: inv.payment_date || '',
          lastEditedAt: inv.last_edited_at || '',
          editLog: inv.edit_log || [],
          createdAt: inv.created_at,
          elements: inv.elements || [],
          pageSize: inv.page_size || 'A4',
          payment_mode: inv.payment_mode || '',
          isBilingual: inv.is_bilingual || false,
          outlet_id: inv.outlet_id ?? null,
          cancellation_reason: inv.cancellation_reason || '',
          cancelled_by: inv.cancelled_by || null,
          dynamicValuesAr: inv.dynamic_values_ar || {},
          dynamicFieldLabelsAr: inv.dynamic_field_labels_ar || {},
          row_labels_ar: inv.row_labels_ar || undefined,
          tax_lines_ar: inv.tax_lines_ar || undefined,
          discount_lines_ar: inv.discount_lines_ar || undefined,
          seller_country: inv.seller_country || undefined,
          seller_region: inv.seller_region || undefined,
          buyer_region: inv.buyer_region || undefined,
          publicToken: inv.public_token || undefined,
  showAmountInWords: inv.show_amount_in_words || false,
        })));
      }
    }).catch(() => {});
  });
}, [currentTab, selectedOutletFilter]);

useEffect(() => {
  if (currentTab !== 'accounts' && currentTab !== 'quick_invoice') return;
  const token = getStoredToken();
  if (!token) return;
  import('./utils/api').then(({ clientsApi }) => {
    clientsApi.list(selectedOutletFilter ?? undefined).then((dbClients: any[]) => {
      if (dbClients) {
        const mapped = dbClients.map((c: any) => ({
          id: String(c.id),
          name: c.name,
          email: c.email || '',
          phone: c.phone || '',
          company: c.company || '',
          address: c.address || '',
          createdAt: c.created_at,
          visitCount: c.visit_count || 0,
          totalSpend: c.total_spend || 0,
          exemptFromAutoDiscount: c.exempt_from_auto_discount || false,
          outletId: c.outlet_id ?? null,
        }));
        setAccounts(mapped);
      }
    }).catch(() => {});
  });

  // Keep discount rules synced from database
  fetch(`${BACKEND}/discount-rules/`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => (r.ok ? r.json() : []))
    .then(dbRules => {
      if (Array.isArray(dbRules)) {
        setDiscountRules(
          dbRules.map((r: any) => ({
            id: String(r.id),
            label: r.label || '',
            metric: r.metric,
            threshold: r.threshold,
            type: r.type,
            value: r.value,
            visibleOnBill: r.visible_on_bill,
            createdAt: r.created_at,
            outletId: r.outlet_id ?? null,
          }))
        );
      }
    })
    .catch(() => {});
}, [currentTab, selectedOutletFilter]);
useEffect(() => {
  if (currentTab !== 'pos') return;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  if (!jwt) return;
  fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${jwt}` } })
    .then(r => r.json())
    .then(d => { if (Array.isArray(d)) setOutletsData(d); })
    .catch(() => {});
}, [currentTab]);
// --- Google Auth Session Initializer ---
  useEffect(() => {
    const unsubscribe = initAuth(
      (user, token) => {
        setGoogleUser(user);
        setGoogleToken(token);
        // Soft load event logs
        setLogs(prev => addLogItem(prev, 'SYSTEM_AUTH_SYNC', `Restored Google Drive sync session for user: ${user.email}`));
      },
      () => {
        setGoogleUser(null);
        setGoogleToken(null);
      }
    );
    return () => unsubscribe();
  }, []);

  // --- Core States ---
 const [elements, setElements] = useState<CanvasElement[]>(() => {
  try {
    const draft = localStorage.getItem('cloudairambo_canvas_draft');

    if (draft) {
      const parsed = JSON.parse(draft);
      if (parsed?.elements?.length) return parsed.elements;
    }
  } catch {}
  return JSON.parse(JSON.stringify(getFreshPresets()[0].elements));
});

const handleSendPromoCampaign = async (payload: PromoCampaignPayload) => {
  return marketingApi.sendPromo({
    recipients: payload.recipients.map(r => ({ id: r.id, name: r.name, email: r.email })),
    subject: payload.subject,
    message: payload.message,
    image_url: payload.imageUrl,
    image_file: payload.imageFile,
  });
};

  const [pageSize, setPageSize] = useState<PageSizePreset>(() => {
  try {
    const draft = localStorage.getItem('cloudairambo_canvas_draft');
    if (draft) {
      const parsed = JSON.parse(draft);
      if (parsed?.pageSize) return parsed.pageSize;
    }
  } catch {}
  return (localStorage.getItem('cloudairambo_default_page_size') as PageSizePreset) || 'A4';
});




const [planExpiredLock, setPlanExpiredLock] = useState(false);
const authUserRef = useRef<AuthUser | null>(null);
useEffect(() => { authUserRef.current = authUser; }, [authUser]);

useEffect(() => {
  if (!authUser) return;
  const interval = setInterval(async () => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    if (!jwt) return;
    try {
      const res = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } });
      if (!res.ok) return;
      const fresh = await res.json();

      const prevPlan = authUserRef.current?.plan;
      const wasPaid = prevPlan === 'pro' || prevPlan === 'ultra';
      const nowBasic = fresh.plan === 'basic';

      setAuthUser(fresh);

      // Refresh module trial/lock states FIRST so we know if an active module covers them
      let hasActiveModule = false;
      const modRes = await fetch(`${BACKEND}/billing/modules/status`, { headers: { Authorization: `Bearer ${jwt}` } });
      if (modRes.ok) {
        const modData = await modRes.json();
        if (modData?.statuses) {
          setModuleStatuses(modData.statuses);
          hasActiveModule = Object.values(modData.statuses).some((s: any) => s === 'active' || s === 'trial');
        }
        if (modData?.trial_end_dates) setModuleTrialEndDates(modData.trial_end_dates);
        if (modData?.period_end_dates) setModulePeriodEndDates(modData.period_end_dates);
        if (modData?.outlet_counts) setModuleOutletCounts(modData.outlet_counts);
        if (modData?.seats_included && modData?.seat_counts) {
          const limits: Record<string, number> = {};
          for (const id of Object.keys(modData.seats_included)) {
            limits[id] = (modData.seats_included[id] ?? 0) + (modData.seat_counts[id] ?? 0);
          }
          setModuleSeatLimits(limits);
        }
        if (modData?.suite) {
          setSuiteSeatLimit((modData.suite.seats_included ?? 0) + (modData.suite.seat_count ?? 0));
        } else {
          setSuiteSeatLimit(null);
        }
      }

      // Only nudge to resubscribe if downgraded AND no active module covers them
      if (wasPaid && nowBasic && !hasActiveModule) {
        setPlanExpiredLock(true);
        setShowPlans(true);
      }
    } catch (e) {
      console.warn('Plan refresh poll failed:', e);
    }
  }, 60000); // check every 60s

  return () => clearInterval(interval);
}, [authUser?.id]);
useEffect(() => {
  if (storageOption) localStorage.setItem('cloudairambo_storage_option', storageOption);
}, [storageOption]);

useEffect(() => {
  if (isDataLoaded && elements.length > 0 && !isResettingRef.current) {
    localStorage.setItem('cloudairambo_canvas_draft', JSON.stringify({ elements, pageSize }));
  }
}, [elements, pageSize, isDataLoaded]);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [copiedElement, setCopiedElement] = useState<CanvasElement | null>(null);
  const [pageBehavior, setPageBehavior] = useState<'fixed' | 'auto_expand' | 'multi_page'>(
    () => (localStorage.getItem('cloudairambo_default_page_behavior') as any) || 'auto_expand'
  );
  
  // --- Setup & Customizer guides ---
  const [zoom, setZoom] = useState<number>(0.85);

useEffect(() => {
  if (window.innerWidth < 768) {
    const availableWidth = window.innerWidth - 24; // small margin
    const fitZoom = Math.min(1, availableWidth / pageConfig.width);
    setZoom(parseFloat(fitZoom.toFixed(2)));
  }
}, []);
  const [gridSnapping, setGridSnapping] = useState<boolean>(true);
  const [showGridLines, setShowGridLines] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'add' | 'page' | 'guide'>('add');
  const [rightSidebarTab, setRightSidebarTab] = useState<'layers' | 'clients' | 'logs' | 'settings'>('layers');
  const [mobileLeftOpen, setMobileLeftOpen] = useState(false);
const [mobileRightOpen, setMobileRightOpen] = useState(false);
  const [rightClientSearch, setRightClientSearch] = useState('');
  const [draggedColumnIdx, setDraggedColumnIdx] = useState<number | null>(null);
  const [draggedOverColumnIdx, setDraggedOverColumnIdx] = useState<number | null>(null);
  const [lockedElementIds, setLockedElementIds] = useState<string[]>([]);
  useEffect(() => {
    if (selectedElementId) {
      setActiveTab('add');
    }
  }, [selectedElementId]);

  // --- Inline Typing / Edit Overlays ---
  const [inlineEditingId, setInlineEditingId] = useState<string | null>(null);
  const [inlineEditText, setInlineEditText] = useState<string>('');

  // --- Marketplace GSOUQMOVERS Profiles ---
  const [activeMarketplaceProfile, setActiveMarketplaceProfile] = useState<'agent' | 'agency' | 'builder' | 'developer'>('agent');
  const [marketplaceProfiles, setMarketplaceProfiles] = useState({
    agent: {
        businessName: "GSOUQ INDEPENDENT AGENT",
        representative: "Sarah Jenkins, Licensed Move Broker",
        license: "#AGT-2026-9042",
        contact: "+1 (555) 321-4455, sarah.j@gsouqmovers.com",
        address: "Dubai Marina Tower, Level 42",
        terms: "This broker commission is subject to direct distribution via GSOUQ Escrow protocol.\nPayment is split as per independent agent agreement guidelines. Vault transfers usually settle in 3 workdays.",
        color: "#d97706"
      },
      agency: {
        businessName: "GSOUQ ASSOCIATED AGENCIES",
        representative: "Arthur Vance, Managing Director",
        license: "#ACY-99201",
        contact: "+1 (555) 998-2190, broker@gsouqagency.co",
        address: "Broadgate Tower Level 12, London",
        terms: "This settlement is automatically debited from agent escrow pool directly inside GSOUQ ledger.\nB2B associated rules apply. Verified and recorded by Apex Alliance Lead Broker.",
        color: "#1e3a8a"
      },
      builder: {
        businessName: "GSOUQ CONTRACT AND BUILDERS",
        representative: "Raymond Sterling, Senior Surveyor",
        license: "#CIVIL-88219-X",
        contact: "+1 (555) 880-9281, build@gsouqbuilders.com",
        address: "Helix Structural Builders, Yard B, Houston TX",
        terms: "This progress draw is backed by certified engineer physical verification checks.\nNet 15 days upon certified submittal. Overdue accounts trigger automatic machinery demobilization clauses.",
        color: "#ea580c"
      },
      developer: {
        businessName: "GSOUQ DEVELOPMENTS INC",
        representative: "Julian Strata, Chief Executive",
        license: "#DEV-2026-X",
        contact: "+1 (555) 750-1049, legal@gsouqdevelopments.com",
        address: "Dubai Business Bay Gate, Tower B",
        terms: "Progress invoice subject to construction escrow drawing accounts matching GSOUQ standards.\nDrawings are due Net 30. Unresolved balances are escalated through statutory builder lien pathways.",
        color: "#047857"
      }
  
  });

  const updateMarketplaceProfile = (key: 'businessName' | 'representative' | 'license' | 'contact' | 'address' | 'terms' | 'color', val: string) => {
    const updated = {
      ...marketplaceProfiles,
      [activeMarketplaceProfile]: {
        ...marketplaceProfiles[activeMarketplaceProfile],
        [key]: val
      }
    };
    setMarketplaceProfiles(updated);
    saveData('cloudairambo_marketplace_profiles', updated);
  };

  const applyMarketplaceBranding = (
  roleToApply = activeMarketplaceProfile,
  profileOverride?: any,
) => {
    const profile =
      profileOverride ||
      marketplaceProfiles[roleToApply];

    if (!profile) return;

    if (profileOverride) {
      setMarketplaceProfiles((current) => ({
        ...current,
        [roleToApply]: profileOverride,
      }));
    }

    const nextElements = elements.map(el => {
      if (el.type === 'text') {
        const textContentLower = (el.textContent || '').toLowerCase();
        
        if (
          el.id === 'corp-logo' || 
          el.id === 'modern-comp-logo' || 
          el.id === 'studio-logo' ||
          textContentLower.includes('gsouq independent agent') ||
          textContentLower.includes('gsouq associated agencies') ||
          textContentLower.includes('gsouq contract and builders') ||
          textContentLower.includes('gsouq developments inc') ||
          textContentLower.includes('strata global') ||
          textContentLower.includes('studio eighty-eight')
        ) {
          return {
            ...el,
            textContent: profile.businessName.toUpperCase(),
            color: profile.color
          };
        }
        
        if (
          el.id === 'sender-addr-block' ||
          el.id === 'modern-sender-addr' ||
          el.id === 'studio-sender' ||
          textContentLower.startsWith('from agent representative:') ||
          textContentLower.startsWith('sender realty:') ||
          textContentLower.startsWith('contractor registry:') ||
          textContentLower.startsWith('sender entity:') ||
          textContentLower.startsWith('from:') ||
          textContentLower.startsWith('sender:')
        ) {
          let textStr = "";
          if (roleToApply === 'agent') {
            textStr = `FROM AGENT REPRESENTATIVE:\n${profile.businessName}\nRepresentative: ${profile.representative}\nLicense ID: ${profile.license}\nPhone/Email: ${profile.contact}\nAddress: ${profile.address}`;
          } else if (roleToApply === 'agency') {
            textStr = `SENDER REALTY:\n${profile.businessName}\nAuth Officer: ${profile.representative}\nRegistration: ${profile.license}\nPhone/Email: ${profile.contact}\nAddress: ${profile.address}`;
          } else if (roleToApply === 'builder') {
            textStr = `CONTRACTOR REGISTRY:\n${profile.businessName}\nContractor Lic: ${profile.license}\nRepresentative: ${profile.representative}\nPhone/Email: ${profile.contact}\nAddress: ${profile.address}`;
          } else {
            textStr = `SENDER ENTITY:\n${profile.businessName}\nAuth Representative: ${profile.representative}\nLic/Reg: ${profile.license}\nPhone/Email: ${profile.contact}\nAddress: ${profile.address}`;
          }
          return {
            ...el,
            textContent: textStr
          };
        }

        if (
          el.id === 'doc-notes-block' ||
          el.id === 'modern-notes' ||
          el.id === 'studio-notes' ||
          textContentLower.startsWith('notes:\n')
        ) {
          return {
            ...el,
            textContent: `NOTES:\n${profile.terms}`
          };
        }
      }

      if (el.type === 'signature') {
        if (el.id === 'representative-sig-line' || el.id === 'modern-sig' || el.id === 'studio-sig') {
          return {
            ...el,
            signatureName: profile.representative.split(',')[0],
            textContent: profile.representative.split(',')[1] || "Authorized representative",
            color: profile.color
          };
        }
      }

      if (el.type === 'table') {
        return { ...el, color: profile.color };
      }
      if (el.type === 'shape') {
        const updates: Partial<CanvasElement> = {};
        if (el.backgroundColor && el.backgroundColor !== 'transparent') {
          updates.backgroundColor = profile.color;
        }
        if (el.borderColor && el.borderColor !== 'transparent') {
          updates.borderColor = profile.color;
        }
        return { ...el, ...updates };
      }
      if (el.type === 'serial' || el.type === 'stamp' || el.type === 'signature') {
        return { ...el, color: profile.color };
      }

      return el;
    });

    setElements(nextElements);
    setCorporateColor(profile.color);
    saveToHistory(nextElements, pageSize);

    const updatedLogs = addLogItem(
      logs,
      'MARKETPLACE_BRANDING_APPLIED',
      `Applied ${roleToApply.toUpperCase()} branding per user profile directly to active canvas. Entity: ${profile.businessName}. Theme: ${profile.color}.`
    );
    setLogs(updatedLogs);
  };

const applyThemeColorDebounced = useRef<ReturnType<typeof setTimeout> | null>(null);
  // --- Corporate Brand Identity Theme ---
  const [corporateColor, setCorporateColor] = useState<string>('#4f46e5');
  const [activeCurrency, setActiveCurrency] = useState<string>(() => {

  const saved = localStorage.getItem('cloudairambo_active_currency');
  if (!saved) return 'USD';
  try {
    const parsed = JSON.parse(saved);
    if (typeof parsed === 'string') return parsed;
    if (parsed?.iso_code) return parsed.iso_code;
    return 'USD';
  } catch {
    return saved; // it's already a plain string
  }
});
const [currencyDisplayMode, setCurrencyDisplayMode] = useState<'symbol' | 'code'>(() => {
  return (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol';
});
const handleCurrencyDisplayModeChange = (mode: 'symbol' | 'code') => {
  setCurrencyDisplayMode(mode);
  localStorage.setItem('cloudairambo_currency_display_mode', mode);
};
const handleCurrencyChange = (raw: any) => {
  const currency = typeof raw === 'string' ? raw : (raw?.iso_code || raw?.code || 'USD');
  setActiveCurrency(currency);
  localStorage.setItem('cloudairambo_active_currency', currency);

  const jwt = localStorage.getItem('cloudairambo_jwt');
  if (jwt) {
    fetch(`${BACKEND}/auth/company-profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ currency }),
    }).catch(() => {});
  }
};

const effectiveCurrency = useMemo(() => {
  const o = outletsData.find(x => x.id === selectedOutletFilter);
  return o?.currency || activeCurrency;
}, [outletsData, selectedOutletFilter, activeCurrency]);

const effectiveCurrencyDisplayMode = useMemo<'symbol' | 'code'>(() => {
  const o = outletsData.find(x => x.id === selectedOutletFilter);
  return (o?.currency_display_mode === 'code' || o?.currency_display_mode === 'symbol')
    ? o.currency_display_mode
    : currencyDisplayMode;
}, [outletsData, selectedOutletFilter, currencyDisplayMode]);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>('All');

  // --- Smart Financial Recommended Settings States ---
  const [activeIndustryGroup, setActiveIndustryGroup] = useState<string>('Professional Services');
  const [activeIndustryName, setActiveIndustryName] = useState<string>('Modern Corporate Invoice (A4)');

  const getFinancialSuggestions = (group: string) => {
    switch (group) {
      case 'Retail & Commerce':
        return [
          { taxLabel: 'State Sales Tax (8.25%)', taxRate: 8.25, discountLabel: 'Loyalty Tier (10.0%)', discountRate: 10, description: 'Best for standard retail shop counters and physical goods sales.' },
          { taxLabel: 'National VAT (15.0%)', taxRate: 15, discountLabel: 'Bulk Order (15.0%)', discountRate: 15, description: 'Suitable for international or higher tax bracket commerce.' },
          { taxLabel: 'Luxury Goods Duty (20.0%)', taxRate: 20, discountLabel: 'VIP Promotion (25.0%)', discountRate: 25, description: 'Designed for high-end boutique and premium luxury collections.' }
        ];
      case 'Food & Hospitality':
        return [
          { taxLabel: 'Standard Food Tax (8.0%)', taxRate: 8, discountLabel: 'Group Catering (5.0%)', discountRate: 5, description: 'Applies to typical dine-in orders and family dinner arrangements.' },
          { taxLabel: 'Meal & Beverage Tax (10.0%)', taxRate: 10, discountLabel: 'Corporate Booking (10.0%)', discountRate: 10, description: 'Includes specific city hospitality taxes on food and drinks.' },
          { taxLabel: 'Service Fee + VAT (15.0%)', taxRate: 15, discountLabel: 'Seasonal Discount (15.0%)', discountRate: 15, description: 'Standard high-occupancy event and banquet venue billings.' }
        ];
      case 'Medical & Wellness':
        return [
          { taxLabel: 'Tax Exempt / Copay (0.0%)', taxRate: 0, discountLabel: 'Wellness Package (10.0%)', discountRate: 10, description: 'For standard clinical diagnoses, prescription drugs, and medical consults.' },
          { taxLabel: 'Wellness VAT (5.0%)', taxRate: 5, discountLabel: 'Prepaid Therapy (15.0%)', discountRate: 15, description: 'Great for massage treatments, fitness plans, or holistic therapy sessions.' },
          { taxLabel: 'Specialist Aesthetics (12.5%)', taxRate: 12.5, discountLabel: 'Membership Savings (20.0%)', discountRate: 20, description: 'Applies to medical spa treatments, select cosmetic services, and specialized therapies.' }
        ];
      case 'Professional Services':
        return [
          { taxLabel: 'Out-Of-State Exempt (0.0%)', taxRate: 0, discountLabel: 'Early Payment Saver (3.0%)', discountRate: 3, description: 'Applicable for cross-border services or non-taxable b2b consultancy.' },
          { taxLabel: 'Commonwealth GST (10.0%)', taxRate: 10, discountLabel: 'Quarterly Retainer (10.0%)', discountRate: 10, description: 'Standard rate for Australia, Singapore, and global GST/HST regimes.' },
          { taxLabel: 'Technical Advisory Tax (18.0%)', taxRate: 18, discountLabel: 'Contract Volume Discount (15.0%)', discountRate: 15, description: 'Applies to enterprise legal, advisory, and technical standard frameworks.' }
        ];
      case 'Logistics & Industrial':
        return [
          { taxLabel: 'Cross-Border Exempt (0.0%)', taxRate: 0, discountLabel: 'Frequent Shipper (5.0%)', discountRate: 5, description: 'Avoids duplicate cargo duties for commercial inter-state freight.' },
          { taxLabel: 'Industrial Transit Duty (6.5%)', taxRate: 6.5, discountLabel: 'Tonnage Contract (8.0%)', discountRate: 8, description: 'Typical regional freight handling and warehouse distribution fee.' },
          { taxLabel: 'Heavy Logistics Tax (12.0%)', taxRate: 12, discountLabel: 'Fleet Contract Tier (12.0%)', discountRate: 12, description: 'Applicable to oversized cargo processing and high-priority deliveries.' }
        ];
      case 'Tech, Design & Academic':
        return [
          { taxLabel: 'Academic License Exempt (0.0%)', taxRate: 0, discountLabel: 'Grant/Charity Special (25.0%)', discountRate: 25, description: 'For public educational services, tuition, or accredited scientific research.' },
          { taxLabel: 'Digital Licensing Tax (5.0%)', taxRate: 5, discountLabel: 'Annual Bundle Plan (15.0%)', discountRate: 15, description: 'Best for standard software usage licenses, font, or artwork assets.' },
          { taxLabel: 'Enterprise SaaS VAT (20.0%)', taxRate: 20, discountLabel: 'Developer Multi-Seat (20.0%)', discountRate: 20, description: 'Complies with standard EU digital services and cloud computing VAT rates.' }
        ];
      default:
        return [
          { taxLabel: 'Standard Service GST (10.0%)', taxRate: 10, discountLabel: 'General Loyalty Offer (5.0%)', discountRate: 5, description: 'A flexible combination of standard taxation and commercial discount.' }
        ];
    }
  };

  // --- Industry 180 Categories Catalog State ---
  const [industrySearchQuery, setIndustrySearchQuery] = useState<string>('');
  const [selectedIndustryGroup, setSelectedIndustryGroup] = useState<string>('All');

  const filtered180Niches = useMemo(() => {
    return INDUSTRY_CATEGORIES_METADATA.filter(niche => {
      const matchesSearch = niche.name.toLowerCase().includes(industrySearchQuery.toLowerCase()) || 
                            niche.group.toLowerCase().includes(industrySearchQuery.toLowerCase());
      const matchesGroup = selectedIndustryGroup === 'All' || niche.group === selectedIndustryGroup;
      return matchesSearch && matchesGroup;
    });
  }, [industrySearchQuery, selectedIndustryGroup]);

  // --- Undo/Redo State History Stack ---
  const [history, setHistory] = useState<string[]>([]);
  const [historyPointer, setHistoryPointer] = useState<number>(-1);

  // --- Drag and Resize State Tracker ---
  const [dragState, setDragState] = useState<{
    id: string;
    type: 'drag' | 'resize';
    resizeDirection?: 'nw' | 'ne' | 'sw' | 'se';
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
    initialW: number;
    initialH: number;
    initialFontSize?: number;
  } | null>(null);

  // --- Drawing / Pencil / Pen Mode States ---
  const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [bilingualMode, setBilingualMode] = useState<boolean>(false);
  const [bilingualDirection, setBilingualDirection] = useState<'en-ar' | 'ar-en'>('en-ar');
  const [currentPenColor, setCurrentPenColor] = useState<string>('#4f46e5');
  const [currentPenWidth, setCurrentPenWidth] = useState<number>(3);
  const [isDrawingActive, setIsDrawingActive] = useState<boolean>(false);
  const [currentPathPoints, setCurrentPathPoints] = useState<{ x: number; y: number }[]>([]);

  // --- Print Preview Overlay States ---
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState<boolean>(false);
  const [qrDataUrls, setQrDataUrls] = useState<Record<string, string>>({});
  const [sharedInvoiceData, setSharedInvoiceData] = useState<any>(null);
  const [hiddenElementIds, setHiddenElementIds] = useState<string[]>([]);
  const [printSelectedElementId, setPrintSelectedElementId] = useState<string | null>(null);
useEffect(() => {
  if (!isPrintPreviewOpen) return;
  elements.forEach(el => {
    if (el.type === 'qr' && el.qrValue && !qrDataUrls[el.id]) {
      generateQRDataUrl(el.qrValue, el.id);
    }
  });
}, [isPrintPreviewOpen]);
  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get active canvas layout config
  const pageConfig = useMemo(() => PAGE_PRESETS[pageSize], [pageSize]);

  const computedHeight = useMemo(() => {
  if (pageBehavior === 'auto_expand') {
    const maxBottom = elements.reduce((max, el) => {
      const h = el.type === 'table'
        ? (el.height || 0) + ((el.rows?.length || 0) * 20) + 80
        : (el.height || 0);
      return Math.max(max, (el.y || 0) + h);
    }, 0);
    return Math.max(pageConfig.height, Math.ceil(maxBottom + 60));
  } else if (pageBehavior === 'multi_page') {
      const maxBottom = elements.reduce((max, el) => Math.max(max, el.y + el.height), 0);
      const count = Math.max(1, Math.ceil((maxBottom + 20) / pageConfig.height));
      return pageConfig.height * count;
    }
    return pageConfig.height;
  }, [elements, pageConfig.height, pageBehavior]);

  const pageCount = useMemo(() => {
    if (pageBehavior === 'multi_page') {
      const maxBottom = elements.reduce((max, el) => Math.max(max, el.y + el.height), 0);
      return Math.max(1, Math.ceil((maxBottom + 20) / pageConfig.height));
    }
    return 1;
  }, [elements, pageConfig.height, pageBehavior]);

  // Current selected Element
  const selectedElement = useMemo(() => {
    return elements.find(el => el.id === selectedElementId) || null;
  }, [elements, selectedElementId]);
const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({
  brand: true,
  gsouq: false,
  templates: false,
  industries: false,
  customTemplates: true,
  components: true,
  draw: false,
});

const togglePanel = (key: string) => {
  setOpenPanels(prev => ({ ...prev, [key]: !prev[key] }));
};
  // --- Initialize History ---
  useEffect(() => {
    const serialized = JSON.stringify({ elements, pageSize });
    setHistory([serialized]);
    setHistoryPointer(0);
  }, []);

  // --- Save State to Undo History ---
  const saveToHistory = (newElements: CanvasElement[], newPageSize?: PageSizePreset) => {
    const nextSize = newPageSize || pageSize;
    const serialized = JSON.stringify({ elements: newElements, pageSize: nextSize });
    
    // Discard future branches if we did undo and edited
    const cleanedHistory = history.slice(0, historyPointer + 1);
    const updatedHistory = [...cleanedHistory, serialized];
    
    // Limit stack depth to 30
    if (updatedHistory.length > 30) {
      updatedHistory.shift();
    }
    
    setHistory(updatedHistory);
    setHistoryPointer(updatedHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyPointer > 0) {
      const idx = historyPointer - 1;
      const snapshot = JSON.parse(history[idx]);
      setElements(snapshot.elements);
      setPageSize(snapshot.pageSize);
      setHistoryPointer(idx);
      setSelectedElementId(null);
    }
  };

  const handleRedo = () => {
    if (historyPointer < history.length - 1) {
      const idx = historyPointer + 1;
      const snapshot = JSON.parse(history[idx]);
      setElements(snapshot.elements);
      setPageSize(snapshot.pageSize);
      setHistoryPointer(idx);
      setSelectedElementId(null);
    }
  };
const [translateTargetLang, setTranslateTargetLang] = useState<string>('ar');
  // --- Dynamic Freehand Drawing Mouse Handlers ---
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (!isDrawingMode || !canvasRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    setIsDrawingActive(true);
    setCurrentPathPoints([{ x, y }]);
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (!isDrawingActive || !isDrawingMode || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    const last = currentPathPoints[currentPathPoints.length - 1];
    if (last && Math.hypot(x - last.x, y - last.y) < 4) {
      return;
    }
    setCurrentPathPoints(prev => [...prev, { x, y }]);
  };

  const handleCanvasMouseUp = () => {
    if (!isDrawingActive) return;
    setIsDrawingActive(false);

    if (currentPathPoints.length < 2) {
      setCurrentPathPoints([]);
      return;
    }

    const xs = currentPathPoints.map(p => p.x);
    const ys = currentPathPoints.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const w = Math.max(10, maxX - minX);
    const h = Math.max(10, maxY - minY);

    const adjustedPoints = currentPathPoints.map(p => ({
      x: p.x - minX,
      y: p.y - minY
    }));

    const newDrawing: CanvasElement = {
      id: `drawing-${Date.now()}`,
      type: 'drawing',
      x: Math.round(minX),
      y: Math.round(minY),
      width: Math.round(w),
      height: Math.round(h),
      opacity: 100,
      zIndex: elements.length > 0 ? Math.max(...elements.map(el => el.zIndex)) + 1 : 1,
      rotation: 0,
      color: currentPenColor,
      borderWidth: currentPenWidth,
      drawingPoints: adjustedPoints
    };

    const nextElements = [...elements, newDrawing];
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);
    setSelectedElementId(newDrawing.id);
    setCurrentPathPoints([]);
  };

  const handleDuplicateElement = (id: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const target = elements.find(el => el.id === id);
    if (!target) return;
    const copy: CanvasElement = JSON.parse(JSON.stringify(target));
    copy.id = `${target.type}-${Date.now()}`;
    copy.x = Math.min(pageConfig.width - 100, target.x + 25);
    copy.y = Math.min(computedHeight - 50, target.y + 25);
    copy.zIndex = Math.max(...elements.map(el => el.zIndex), 0) + 1;
    
    const updated = [...elements, copy];
    setElements(updated);
    setSelectedElementId(copy.id);
    saveToHistory(updated, pageSize);
  };
const handleProductScanned = (product: { name: string; description: string; rate: number; unit: string }) => {
  const tableEl = elements.find(el => el.type === 'table');
  if (!tableEl) {
    alert('No invoice table on canvas. Add an Items Table element first.');
    return;
  }
  const tableId = tableEl.id;
  const oldBottom = tableEl.y + tableEl.height;

  const nextElements = elements.map(el => {
    if (el.id === tableId && el.rows) {
      const newRow = {
        id: `r-${Date.now()}`,
        values: {
          description: product.name,
          qty: 1,
          rate: product.rate,
          total: product.rate,
        }
      };
      const updatedRows = [...el.rows, newRow];
      const rowH = bilingualMode ? 36 : 22;
      return { ...el, rows: updatedRows, height: el.height + rowH };
    }
    return el;
  });

  const updatedTable = nextElements.find(el => el.id === tableId);
  if (updatedTable) {
    const newBottom = updatedTable.y + updatedTable.height;
    const delta = newBottom - oldBottom;
    const shifted = nextElements.map(el => {
      if (el.id !== tableId && el.y >= oldBottom - 10) {
        return { ...el, y: Math.round(el.y + delta) };
      }
      return el;
    });
    setElements(shifted);
    saveToHistory(shifted, pageSize);
  } else {
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);
  }
};
 const handleCanvasAddRow = (tableId: string) => {
  const tableEl = elements.find(el => el.id === tableId);
  if (!tableEl) return;
  const oldBottom = tableEl.y + tableEl.height;

  const nextElements = elements.map(el => {
    if (el.id === tableId && el.rows) {
      const newRowId = `r-${Date.now()}`;
      const newRow: TableRow = {
        id: newRowId,
        values: { description: 'New custom service line item', qty: 1, rate: 100, total: 100 }
      };
      const updatedRows = [...el.rows, newRow];
      const rowH = bilingualMode ? 36 : 22;
      const newHeight = el.height + rowH;
      return { ...el, rows: updatedRows, height: newHeight };
    }
    return el;
  });

  // Push elements below the table down
  const updatedTable = nextElements.find(el => el.id === tableId);
  if (updatedTable) {
    const newBottom = updatedTable.y + updatedTable.height;
    const delta = newBottom - oldBottom;
    const shifted = nextElements.map(el => {
      if (el.id !== tableId && el.y >= oldBottom - 10) {
        return { ...el, y: Math.round(el.y + delta) };
      }
      return el;
    });
    setElements(shifted);
    saveToHistory(shifted, pageSize);
  } else {
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);
  }
};

 const handleCanvasAddColumn = (tableId: string) => {
  const tableEl = elements.find(el => el.id === tableId);
  if (!tableEl) return;
  const oldRight = tableEl.x + tableEl.width;

  const nextElements = elements.map(el => {
    if (el.id === tableId && el.columns && el.rows) {
      const newColId = `c-${Date.now()}`;
      const newCol: ColumnDefinition = {
        id: newColId,
        header: 'New Col',
        width: 10,
        align: 'left',
        key: newColId
      };
      const updatedCols = [...el.columns, newCol];
      const total = updatedCols.length;
      const baseW = Math.floor(100 / total);
      const remainder = 100 - (baseW * total);
      const balancedCols = updatedCols.map((c, idx) => ({
        ...c,
        width: idx === 0 ? baseW + remainder : baseW
      }));
      const updatedRows = el.rows.map(row => ({
        ...row,
        values: { ...row.values, [newColId]: '' }
      }));

      return { ...el, columns: balancedCols, rows: updatedRows, width: el.width + 80 };
    }
    return el;
  });

  const updatedTable = nextElements.find(el => el.id === tableId);
  if (updatedTable) {
    const newRight = updatedTable.x + updatedTable.width;
    const delta = newRight - oldRight;
    const shifted = nextElements.map(el => {
      if (el.id !== tableId && el.x >= oldRight - 10) {
        return { ...el, x: Math.round(el.x + delta) };
      }
      return el;
    });
    setElements(shifted);
    saveToHistory(shifted, pageSize);
  } else {
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);
  }
};
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only capture keyboard shortcuts when on the invoice builder canvas tab
      if (currentTab !== 'generator') return;

      const activeEl = document.activeElement;
      if (activeEl) {
        const tagName = activeEl.tagName.toLowerCase();
        if (
          tagName === 'input' ||
          tagName === 'textarea' ||
          activeEl.getAttribute('contenteditable') === 'true' ||
          activeEl.closest('[contenteditable]') !== null
        ) {
          // Do not override standard browser native editing keys inside text boxes
          return;
        }
      }

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      // 1. Undo / Redo Actions
      if (isCmdOrCtrl && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
          const updatedLogs = addLogItem(logs, 'CANVAS_REDO', 'Triggered keyboard shortcut: Redo (Ctrl+Shift+Z)');
          setLogs(updatedLogs);
        } else {
          handleUndo();
          const updatedLogs = addLogItem(logs, 'CANVAS_UNDO', 'Triggered keyboard shortcut: Undo (Ctrl+Z)');
          setLogs(updatedLogs);
        }
        return;
      }

      if (isCmdOrCtrl && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
        const updatedLogs = addLogItem(logs, 'CANVAS_REDO', 'Triggered keyboard shortcut: Redo (Ctrl+Y)');
        setLogs(updatedLogs);
        return;
      }

      // 2. Copy Action
      if (isCmdOrCtrl && e.key.toLowerCase() === 'c') {
        if (selectedElementId) {
          const target = elements.find(el => el.id === selectedElementId);
          if (target) {
            e.preventDefault();
            setCopiedElement(target);
            const updatedLogs = addLogItem(logs, 'CANVAS_COPY', `Copied active workspace layer: ${target.type.toUpperCase()}`);
            setLogs(updatedLogs);
          }
        }
        return;
      }

      // 3. Paste Action
      if (isCmdOrCtrl && e.key.toLowerCase() === 'v') {
        if (copiedElement) {
          e.preventDefault();
          const pasted: CanvasElement = JSON.parse(JSON.stringify(copiedElement));
          pasted.id = `${copiedElement.type}-${Date.now()}`;
          // Offset pos slightly, constrained inside the visual canvas area
          pasted.x = Math.min(pageConfig.width - 100, (pasted.x ?? 50) + 25);
          pasted.y = Math.min(computedHeight - 50, (pasted.y ?? 50) + 25);
          pasted.zIndex = Math.max(...elements.map(el => el.zIndex), 0) + 1;

          const updated = [...elements, pasted];
          setElements(updated);
          setSelectedElementId(pasted.id);
          saveToHistory(updated, pageSize);

          const updatedLogs = addLogItem(logs, 'CANVAS_PASTE', `Pasted copy of active workspace layer: ${copiedElement.type.toUpperCase()}`);
          setLogs(updatedLogs);
        }
        return;
      }

      // 4. Delete / Backspace Action
      if (selectedElementId && e.key === 'Delete') {
  e.preventDefault();
  handleDeleteElement(selectedElementId);
}
if (selectedElementId && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
  e.preventDefault();
  const nudge = e.shiftKey ? 10 : 1;
  const el = elements.find(el => el.id === selectedElementId);
  if (el) {
    const newX = e.key === 'ArrowLeft' ? el.x - nudge : e.key === 'ArrowRight' ? el.x + nudge : el.x;
    const newY = e.key === 'ArrowUp' ? el.y - nudge : e.key === 'ArrowDown' ? el.y + nudge : el.y;
    updateElementProps(selectedElementId, { x: newX, y: newY });

  }
}
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedElementId,
    elements,
    pageSize,
    copiedElement,
    pageConfig,
    computedHeight,
    currentTab,
    logs,
    setLogs
  ]);

  // --- Templates Loader ---
  const handleLoadTemplateObj = async (template: ActiveTemplate) => {
    localStorage.removeItem('cloudairambo_canvas_draft');
    let elementsClone = JSON.parse(JSON.stringify(template.elements));

    // For POS Receipts, fetch a fresh number from backend
    if (template.pageSize?.startsWith('THERMAL') && authUser) {
      try {
        const { posCounterApi } = await import('./utils/api');
        // Find the prefix from the recno element
        const recnoEl = elementsClone.find((el: any) =>
          el.type === 'text' && el.textContent &&
          /^(Receipt No:|Bill No:|Order No:|Member No:|Invoice:)/.test(el.textContent.trim())
        );
        if (recnoEl) {
          const match = recnoEl.textContent.match(/^[^:]+:\s*([A-Z]+)-/);
          const prefix = match ? match[1] : 'POS';
          const digits = recnoEl.textContent.match(/\d+/)?.[0]?.length || 5;
          const freshNumber = await posCounterApi.next(prefix, digits);
          // Update all elements that contain this prefix
          elementsClone = elementsClone.map((el: any) => {
            if (el.type === 'text' && el.textContent?.includes(`${prefix}-`)) {
              return { ...el, textContent: el.textContent.replace(/[A-Z]+-\d+/, freshNumber) };
            }
            return el;
          });
        }
      } catch (e) {
        console.warn('POS counter fetch failed, using random:', e);
      }
    }

    setElements(elementsClone);
setPageSize(template.pageSize);
setSelectedElementId(null);
setInlineEditingId(null);
saveToHistory(elementsClone, template.pageSize);

setTimeout(() => {
  const scrollEl = document.querySelector('.overflow-auto');
  if (scrollEl) scrollEl.scrollTop = 0;
}, 50);

// Auto-enable bilingual if template has arabic content
const hasArabic = elementsClone.some((el: any) => 
  el.arabicContent || 
  el.columns?.some((c: any) => c.arabicHeader) ||
  el.rows?.some((r: any) => r.values?.arabicDescription)
);
if (hasArabic) setBilingualMode(true);
  };

  const loadTemplate = (templateIndex: number) => {
    const fresh = getFreshPresets()[templateIndex];
    if (!fresh) return;
    handleLoadTemplateObj(fresh);

    // Map standard category to industry group & name
    const cat = fresh.category;
    let group = 'Professional Services';
    if (cat === 'Retail') group = 'Retail & Commerce';
    else if (cat === 'Healthcare') group = 'Medical & Wellness';
    else if (cat === 'Logistics') group = 'Logistics & Industrial';
    else if (cat === 'Freelance') group = 'Tech, Design & Academic';
    else if (cat === 'Academic') group = 'Tech, Design & Academic';
    else if (cat === 'Marketplace') group = 'Professional Services';
    
    setActiveIndustryGroup(group);
    setActiveIndustryName(fresh.name);
  };
const getTableNaturalHeight = (el: CanvasElement): number => {
  if (el.type !== 'table') return el.height;
  const headerH = 28;
  const rowH = bilingualMode ? 36 : 22;
  const rowCount = el.rows?.length || 0;
  const totalsH = 80 + (el.taxRate ? 20 : 0) + (el.discountRate ? 20 : 0) + (el.shippingCost ? 20 : 0);
  return headerH + (rowCount * rowH) + totalsH + 16;
};
  // --- Table Calculations Helper ---
  const computeTableTotals = (element: CanvasElement) => {
    if (element.type !== 'table' || !element.rows) {
      return { subtotal: 0, tax: 0, discount: 0, shipping: 0, productTax: 0, total: 0 };
    }

    let subtotal = 0;
    const lineBases: { base: number; taxRate: number }[] = [];
    element.rows.forEach((row: any) => {
      const qty = parseFloat(String(row.values.qty)) || 0;
      const rate = parseFloat(String(row.values.rate)) || 0;
      const lineTotal = qty * rate;
      const taxRate = parseFloat(String(row.values.taxRate ?? row.values.tax_rate ?? 0)) || 0;
      const inclusive = !!(row.values.taxInclusive ?? row.values.tax_inclusive);
      const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
      lineBases.push({ base, taxRate });
      subtotal += base;
    });

    // Discount reduces the TAXABLE VALUE before tax is calculated —
    // standard GST/VAT invoicing math (tax on post-discount value).
    // Fall back to the legacy single `discountRate` field when the newer
    // `discounts` array is empty — older/simpler saved invoices only have
    // the scalar field, and without this fallback the PDF silently treats
    // the discount as zero even though the edit-modal correctly shows it.
    const effectiveDiscounts = ((element as any).discounts && (element as any).discounts.length > 0)
      ? (element as any).discounts
      : (element.discountRate ? [{ label: 'Discount', rate: element.discountRate }] : []);
    const discountAmount = effectiveDiscounts.filter((d: any) => d.rate > 0).reduce((sum: number, d: any) => sum + subtotal * (Math.abs(d.rate) / 100), 0);
    const discountRatio = subtotal > 0 ? Math.min(1, discountAmount / subtotal) : 0;
    const productTax = lineBases.reduce((sum, lb) => sum + lb.base * (1 - discountRatio) * (lb.taxRate / 100), 0);

    const taxableValue = subtotal - discountAmount;
    const taxAmount = ((element as any).taxes || []).filter((t: any) => t.rate > 0).reduce((sum: number, t: any) => sum + taxableValue * (Math.abs(t.rate) / 100), 0);
    const shipping = element.shippingCost || 0;
    const grandTotal = taxableValue + productTax + taxAmount + shipping;

    return { subtotal, tax: taxAmount, discount: discountAmount, shipping, productTax, total: grandTotal };
 };

  // --- Element Mutation Functions ---
const updateElementProps = (id: string, updatedProps: Partial<CanvasElement>) => {
  const oldEl = elements.find(el => el.id === id);
  const nextElements = elements.map(el => {
    if (el.id === id) return { ...el, ...updatedProps };
    return el;
  });

  // If a table's rows changed, push elements below it down
  if (oldEl?.type === 'table' && updatedProps.rows) {
    const oldRowCount = oldEl.rows?.length || 0;
    const newRowCount = updatedProps.rows.length;
    const rowH = 22;
    const delta = (newRowCount - oldRowCount) * rowH;
    const oldBottom = oldEl.y + oldEl.height;
    if (delta !== 0) {
      const shifted = nextElements.map(el => {
        if (el.id === id) return { ...el, ...updatedProps, height: (oldEl.height || 0) + Math.max(0, delta) };
        if (el.y >= oldBottom - 10) return { ...el, y: Math.round(el.y + delta) };
        return el;
      });
      setElements(shifted);
      return;
    }
  }

  setElements(nextElements);
};

  // Debouce-less end-of-drag database save
  const commitCurrentState = () => {
    saveToHistory(elements, pageSize);
  };

  // Delete Element
  const handleDeleteElement = (id: string) => {
    const nextElements = elements.filter(el => el.id !== id);
    setElements(nextElements);
    setSelectedElementId(null);
    setInlineEditingId(null);
    saveToHistory(nextElements, pageSize);
  };

  // One-click Theme Accent Color Changer
  const applyThemeColor = (colorHex: string) => {
    setCorporateColor(colorHex);
    const nextElements = elements.map(el => {
      if (el.type === 'table') {
        return { ...el, color: colorHex };
      }
      if (el.type === 'shape') {
        const updates: Partial<CanvasElement> = {};
        if (el.backgroundColor && el.backgroundColor !== 'transparent') {
          updates.backgroundColor = colorHex;
        }
        if (el.borderColor && el.borderColor !== 'transparent') {
          updates.borderColor = colorHex;
        }
        return { ...el, ...updates };
      }
      if (el.type === 'text') {
        // Safe check for primary text elements or custom header texts in presets
        if (el.color && el.color !== '#000000' && el.color !== '#ffffff' && el.color !== 'transparent' && !el.color.toLowerCase().startsWith('#1e') && !el.color.toLowerCase().startsWith('#0c')) {
          return { ...el, color: colorHex };
        }
      }
      if (el.type === 'serial') {
        return { ...el, color: colorHex };
      }
      if (el.type === 'signature') {
        return { ...el, color: colorHex };
      }
      if (el.type === 'stamp') {
        return { ...el, color: colorHex };
      }
      return el;
    });
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);

    // Dynamic logging of corporate branding recolor events!
    const updatedLogs = addLogItem(
      logs,
      'BRAND_THEME_APPLIED',
      `Modified corporate brand theme accent to Hex ${colorHex.toUpperCase()}. Instantly updated table headers, shapes, and stamps.`
    );
    setLogs(updatedLogs);
  };

  // Auto-Align & Even Distribution Helper
  const handleAutoAlign = (mode: 'horizontal-distribution' | 'vertical-distribution' | 'center-horizontal' | 'center-vertical') => {
    if (elements.length < 1) return;
    
    let nextElements = [...elements];
    const activeEl = elements.find(el => el.id === selectedElementId);
    
    if (mode === 'center-horizontal') {
      if (activeEl) {
        const newX = Math.round((pageConfig.width - activeEl.width) / 2);
        nextElements = elements.map(el => el.id === selectedElementId ? { ...el, x: newX } : el);
      } else {
        const pW = pageConfig.width;
        nextElements = elements.map(el => ({ ...el, x: Math.round((pW - el.width) / 2) }));
      }
    } else if (mode === 'center-vertical') {
      if (activeEl) {
        const newY = Math.round((computedHeight - activeEl.height) / 2);
        nextElements = elements.map(el => el.id === selectedElementId ? { ...el, y: newY } : el);
      } else {
        nextElements = elements.map(el => ({ ...el, y: Math.round((computedHeight - el.height) / 2) }));
      }
    } else if (mode === 'horizontal-distribution') {
      const targetType = activeEl ? activeEl.type : null;
      const filtered = elements.filter(el => !targetType || el.type === targetType);
      
      if (filtered.length >= 2) {
        const sorted = [...filtered].sort((a, b) => a.x - b.x);
        const startX = Math.max(20, sorted[0].x);
        const endX = Math.min(pageConfig.width - sorted[sorted.length - 1].width - 20, sorted[sorted.length - 1].x);
        const availableWidth = endX - startX;
        
        const sumW = sorted.slice(0, -1).reduce((acc, el) => acc + el.width, 0);
        const gap = filtered.length > 1 ? (availableWidth - sumW) / (filtered.length - 1) : 0;
        
        let currentX = startX;
        const idToNewX: Record<string, number> = {};
        
        sorted.forEach((el) => {
          idToNewX[el.id] = Math.round(currentX);
          currentX += el.width + gap;
        });
        
        nextElements = elements.map(el => {
          if (el.id in idToNewX) {
            return { ...el, x: idToNewX[el.id] };
          }
          return el;
        });
      }
    } else if (mode === 'vertical-distribution') {
      const targetType = activeEl ? activeEl.type : null;
      const filtered = elements.filter(el => !targetType || el.type === targetType);
      
      if (filtered.length >= 2) {
        const sorted = [...filtered].sort((a, b) => a.y - b.y);
        const startY = Math.max(20, sorted[0].y);
        const endY = Math.min(computedHeight - sorted[sorted.length - 1].height - 20, sorted[sorted.length - 1].y);
        const availableHeight = endY - startY;
        
        const sumH = sorted.slice(0, -1).reduce((acc, el) => acc + el.height, 0);
        const gap = filtered.length > 1 ? (availableHeight - sumH) / (filtered.length - 1) : 0;
        
        let currentY = startY;
        const idToNewY: Record<string, number> = {};
        
        sorted.forEach((el) => {
          idToNewY[el.id] = Math.round(currentY);
          currentY += el.height + gap;
        });
        
        nextElements = elements.map(el => {
          if (el.id in idToNewY) {
            return { ...el, y: idToNewY[el.id] };
          }
          return el;
        });
      }
    }
    
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);
  };

  // Change Stacking Order
  const handleZIndex = (id: string, action: 'front' | 'back' | 'up' | 'down') => {
    const maxZ = elements.length > 0 ? Math.max(...elements.map(el => el.zIndex)) : 0;
    const minZ = elements.length > 0 ? Math.min(...elements.map(el => el.zIndex)) : 0;
    
    const nextElements = elements.map(el => {
      if (el.id === id) {
        if (action === 'front') return { ...el, zIndex: maxZ + 1 };
        if (action === 'back') return { ...el, zIndex: Math.max(0, minZ - 1) };
        if (action === 'up') return { ...el, zIndex: el.zIndex + 1 };
        if (action === 'down') return { ...el, zIndex: Math.max(0, el.zIndex - 1) };
      }
      return el;
    });
    setElements(nextElements);
    saveToHistory(nextElements, pageSize);
  };

  // --- Add New Elements to Canvas ---
  const handleAddElement = (type: ElementType, customProps: Partial<CanvasElement> = {}) => {
    // Position near the visible viewport center or simple coordinates
    const defaultX = Math.round(pageConfig.width / 4);
    const defaultY = Math.round(pageConfig.height / 4);
    
    const matchMaxZ = elements.reduce((max, el) => Math.max(max, el.zIndex), 0);
    
    let w = 150;
    let h = 80;
    let defaultText = '';
    
    if (type === 'text') {
      w = 260; h = 50;
      defaultText = "Double-click to type label details...";
    } else if (type === 'shape') {
      w = 300; h = 4;
    } else if (type === 'qr') {
      w = 110; h = 110;
    } else if (type === 'stamp') {
      w = 160; h = 60;
    } else if (type === 'serial') {
      w = 200; h = 40;
    } else if (type === 'signature') {
      w = 220; h = 65;
      defaultText = "Authorized Signature";
    } else if (type === 'table') {
      w = 720; h = 240;
    }

    const newEl: CanvasElement = {
      id: `${type}-${Date.now()}`,
      type,
      x: defaultX,
      y: defaultY,
      width: w,
      height: h,
      opacity: 100,
      zIndex: matchMaxZ + 1,
      rotation: 0,
      
      // Default styles
      color: type === 'stamp' ? '#dc2626' : '#000000',
      backgroundColor: type === 'shape' ? '#000000' : 'transparent',
      borderColor: 'transparent',
      borderWidth: 0,
      borderRadius: 0,
      fontSize: type === 'text' ? 14 : type === 'signature' ? 24 : 12,
      fontFamily: type === 'signature' ? 'Alex Brush' : 'Inter',
      fontWeight: 'normal',
      textAlign: 'left',
      letterSpacing: 0,
      lineHeight: 1.4,
      
      // Values
      textContent: defaultText,
      qrValue: type === 'qr' ? 'https://yourapp.com/invoice/view' : undefined,
      
      stampText: type === 'stamp' ? 'APPROVED' : undefined,
      stampDraft: true,
      stampShape: type === 'stamp' ? 'rectangle' : undefined,
      
      // Tables default
      columns: type === 'table' ? [
        { id: 'c1', header: 'Item', width: 50, align: 'left', key: 'description' },
        { id: 'c2', header: 'Qty', width: 15, align: 'center', key: 'qty' },
        { id: 'c3', header: 'Price', width: 15, align: 'right', key: 'rate' },
        { id: 'c4', header: 'Total', width: 20, align: 'right', key: 'total' }

      ] : undefined,
      rows: type === 'table' ? [
        { id: 'r1', values: { description: 'Custom Component Drafting Services', qty: 1, rate: 250 } }
      ] : undefined,
      taxRate: type === 'table' ? 8.25 : undefined,
      discountRate: type === 'table' ? 0 : undefined,

      // Serial default
      serialSettings: type === 'serial' ? {
  prefix: "SN-",
  suffix: "",
  digits: 4,
  current: 1,
  step: 1,
  serialType: 'product'
} : undefined,
      serialStyle: type === 'serial' ? 'stamped_red' : undefined,

      // Signature default
      signatureName: type === 'signature' ? 'Johnathan Doe' : undefined,
      signatureStyle: type === 'signature' ? 'cursive' : undefined,

      ...customProps
    };

    const updatedElements = [...elements, newEl];
    setElements(updatedElements);
    setSelectedElementId(newEl.id);
    setRightSidebarTab('settings');
    setInlineEditingId(null);
    saveToHistory(updatedElements, pageSize);
  };

  // --- Mobile Invoice WebView Bridge ---
  useEffect(() => {
    // Canvas receives editor commands.
// Right sidebar also receives selection/settings commands from the mobile app.
if (!isInvoiceCanvasEmbed && !isInvoiceRightEmbed) return;

    const handleInvoiceMobileMessage = (event: any) => {
  try {
    const raw = event?.data;

    
    
    const data =
      typeof raw === 'string'
        ? JSON.parse(raw)
        : raw;

    if (!data || typeof data.action !== 'string') {
      return;
    }

    // Visible diagnostic on the actual canvas.
    if (data.action === 'apply-theme-color') {
      document.body.setAttribute(
        'data-mobile-theme-test',
        data.color || '',
      );

      
    }

        if (!data || typeof data.action !== 'string') {
  return;
}



switch (data.action) {
          case 'add-element': {
            if (data.elementType) {
              handleAddElement(
                data.elementType as ElementType,
                data.props || {},
              );
            }
            break;
          }

          case 'apply-theme-color': {
  if (typeof data.color === 'string') {
    setCorporateColor(data.color);
    applyThemeColor(data.color);
  }
  break;
}

case 'set-page-size': {
  const size = data.size as PageSizePreset;

  if (size && PAGE_PRESETS[size]) {
    const oldConfig = PAGE_PRESETS[pageSize];
    const newConfig = PAGE_PRESETS[size];

    const scaleX = newConfig.width / oldConfig.width;
    const scaleY = newConfig.height / oldConfig.height;
    const scale = Math.min(scaleX, scaleY);

    const rescaled = elements.map(el => ({
      ...el,
      x: Math.round(el.x * scaleX),
      y: Math.round(el.y * scaleY),
      width: Math.round(el.width * scale),
      height: Math.round(el.height * scale),
      fontSize: el.fontSize
        ? Math.max(6, Math.round(el.fontSize * scale))
        : el.fontSize,
    }));

    setElements(rescaled);
    setPageSize(size);
    saveToHistory(rescaled, size);
  }

  break;
}

case 'set-page-behavior': {
  if (
    data.behavior === 'auto_expand' ||
    data.behavior === 'multi_page' ||
    data.behavior === 'fixed'
  ) {
    setPageBehavior(data.behavior);
  }

  break;
}

case 'clear-canvas': {
  setElements([]);
  setSelectedElementId(null);
  setInlineEditingId(null);
  saveToHistory([], pageSize);
  break;
}
case 'set-currency': {
  if (typeof data.currency === 'string') {
    handleCurrencyChange(data.currency);
  }
  break;
}

case 'set-currency-display-mode': {
  if (
    data.mode === 'symbol' ||
    data.mode === 'code'
  ) {
    handleCurrencyDisplayModeChange(data.mode);
  }
  break;
}

case 'set-drawing-mode': {
  if (typeof data.enabled === 'boolean') {
    setIsDrawingMode(data.enabled);

    if (data.enabled) {
      setSelectedElementId(null);
      setInlineEditingId(null);
    }
  }

  break;
}
case 'set-pen-color': {
  if (typeof data.color === 'string') {
    setCurrentPenColor(data.color);
  }

  break;
}
case 'set-pen-width': {
  const width = Number(data.width);

  if (Number.isFinite(width) && width > 0 && width <= 50) {
    setCurrentPenWidth(width);
  }

  break;
}

case 'mobile-auth': {
  if (typeof data.token === 'string' && data.token) {
   

    localStorage.setItem(
      'cloudairambo_jwt',
      data.token,
    );

    fetch(`${BACKEND}/auth/me`, {
      headers: {
        Authorization: `Bearer ${data.token}`,
      },
    })
      .then(async res => {
        if (!res.ok) {
          throw new Error(
            `Mobile auth failed: ${res.status}`,
          );
        }

        return res.json();
      })
      .then(user => {

        

  setAuthUser(user);
  setAuthChecked(true);
  setShowLanding(false);

  if ((window as any).ReactNativeWebView?.postMessage) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        action: 'mobile-auth-success',
        userId: user?.id,
        role: user?.role,
      }),
    );
  }
})
      .catch(error => {
  console.error(
    '[Invoice Mobile Auth] failed:',
    error,
  );

  if ((window as any).ReactNativeWebView?.postMessage) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        action: 'mobile-auth-failed',
        error: String(error),
      }),
    );
  }
});
  }

  break;
}

case 'update-element-props': {
  if (
    typeof data.elementId === 'string' &&
    data.props &&
    typeof data.props === 'object'
  ) {
    updateElementProps(
      data.elementId,
      data.props as Partial<CanvasElement>,
    );
  }

  break;
}

case 'show-element-settings': {
  if (
    data.element &&
    typeof data.elementId === 'string'
  ) {
    setElements(current => {
      const exists = current.some(
        item => item.id === data.elementId
      );

      if (exists) {
        return current.map(item =>
          item.id === data.elementId
            ? { ...item, ...data.element }
            : item
        );
      }

      return [...current, data.element];
    });

    setSelectedElementId(data.elementId);
    setRightSidebarTab('settings');

    
    
  }

  break;
}

case 'delete-element': {
  if (typeof data.elementId === 'string') {
    handleDeleteElement(data.elementId);
  }

  break;
}

case 'duplicate-element': {
  if (typeof data.elementId === 'string') {
    handleDuplicateElement(data.elementId);
  }

  break;
}


case 'z-index': {
  if (
    typeof data.elementId === 'string' &&
    (
      data.zAction === 'front' ||
      data.zAction === 'back' ||
      data.zAction === 'up' ||
      data.zAction === 'down'
    )
  ) {
    handleZIndex(
      data.elementId,
      data.zAction as 'front' | 'back' | 'up' | 'down',
    );
  }

  break;
}

case 'run-header-action': {
  

  switch (data.headerAction) {
    case 'set-grid-snapping': {
      if (typeof data.enabled === 'boolean') {
        setGridSnapping(data.enabled);
      }
      break;
    }

    case 'set-show-grid-lines': {
      if (typeof data.enabled === 'boolean') {
        setShowGridLines(data.enabled);
      }
      break;
    }

    case 'undo': {
      handleUndo();
      break;
    }

    case 'redo': {
      handleRedo();
      break;
    }

    case 'auto-align': {
      if (
        data.mode === 'horizontal-distribution' ||
        data.mode === 'vertical-distribution' ||
        data.mode === 'center-horizontal' ||
        data.mode === 'center-vertical'
      ) {
        handleAutoAlign(data.mode);
      }
      break;
    }

    case 'set-translate-language': {
      if (typeof data.language === 'string') {
        setTranslateTargetLang(data.language);
      }
      break;
    }

    case 'translate': {
      if (typeof data.language === 'string') {
        setTranslateTargetLang(data.language);
      }

      setTimeout(() => {
        handleTranslateToArabic();
      }, 0);

      break;
    }

    case 'set-bilingual-mode': {
      if (typeof data.enabled === 'boolean') {
        setBilingualMode(data.enabled);
      }
      break;
    }

    case 'set-bilingual-direction': {
      if (
        data.direction === 'en-ar' ||
        data.direction === 'ar-en'
      ) {
        setBilingualDirection(data.direction);
      }
      break;
    }

    case 'restack-bilingual': {
      const sorted = [...elements].sort(
        (a, b) => a.y - b.y,
      );

      const restacked = [...sorted];

      for (let i = 1; i < restacked.length; i++) {
        const prev = restacked[i - 1];
        const curr = restacked[i];

        if (
          curr.y <
          prev.y + prev.height + 10
        ) {
          restacked[i] = {
            ...curr,
            y: prev.y + prev.height + 10,
          };
        }
      }

      const final = elements.map(
        el =>
          restacked.find(r => r.id === el.id) ||
          el,
      );

      setElements(final);
      saveToHistory(final, pageSize);

      break;
    }

    case 'share': {
      handleGenerateShareLink();
      break;
    }

    case 'open-scanner': {
      setShowScannerModal(true);
      break;
    }

    case 'save-template': {
  handleSaveAsTemplate(data.templateName);
  break;
}

    case 'save-invoice': {
      handleSaveActiveWorkspaceAsBill();
      break;
    }

    case 'reset-canvas': {
      isResettingRef.current = true;

      localStorage.removeItem(
        'cloudairambo_canvas_draft',
      );

      const defaultSize =
        (localStorage.getItem(
          'cloudairambo_default_page_size',
        ) as PageSizePreset) || 'A4';

      const fresh = JSON.parse(
        JSON.stringify(
          getFreshPresets()[0].elements,
        ),
      );

      setElements(fresh);
      setPageSize(defaultSize);
      setSelectedElementId(null);
      setInlineEditingId(null);

      setBilingualMode(false);
      setBilingualDirection('en-ar');
      setTranslateTargetLang('ar');

      setGridSnapping(false);
      setShowGridLines(false);

      const serialized = JSON.stringify({
        elements: fresh,
        pageSize: defaultSize,
      });

      setHistory([serialized]);
      setHistoryPointer(0);

      setTimeout(() => {
        isResettingRef.current = false;

        localStorage.setItem(
          'cloudairambo_canvas_draft',
          JSON.stringify({
            elements: fresh,
            pageSize: defaultSize,
          }),
        );
      }, 100);

      break;
    }

    case 'export-pdf': {
      handleExportPDF();
      break;
    }

    case 'split-layout': {
      console.warn(
        '[Invoice Canvas Bridge] split-layout received; handler wiring pending.',
      );
      break;
    }

    default: {
      console.warn(
        '[Invoice Canvas Bridge] unknown header action:',
        data.headerAction,
      );
      break;
    }
  }

  break;
}

          case 'load-template': {
            if (data.template) {
              handleLoadTemplateObj(data.template);

              if (data.industryGroup) {
                setActiveIndustryGroup(
                  data.industryGroup,
                );
              }

              if (data.industryName) {
                setActiveIndustryName(
                  data.industryName,
                );
              }
            } else if (
              typeof data.templateIndex === 'number'
            ) {
              loadTemplate(data.templateIndex);
            }

            break;
          }

          case 'apply-marketplace-branding': {
            if (data.profile && data.profileData) {
              applyMarketplaceBranding(
                data.profile,
                data.profileData,
              );
            } else if (data.profile) {
              applyMarketplaceBranding(data.profile);
            } else {
              applyMarketplaceBranding();
            }

            break;
          }

          default:
            
        }
      } catch (error) {
        console.error(
          '[Invoice Mobile Bridge] Invalid message:',
          error,
        );
      }
    };

    window.addEventListener(
      'message',
      handleInvoiceMobileMessage,
    );

    document.addEventListener(
      'message',
      handleInvoiceMobileMessage as any,
    );

    return () => {
      window.removeEventListener(
        'message',
        handleInvoiceMobileMessage,
      );

      document.removeEventListener(
        'message',
        handleInvoiceMobileMessage as any,
      );
    };
  }, [
    isInvoiceCanvasEmbed,
    elements,
    pageSize,
    activeMarketplaceProfile,
    marketplaceProfiles,
    logs,
  ]);

  // Logo / Photo Uploader to draggable image block
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result as string;
        handleAddElement('image', {
          textContent: base64, // we store base64 inside textContent
          width: 160,
          height: 120,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // --- Inline Focus editor trigger ---
  const handleInlineEditStart = (el: CanvasElement) => {
    if (el.type !== 'text' && el.type !== 'stamp') return;
    setInlineEditingId(el.id);
    setInlineEditText(el.textContent || el.stampText || '');
  };

  const handleElementDoubleClick = (el: CanvasElement) => {
  // Keep normal website text/stamp inline editing.
  if (!isInvoiceCanvasEmbed) {
    handleInlineEditStart(el);
    return;
  }

  // Mobile Invoice Creator:
  // double-click/tap opens the native Properties sidebar.
  if ((window as any).ReactNativeWebView?.postMessage) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        action: 'open-element-settings',
        elementId: el.id,
        elementType: el.type,
        element: el,
      }),
    );
  }
};

  const handleInlineEditSave = () => {
    if (!inlineEditingId) return;
    const item = elements.find(el => el.id === inlineEditingId);
    if (item) {
      const keyProp = item.type === 'stamp' ? 'stampText' : 'textContent';
      updateElementProps(inlineEditingId, { [keyProp]: inlineEditText });
      
      // Save
      const updated = elements.map(el => el.id === inlineEditingId ? { ...el, [keyProp]: inlineEditText } : el);
      saveToHistory(updated, pageSize);
    }
    setInlineEditingId(null);
  };

  // Increment Serial State Helper
  const triggerIncrementSerial = (el: CanvasElement) => {
    if (el.type !== 'serial' || !el.serialSettings) return;
    const settings = el.serialSettings;
    const nextVal = settings.current + settings.step;
    const newSettings = { ...settings, current: nextVal };
    updateElementProps(el.id, { serialSettings: newSettings });
    
    // Save state instantly
    const updated = elements.map(item => item.id === el.id ? { ...item, serialSettings: newSettings } : item);
    saveToHistory(updated, pageSize);
  };

  // --- Dynamic Mouse Coordinate Event Handlers ---
  const handleMouseDown = (
    e: React.MouseEvent,
    el: CanvasElement,
    actionType: 'drag' | 'resize',
    direction?: 'nw' | 'ne' | 'sw' | 'se'
  ) => {
    e.stopPropagation();
    if (inlineEditingId) {
      handleInlineEditSave();
    }
    
    setSelectedElementId(el.id);
setRightSidebarTab('settings');

// Mobile app: tell the React Native shell that an element was selected.
// Normal website is unaffected because ReactNativeWebView only exists in the app WebView.
if (
  isInvoiceCanvasEmbed &&
  (window as any).ReactNativeWebView?.postMessage
) {
  (window as any).ReactNativeWebView.postMessage(
  JSON.stringify({
    action: 'element-selected',
    elementId: el.id,
    elementType: el.type,
    element: el,
  }),
);
}

// Initial click coordinates (accounting for layout zoom factor)
    const startX = e.clientX;
    const startY = e.clientY;

    const defaultFs = el.type === 'signature' ? 24 : el.type === 'stamp' ? 18 : el.type === 'serial' ? 12 : 14;

    setDragState({
      id: el.id,
      type: actionType,
      resizeDirection: direction,
      startX,
      startY,
      initialX: el.x,
      initialY: el.y,
      initialW: el.width,
      initialH: el.height,
      initialFontSize: el.fontSize || defaultFs
    });
  };
const liveDragRef = useRef<{ x: number; y: number; width: number; height: number; fontSize?: number } | null>(null);
const isResettingRef = useRef(false);
  // Unified Move listener
 // Unified Move listener — DOM-direct during move, commits to state on mouseup only
useEffect(() => {
  if (!dragState) return;

  const handleMouseMove = (e: MouseEvent | TouchEvent) => {
  const el = elements.find(item => item.id === dragState.id);
  if (!el) return;

  // Works for both mouse drags and finger drags
  const point = 'touches' in e ? e.touches[0] : e;
  if (!point) return;
  if ('touches' in e) e.preventDefault(); // stop page scroll while dragging with finger

  const deltaX = (point.clientX - dragState.startX) / zoom;
  const deltaY = (point.clientY - dragState.startY) / zoom;

    if (dragState.type === 'drag') {
      let proposedX = dragState.initialX + deltaX;
      let proposedY = dragState.initialY + deltaY;

      if (gridSnapping) {
        proposedX = Math.round(proposedX / 5) * 5;
        proposedY = Math.round(proposedY / 5) * 5;
      }

      proposedX = Math.max(-100, Math.min(pageConfig.width + 100, proposedX));
proposedY = Math.max(-100, Math.min(computedHeight + 100, proposedY));

      // Direct DOM move — zero React re-renders during drag
      const node = canvasRef.current?.querySelector(`[data-elid="${el.id}"]`) as HTMLElement | null;
      if (node) {
        node.style.left = proposedX + 'px';
        node.style.top = proposedY + 'px';
      }
      liveDragRef.current = { x: proposedX, y: proposedY, width: el.width, height: el.height };

    } else if (dragState.type === 'resize' && dragState.resizeDirection) {
      let newX = dragState.initialX;
      let newY = dragState.initialY;
      let newW = dragState.initialW;
      let newH = dragState.initialH;

      const dir = dragState.resizeDirection;

      if (dir.includes('e')) newW = dragState.initialW + deltaX;
      else if (dir.includes('w')) { newW = dragState.initialW - deltaX; newX = dragState.initialX + deltaX; }

      if (dir.includes('s')) newH = dragState.initialH + deltaY;
      else if (dir.includes('n')) { newH = dragState.initialH - deltaY; newY = dragState.initialY + deltaY; }

      if (gridSnapping) {
        newW = Math.round(newW / 5) * 5;
        newH = Math.round(newH / 5) * 5;
        newX = Math.round(newX / 5) * 5;
        newY = Math.round(newY / 5) * 5;
      }

      const minVal = 15;
      if (newW < minVal) { if (dir.includes('w')) newX = dragState.initialX + dragState.initialW - minVal; newW = minVal; }
      if (newH < minVal) { if (dir.includes('n')) newY = dragState.initialY + dragState.initialH - minVal; newH = minVal; }
// Aspect ratio lock for shapes
      const draggedEl = elements.find(e => e.id === dragState.id);
      if (draggedEl?.lockAspectRatio && dragState.initialW > 0 && dragState.initialH > 0) {
        const ratio = dragState.initialW / dragState.initialH;
        if (dir.includes('e') || dir.includes('w')) {
          newH = newW / ratio;
        } else {
          newW = newH * ratio;
        }
      }
      let updatedFontSize = el.fontSize;
      if (['text', 'stamp', 'signature', 'serial'].includes(el.type)) {
        const initW = dragState.initialW;
        const initH = dragState.initialH;
        if (initW > 0 && initH > 0) {
          // Scale font primarily by width (like Canva) — text fills box horizontally
          const scaleRatio = Math.min(newW / initW, newH / initH);
          const startFs = dragState.initialFontSize || (el.type === 'signature' ? 24 : el.type === 'stamp' ? 18 : el.type === 'serial' ? 12 : 14);
          updatedFontSize = Math.max(6, Math.round(startFs * scaleRatio));
        }
      }

      // Direct DOM resize — zero React re-renders during resize
      const node = canvasRef.current?.querySelector(`[data-elid="${el.id}"]`) as HTMLElement | null;
      if (node) {
        node.style.left = newX + 'px';
        node.style.top = newY + 'px';
        node.style.width = newW + 'px';
        node.style.height = newH + 'px';
      }
      liveDragRef.current = { x: newX, y: newY, width: newW, height: newH, fontSize: updatedFontSize };
    }
  };

  const handleMouseUp = () => {
    if (liveDragRef.current && dragState) {
      const { x, y, width, height, fontSize } = liveDragRef.current;
      
      const draggedEl = elements.find(e => e.id === dragState.id);
      const oldBottom = draggedEl ? draggedEl.y + draggedEl.height : 0;
      const newBottom = y + height;
      const delta = newBottom - oldBottom;

      const nextElements = elements.map(e => {
        if (e.id === dragState.id) {
          const fs = fontSize || e.fontSize || 14;
const lh = e.lineHeight || 1.3;
const lines = (e.textContent || '').split('\n').length;
const minH = e.type === 'text'
  ? Math.max(height, Math.ceil(lines * fs * lh) + 8)
  : height;
return { ...e, x, y, width, height: minH, ...(fontSize !== undefined ? { fontSize } : {}) };
        }
        // Push elements below down if table was resized taller
        if (dragState.type === 'resize' && delta > 0 && draggedEl?.type === 'table' && e.y >= oldBottom - 10) {
          return { ...e, y: Math.round(e.y + delta) };
        }
        return e;
      });
      setElements(nextElements);
      saveToHistory(nextElements, pageSize);
    }
    liveDragRef.current = null;
    setDragState(null);
  };

  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('touchmove', handleMouseMove, { passive: false });
  window.addEventListener('touchend', handleMouseUp);

  return () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    window.removeEventListener('touchmove', handleMouseMove);
    window.removeEventListener('touchend', handleMouseUp);
  };
}, [dragState, elements, zoom, gridSnapping, computedHeight]);

  // --- Dynamic Serial Formatter ---
  const formatSerialNumber = (settings: any) => {
  if (!settings) return "";
  const numStr = String(settings.current).padStart(settings.digits, '0');
  return `${settings.prefix}${numStr}${settings.suffix}`;
};


// --- Snapshot-Based PDF Generator (Identical to Quick Invoice) ---
  const handleExportPDF = async (record?: InvoiceRecord, returnBlobInsteadOfDownload = false) => {
    try {
      const targetPageSize = record?.pageSize || pageSize;
      const targetPageConfig = PAGE_PRESETS[targetPageSize] || PAGE_PRESETS['A4'];
      const pageCss = await getPageCss();

      let paperHtml = '';
      let totalHeight = targetPageConfig.height;

      // ── MODE A: Live Canvas Snapshot (User is in Invoice Creator) ──
      if (!record && canvasRef.current) {
        // Deselect so editor rings, handles, and delete buttons don't appear in the PDF
        setSelectedElementId(null);
        setInlineEditingId(null);

        const liveCanvas = canvasRef.current;
        const clone = liveCanvas.cloneNode(true) as HTMLElement;

        // Strip editor artifacts from snapshot (handles, buttons, rings, and the 20px grid overlay)
        clone.querySelectorAll('.resize-handle-touch, .rotation-handle-touch, .delete-btn-touch, button').forEach(el => el.remove());
        clone.querySelectorAll('[class*="ring-2"], [class*="ring-1"]').forEach(el => {
          el.className = el.className
            .replace(/ring-[12]/g, '')
            .replace(/ring-\w+-\d+/g, '')
            .replace(/shadow-lg/g, '')
            .replace(/bg-blue-50\/10/g, '');
        });

        // Remove the editor grid pattern & page break warning markers
        clone.querySelectorAll('div').forEach(el => {
          if (el.style.backgroundImage && el.style.backgroundImage.includes('linear-gradient')) {
            el.remove();
          }
        });
        clone.querySelectorAll('[class*="border-rose-"], [class*="border-amber-"]').forEach(el => el.remove());

        // Neutralize scale and force pure clean white paper background
        clone.style.transform = 'none';
        clone.style.border = 'none';
        clone.style.boxShadow = 'none';
        clone.style.backgroundColor = '#ffffff';
        clone.style.position = 'relative';
        clone.style.top = '0';
        clone.style.left = '0';
        clone.style.margin = '0 auto';// Strip editor artifacts from snapshot (handles, buttons, rings, and the 20px grid overlay)
        clone.querySelectorAll('.resize-handle-touch, .rotation-handle-touch, .delete-btn-touch, button').forEach(el => el.remove());
        clone.querySelectorAll('[class*="ring-2"], [class*="ring-1"]').forEach(el => {
          el.className = el.className
            .replace(/ring-[12]/g, '')
            .replace(/ring-\w+-\d+/g, '')
            .replace(/shadow-lg/g, '')
            .replace(/bg-blue-50\/10/g, '');
        });

        // Remove the editor grid pattern & page break warning markers
        clone.querySelectorAll('div').forEach(el => {
          if (el.style.backgroundImage && el.style.backgroundImage.includes('linear-gradient')) {
            el.remove();
          }
        });
        clone.querySelectorAll('[class*="border-rose-"], [class*="border-amber-"]').forEach(el => el.remove());

        // Neutralize scale and force pure clean white paper background
        clone.style.transform = 'none';
        clone.style.border = 'none';
        clone.style.boxShadow = 'none';
        clone.style.backgroundColor = '#ffffff';
        clone.style.position = 'relative';
        clone.style.top = '0';
        clone.style.left = '0';
        clone.style.margin = '0 auto';

        paperHtml = clone.outerHTML;
        totalHeight = Math.max(liveCanvas.scrollHeight, liveCanvas.offsetHeight, targetPageConfig.height);
      } 
      // ── MODE B: Offscreen DOM Render (Exporting a Saved Invoice from Bills) ──
      else {
        const targetElements = record ? record.elements : elements;

        // Real, scannable QR images for every QR on the invoice (invoice QR, Net30, UPI, etc.)
        const modeBQrDataUrls: Record<string, string> = { ...qrDataUrls };
        await Promise.all(
          targetElements
            .filter((el: any) => el.type === 'qr' && el.qrValue)
            .map(async (el: any) => {
              try {
                modeBQrDataUrls[el.id] = await QRCode.toDataURL(el.qrValue, {
                  errorCorrectionLevel: 'M',
                  width: 300,
                  margin: 1,
                });
              } catch { /* keep fallback for this one */ }
            })
        );

        const container = document.createElement('div');
        container.style.cssText = `position:fixed;left:-9999px;top:-9999px;width:${targetPageConfig.width}px;background:#ffffff;`;
        document.body.appendChild(container);

        const { createRoot } = await import('react-dom/client');
        const root = createRoot(container);
        const paperRef = { current: null as HTMLDivElement | null };

        await new Promise<void>(resolve => {
          root.render(
            React.createElement(CanvasWorkspace, {
              pageConfig: { ...targetPageConfig, name: targetPageSize },
              computedHeight: targetPageConfig.height,
              pageCount: 1,
              pageBehavior: 'auto_expand',
              setPageBehavior: () => {},
              zoom: 1,
              setZoom: () => {},
              showGridLines: false,
              elements: targetElements,
              selectedElementId: null,
              inlineEditingId: null,
              inlineEditText: '',
              setInlineEditText: () => {},
              lockedElementIds: [],
              dragState: null,
              qrDataUrls: modeBQrDataUrls,
              isDrawingMode: false,
              isDrawingActive: false,
              currentPathPoints: [],
              currentPenColor: '#000000',
              currentPenWidth: 2,
              bilingualMode: false,
              bilingualDirection: 'en-ar',
              translateTargetLang: 'en',
              activeCurrency: record?.currency || effectiveCurrency,
              currencyDisplayMode,
              computeTableTotals,
              formatSerialNumber,
              canvasRef: paperRef,
              handleMouseDown: () => {},
              handleDeleteElement: () => {},
              handleInlineEditStart: () => {},
              handleInlineEditSave: () => {},
              handleElementDoubleClick: () => {},
              commitCurrentState: () => {},
              updateElementProps: () => {},
              handleCanvasMouseDown: () => {},
              handleCanvasMouseMove: () => {},
              handleCanvasMouseUp: () => {},
              onBackgroundClick: () => {},
              printMode: true,
              readOnlyPreview: true,
            })
          );
          setTimeout(resolve, 600);
        });

        paperHtml = paperRef.current ? paperRef.current.outerHTML : container.innerHTML;
        totalHeight = Math.max(container.scrollHeight, container.offsetHeight, targetPageConfig.height);
        root.unmount();
        if (document.body.contains(container)) document.body.removeChild(container);
      }

      // ── Package the Exact DOM Snapshot with Full Stylesheets ──
      const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=Noto+Sans+Arabic:wght@400;600;700;800&family=Alex+Brush&family=Dancing+Script:wght@400;700&family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet"/>
    <style>
      ${pageCss}
      * { 
        box-sizing: border-box !important; 
        -webkit-print-color-adjust: exact !important; 
        print-color-adjust: exact !important; 
      }
      @page { 
        size: ${targetPageSize === 'LETTER' ? 'letter' : targetPageSize === 'A5' ? 'a5' : 'a4'} portrait; 
        margin: 0 !important; 
      }
      html, body { 
        margin: 0 !important; 
        padding: 0 !important; 
        background: #ffffff !important; 
        width: ${targetPageConfig.width}px !important;
        overflow: visible !important;
      }
    </style>
  </head>
  <body>
    ${paperHtml}
  </body>
</html>`;

      const { pdfApi } = await import('./utils/api');
      const blob = await pdfApi.generate(html, targetPageConfig.width, targetPageConfig.height, totalHeight);

      if (returnBlobInsteadOfDownload) return blob;

      const fileName = record
        ? `${record.invoiceNumber}_${record.clientName.replace(/\s+/g, '_')}.pdf`
        : `${targetPageSize}_Export_${Date.now().toString().slice(-6)}.pdf`;

      // Mobile app WebView handling
      if (isInvoiceCanvasEmbed && (window as any).ReactNativeWebView?.postMessage) {
        const reader = new FileReader();
        reader.onloadend = () => {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ action: 'invoice-pdf-ready', fileName, data: reader.result })
          );
        };
        reader.onerror = () => {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ action: 'invoice-pdf-error', message: 'PDF generated, but could not be downloaded.' })
          );
        };
        reader.readAsDataURL(blob);
        return;
      }

      // Normal browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

const buildQuickInvoicePrintJob = async (invoice: InvoiceRecord) => {
  const els = invoice.elements || [];
  let targetLang =
    (invoice as any).translated_lang ||
    (invoice as any).translatedLang ||
    (invoice as any).translateTargetLang ||
    (invoice as any).detectedLang ||
    els.find((el: any) => el.translatedLang)?.translatedLang;

  // Sanity check: If content is Devanagari (Hindi), do not treat as Arabic!
  if (targetLang === 'ar') {
    const hasDevanagari = els.some((el: any) => /[\u0900-\u097F]/.test(el.arabicContent || '')) ||
      /[\u0900-\u097F]/.test(JSON.stringify((invoice as any).dynamic_values_ar || {}));
    if (hasDevanagari) targetLang = 'hi';
  }

  const isBilingualTarget = !!targetLang;
  const tableEl = els.find((el: any) => el.type === 'table');
  // Resolve fresh at print time — never trust a saved element's
  // arabicContent line-split, which silently comes back undefined
  // whenever that element's translation didn't include the client name
  // in the expected position (e.g. templates whose Bill-To block isn't
  // shaped exactly like "Label\nName\n...").
  const _jwtForClient = getStoredToken();
  const _headersForClient = _jwtForClient ? { Authorization: `Bearer ${_jwtForClient}` } : {};
  const clientLineAr = isBilingualTarget
    ? await resolveClientLineAr(invoice.clientName || '', targetLang, BACKEND, _headersForClient)
    : undefined;

  const items = (tableEl?.rows || []).map((r: any) => ({
    description: r.values.description || '',
    qty: parseFloat(String(r.values.qty)) || 0,
    rate: parseFloat(String(r.values.rate)) || 0,
    arabicName: isBilingualTarget
      ? (r.values.arabicName || r.values.arabicDescription || undefined)
      : undefined,
  }));

  // Custom/extra table columns (Batch No etc.) → one text line per item
  const _stdKeys = new Set(['description', 'qty', 'rate', 'total', 'sno', 'serial', 'index']);
  const _extraCols = ((tableEl as any)?.columns || []).filter(
    (c: any) => !_stdKeys.has(c.key) && !isSerialNoColumn(c)
  );
  const itemExtraLines = (tableEl?.rows || []).map((r: any) =>
    _extraCols
      .map((c: any) => (r.values[c.key] ? `${c.originalHeader || c.header}: ${r.values[c.key]}` : ''))
      .filter(Boolean)
      .join('  ')
  );
  const _buyerTaxId =
    (invoice as any).buyerTaxId || (invoice as any).buyer_tax_id ||
    (invoice as any).clientGSTIN || (tableEl as any)?.buyerTaxId || '';
  const buyerTaxLabel = (invoice as any).sellerCountry === 'IN' ? 'GSTIN' : 'Tax ID';

  // Net base per row (excludes inclusive tax) — needed to work out what
  // fraction of the subtotal the header discount actually removes.
  const netSubtotalForTax = (tableEl?.rows || []).reduce((sum: number, r: any) => {
    const qty = parseFloat(String(r.values.qty)) || 0;
    const rate = parseFloat(String(r.values.rate)) || 0;
    const lineTotal = qty * rate;
    const taxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
    const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
    const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
    return sum + base;
  }, 0);

  const rawTaxLines = ((tableEl as any)?.taxes || []).filter((t: any) => t.rate > 0);
  const rawDiscountLines = ((tableEl as any)?.discounts || []).filter((d: any) => d.rate > 0);
  const shippingCost = (tableEl as any)?.shippingCost || 0;

  // Discount reduces the taxable base before product tax is calculated —
  // same standard GST/VAT math as every other totals block in the app.
  const discTotalForProductTax = rawDiscountLines.reduce((s: number, d: any) => s + netSubtotalForTax * (d.rate / 100), 0);
  const discountRatioForProductTax = netSubtotalForTax > 0 ? Math.min(1, discTotalForProductTax / netSubtotalForTax) : 0;

  // Per-item tax breakdown — mirrors getItemTaxBreakdown in QuickInvoiceView/POSView.
  const productTaxTotal = (tableEl?.rows || []).reduce((sum: number, r: any) => {
    const qty = parseFloat(String(r.values.qty)) || 0;
    const rate = parseFloat(String(r.values.rate)) || 0;
    const lineTotal = qty * rate;
    const taxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
    if (taxRate <= 0) return sum;
    const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
    const base = inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
    return sum + base * (1 - discountRatioForProductTax) * (taxRate / 100);
  }, 0);

  // Named GST/VAT/QST breakdown (CGST/SGST/IGST/QST/etc.) — same approach
  // as renderPOSReceiptToPdf below. Only possible when this invoice was
  // saved with seller/buyer region context; older invoices fall back to
  // buildReceiptBytes' blended "Total Tax" line.
  let taxBreakdown: { name: string; rate: number; amount: number }[] | undefined;
  const sellerCountryForTax = (invoice as any).sellerCountry;
  if (sellerCountryForTax && productTaxTotal > 0) {
    const taxScheme = getDefaultSchemeForCountryAndRegion(sellerCountryForTax, (invoice as any).sellerRegion);
    const buyerRegionForTax = (invoice as any).buyerRegion || '';
    if (taxScheme.kind === 'component') {
      const combined: Record<string, { rate: number; amount: number }> = {};
      (tableEl?.rows || []).forEach((r: any) => {
        const qty = parseFloat(String(r.values.qty)) || 0;
        const rate = parseFloat(String(r.values.rate)) || 0;
        const lineTotal = qty * rate;
        const rowTaxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
        if (rowTaxRate <= 0) return;
        const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
        const base = inclusive ? lineTotal / (1 + rowTaxRate / 100) : lineTotal;
        const discountedBase = base * (1 - discountRatioForProductTax);
        const breakdown = computeTaxBreakdown(
          taxScheme, discountedBase,
          { sellerRegion: (invoice as any).sellerRegion, buyerRegion: buyerRegionForTax }, rowTaxRate
        );
        breakdown.lines.forEach(l => {
          if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
          combined[l.name].amount += l.amount;
        });
      });
      const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
      if (entries.length > 0) taxBreakdown = entries;
    } else {
      // Simple/single-name scheme — same custom-name fallback POSView uses,
      // computed BEFORE the savedBreakdown override below (which still wins
      // when this invoice already has a real saved breakdown from save time).
      const combined: Record<string, { rate: number; amount: number }> = {};
      (tableEl?.rows || []).forEach((r: any) => {
        const qty = parseFloat(String(r.values.qty)) || 0;
        const rate = parseFloat(String(r.values.rate)) || 0;
        const lineTotal = qty * rate;
        const rowTaxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
        if (rowTaxRate <= 0) return;
        const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
        const base = inclusive ? lineTotal / (1 + rowTaxRate / 100) : lineTotal;
        const discountedBase = base * (1 - discountRatioForProductTax);
        const customLabel = r.values.b2bTaxName || r.values.taxName ||
          taxScheme.components?.[0]?.name || taxScheme.label.replace(/\s*\(.*?\)/, '') || 'Tax';
        if (!combined[customLabel]) combined[customLabel] = { rate: rowTaxRate, amount: 0 };
        combined[customLabel].amount += discountedBase * (rowTaxRate / 100);
      });
      const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
      if (entries.length > 0) taxBreakdown = entries;
    }
  }

  // Prefer the tax names saved with the invoice. Only trust them if they
  // still add up to the current tax total (an edited invoice would be stale).
  const savedBreakdown = (tableEl as any)?.taxBreakdown;
  if (Array.isArray(savedBreakdown) && savedBreakdown.length > 0 && productTaxTotal > 0) {
    const savedSum = savedBreakdown.reduce((s: number, t: any) => s + (t.amount || 0), 0);
    // Always keep the user's saved tax names. If the amounts drifted (edited invoice),
    // rescale them so they add up to the real tax total instead of dropping the names.
    const k = savedSum > 0 ? productTaxTotal / savedSum : 1;
    taxBreakdown = savedBreakdown.map((t: any) => ({
      name: t.name,
      rate: t.rate,
      amount: savedSum > 0 ? (t.amount || 0) * k : productTaxTotal / savedBreakdown.length,
      nameAr: t.nameAr || t.arabicName,
    }));
  }

  // Resolve every Arabic label fresh (rather than relying on t.arabicLabel,
  // which is never populated on saved invoice elements) — cached in
  // localStorage per language so repeat prints are instant.
  const _cacheKey = (label: string) => `cloudairambo_label_cache_${targetLang}_${label}`;
  const _jwt = getStoredToken();
  const _headers = _jwt ? { Authorization: `Bearer ${_jwt}` } : {};
  const _getCachedLabelTranslated = async (label: string) => {
    if (!isBilingualTarget || !label) return undefined;
    const cached = localStorage.getItem(_cacheKey(label));
    if (cached) return cached;
    try {
      const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(label)}&tl=${targetLang}`, { headers: _headers });
      const data = await res.json();
      const translated = data.translated || label;
      localStorage.setItem(_cacheKey(label), translated);
      return translated;
    } catch { return undefined; }
  };

  // Resolve nameAr for each named tax-breakdown component using the same
  // cached-label helper as every other label on this receipt — without
  // this, a bilingual thermal reprint would show CGST/SGST/IGST in
  // English only, even though the rest of the receipt is bilingual.
  if (taxBreakdown && isBilingualTarget) {
    taxBreakdown = await Promise.all(
      taxBreakdown.map(async (t: any) => ({ ...t, nameAr: t.nameAr || (await _getCachedLabelTranslated(t.name)) }))
    );
  }

  // Editing an invoice can drop the saved Arabic item names — translate any missing ones now.
  const itemsWithAr = await Promise.all(
    items.map(async (it: any) => ({
      ...it,
      arabicName: it.arabicName || (isBilingualTarget ? await _getCachedLabelTranslated(it.description) : undefined),
    }))
  );

  const taxLines = await Promise.all(
    rawTaxLines.map(async (t: any) => ({ ...t, labelAr: t.arabicLabel || (await _getCachedLabelTranslated(t.label)) }))
  );
  const discountLines = await Promise.all(
    rawDiscountLines.map(async (d: any) => ({ ...d, labelAr: d.arabicLabel || (await _getCachedLabelTranslated(d.label)) }))
  );

  const [_subtotalAr, _totalAr, _shippingAr, _itemAr, _qtyAr, _amtAr] = await Promise.all([
    _getCachedLabelTranslated('Subtotal'),
    _getCachedLabelTranslated('Total'),
    _getCachedLabelTranslated('Shipping'),
    _getCachedLabelTranslated('Item'),
    _getCachedLabelTranslated('Qty'),
    _getCachedLabelTranslated('Amt'),
  ]);
  const rowLabelsAr = isBilingualTarget
    ? { subtotal: _subtotalAr, total: _totalAr, shipping: _shippingAr, item: _itemAr, qty: _qtyAr, amt: _amtAr }
    : undefined;

  // Amount in Words — this print path builds from the SAVED invoice
  // record, not live form state, so it needs the persisted
  // showAmountInWords flag rather than reading a component state variable
  // that doesn't exist here.
  const amountInWords = (invoice as any).showAmountInWords
    ? amountToWords(invoice.amount || 0, invoice.currency || activeCurrency)
    : undefined;
  const amountInWordsAr = amountInWords && isBilingualTarget
    ? await _getCachedLabelTranslated(amountInWords)
    : undefined;

  const sym = getCurrencyDisplay(
  invoice.currency || activeCurrency,
  currencyDisplayMode
);

const invoiceQR =
  (invoice as any).publicToken
    ? `${APP_URL}/invoice/view/${(invoice as any).publicToken}`
    : els.find((el: any) => el.type === 'qr' && el.qrValue)?.qrValue
      || undefined;

  const textLines = (beforeTable: boolean) => {
    const tableY = tableEl?.y ?? Infinity;

    return els
      .filter((el: any) =>
        el.type === 'text' &&
        (beforeTable
          ? (el.y || 0) < tableY
          : (el.y || 0) >= tableY)
      )
      .sort((a: any, b: any) => (a.y || 0) - (b.y || 0))
      .filter((el: any) => {
        // The QR caption ("Scan to view invoice"/"Scan to track your
        // order") is its own text element on the canvas, but
        // buildReceiptBytes already prints its own caption directly
        // above the QR image when invoiceQR/trackingQR is set. Leaving
        // it in footerLines prints the caption twice on the receipt.
        const t = (el.textContent || '').trim();
        return t !== 'Scan to view invoice' && t !== 'Scan to track your order';
      })
      .flatMap((el: any) => {
        const en = (el.textContent || '').split('\n');
const ar = el.arabicContent
  ? el.arabicContent.split('\n')
  : [];

const lines: string[] = [];

const toArabicNumerals = (value: string) =>
  value.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);

// Reverse of toArabicNumerals — used only to detect "fake" translations
// where Google Translate had nothing real to translate (phone numbers,
// postal codes) and just returned the same text with digits localized
// to Arabic-Indic script. That's not a translation, it's the same line
// wearing different digits, so it must not be treated as distinct content.
const normalizeToAsciiDigits = (value: string) =>
  value.replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

en.forEach((line: string, i: number) => {
  const enLine = line.trim();
  if (enLine) lines.push(enLine);

  const arLine = ar[i]?.trim();
  const isRealTranslation =
    arLine && normalizeToAsciiDigits(arLine) !== enLine;

  if (isRealTranslation) {
            lines.push(targetLang === 'ar' ? toArabicNumerals(arLine) : arLine);
          }
});

        return lines;
      })
      .filter(Boolean);
  };

  // Bilingual captions for the QR scan-prompt lines. Only Arabic is
  // wired up right now (matches the rest of the print pipeline), but
  // this is easy to extend with more languages the same way pdfLabels
  // is extended elsewhere in this file.
  const invoiceQRLabel = isBilingualTarget
    ? await _getCachedLabelTranslated('Scan to view invoice')
    : undefined;


  // ---- Item table columns for the thermal receipt ----
  const MAX_TABLE_COLS = 5; // Item, Qty, Rate, Tax, Amt fits on 58mm. Extra custom columns overflow below the item.
  const _baseCols = ((tableEl as any)?.columns || []).filter(
    (c: any) => !isSerialNoColumn(c)
  );
  const _rowTaxPct = (r: any) =>
    parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
  const _hasTaxCol = _baseCols.some((c: any) =>
    c.key === 'tax' || c.key === 'taxRate' ||
    /^(tax|vat|gst)/i.test(String(c.originalHeader || c.header || '').trim())
  );
  const _addTaxCol = !_hasTaxCol && (tableEl?.rows || []).some((r: any) => _rowTaxPct(r) > 0);
  const _taxCol = { key: '__tax', header: 'Tax', originalHeader: 'Tax', width: 12, align: 'right' };
  const _totalIdx = _baseCols.findIndex((c: any) => c.key === 'total');
  const _allCols = _addTaxCol
    ? (_totalIdx >= 0
        ? [..._baseCols.slice(0, _totalIdx), _taxCol, ..._baseCols.slice(_totalIdx)]
        : [..._baseCols, _taxCol])
    : _baseCols;
  const _mustKeep = (c: any) => c.key === 'description' || c.key === 'total' || c.key === '__tax';
  let _slots = Math.max(0, MAX_TABLE_COLS - _allCols.filter(_mustKeep).length);
  const _printCols = _allCols.filter((c: any) => {
    if (_mustKeep(c)) return true;
    if (_slots > 0) { _slots--; return true; }
    return false;
  });
  const _overflowCols = _allCols.filter((c: any) => !_printCols.includes(c));

  const _cellText = (c: any, r: any): string => {
    const qty = parseFloat(String(r.values.qty)) || 0;
    const rate = parseFloat(String(r.values.rate)) || 0;
    if (c.key === '__tax') { const p = _rowTaxPct(r); return p > 0 ? `${p}%` : ''; }
    if (c.key === 'description') return String(r.values.description || '');
    if (c.key === 'qty') return String(qty);
    if (c.key === 'rate') return `${sym}${rate.toFixed(2)}`;
    if (c.key === 'total') return `${sym}${(qty * rate).toFixed(2)}`;
    const v = r.values[c.key];
    return v === undefined || v === null ? '' : String(v);
  };

  const tableColumns = await Promise.all(_printCols.map(async (c: any) => {
    const en = c.originalHeader || c.header;
    return {
      header: en,
      // Saved Arabic header first, otherwise translate it now (cached per language).
      // Standard names (Item/Qty/Rate/Tax/Amt) are overridden by the printer's dictionary.
      headerAr: isBilingualTarget
        ? (c.arabicHeader || (await _getCachedLabelTranslated(en)) || undefined)
        : undefined,
      align: (c.key === 'description'
        ? 'left'
        : (c.align || (c.key === 'qty' ? 'center' : 'right'))) as 'left' | 'center' | 'right',
      width: c.width,
      primary: c.key === 'description',
    };
  }));

  const itemCells = (tableEl?.rows || []).map((r: any) =>
    _printCols.map((c: any) => _cellText(c, r))
  );

  const _finalExtraLines = (tableEl?.rows || []).map((r: any) =>
    _overflowCols
      .map((c: any) => {
        const t = _cellText(c, r);
        return t ? `${c.originalHeader || c.header}: ${t}` : '';
      })
      .filter(Boolean)
      .join('  ')
  );

  // Arabic overflow lines — mirrors the live-print logic in
  // QuickInvoiceView's handlePrintPreview (_qiExtraLinesAr), but this
  // path reprints an ALREADY-SAVED invoice from Bills. Custom-column
  // cell VALUES (e.g. Tax Name "rihan") are never saved with an Arabic
  // counterpart on the row — only the item description gets
  // arabicDescription. Without resolving these fresh here, itemExtraLinesAr
  // was never even built, so a Bills reprint silently dropped the whole
  // Arabic overflow line for every custom column, every time.
  const _valueCacheKey = (value: string) => `cloudairambo_value_cache_${targetLang}_${value}`;
  const _getCachedValueTranslated = async (value: string): Promise<string | undefined> => {
    if (!isBilingualTarget || !value?.trim()) return undefined;
    const clean = value.trim();
    if (/^[\d.,%\s]+$/.test(clean)) return undefined; // numeric — nothing to translate
    const cached = localStorage.getItem(_valueCacheKey(clean));
    if (cached) return cached;
    try {
      const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(clean)}&tl=${targetLang}`, { headers: _headers });
      const data = await res.json();
      let translated = data.translated || clean;
      // Same fallback as live translate: if translation just echoed the
      // text back (proper nouns like names), transliterate it instead.
      if (translated.toLowerCase() === clean.toLowerCase() && /[a-zA-Z]/.test(clean)) {
        const tRes = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(clean)}&tl=${targetLang}`, { headers: _headers });
        const tData = await tRes.json();
        translated = tData.transliterated || translated;
      }
      localStorage.setItem(_valueCacheKey(clean), translated);
      return translated;
    } catch { return undefined; }
  };

  const _overflowHeaderAr: Record<string, string> = {};
  if (isBilingualTarget) {
    await Promise.all(_overflowCols.map(async (c: any) => {
      _overflowHeaderAr[c.key] = c.arabicHeader || (await _getCachedLabelTranslated(c.originalHeader || c.header)) || '';
    }));
  }

  const _finalExtraLinesAr = isBilingualTarget
    ? await Promise.all((tableEl?.rows || []).map(async (r: any) => {
        const parts = await Promise.all(_overflowCols.map(async (c: any) => {
          const raw = _cellText(c, r);
          if (!raw) return '';
          const headerAr = _overflowHeaderAr[c.key];
          const valueAr = await _getCachedValueTranslated(raw);
          return headerAr && valueAr ? `${headerAr}: ${valueAr}` : '';
        }));
        return parts.filter(Boolean).join('  ');
      }))
    : undefined;

  return {
    shopName: loadCompanyProfile()?.businessName || 'RECEIPT',
    invoiceNumber: invoice.invoiceNumber || '',
    date: invoice.createdAt
      ? new Date(invoice.createdAt).toLocaleString()
      : '',
    clientName: invoice.clientName || 'Walk-in Customer',

    items: itemsWithAr,

    taxLines,
    discountLines,
    shippingCost,
    sym,

    notes: '',

    headerLines: textLines(true),
    footerLines: textLines(false),

    arabicNumerals: targetLang === 'ar',

    invoiceQR,
    invoiceQRLabel,
    clientLineAr,
    rowLabelsAr,
    amountInWords,
    amountInWordsAr,
    trackingQR: undefined,
    productTaxTotal,
    netSubtotal: netSubtotalForTax,
    clientPhone: (invoice as any).clientPhone || '',
    clientEmail: invoice.clientEmail || '',
    buyerTaxId: _buyerTaxId,
    buyerTaxLabel,
    itemExtraLines: _finalExtraLines,
    itemExtraLinesAr: _finalExtraLinesAr,
    taxBreakdown,
    tableColumns,
    itemCells,
  };
};
// Session-scoped cache: once a PDF is built for a given invoice id,
// every subsequent caller (auto-download, WhatsApp send, Bills-tab
// download/print) gets the EXACT SAME Blob object instead of a fresh,
// independently re-rendered one. Blobs are immutable, so reusing one
// across URL.createObjectURL and FileReader.readAsDataURL is safe.
// This is what guarantees auto-download and WhatsApp can never
// disagree again: they're not just running the same renderer anymore
// (that was Fix B, and it still wasn't enough) — they're now
// literally sharing one set of rendered bytes. There is no formula
// left to keep in sync because there's no second render happening.
const invoicePdfCacheRef = useRef<Map<string, Blob>>(new Map());

const getInvoicePdfBlob = async (invoice: InvoiceRecord): Promise<Blob | null> => {
  invoicePdfCacheRef.current.clear();
  const cacheKey = invoice.id;

  const isThermal = (invoice.pageSize || '').startsWith('THERMAL');
  const isBilingualInvoice = (invoice as any).isBilingual === true || (invoice as any).is_bilingual === true;

  // 1. Thermal -> POSReceiptRenderer
  // 2. Bilingual A4/Letter -> renderBilingualInvoiceToPdf
  // 3. Non-Bilingual Standard A4/Letter -> handleExportPDF (Clean 1-page template layout)
  const blob = isThermal
    ? await renderPOSReceiptToPdf(invoice)
    : isBilingualInvoice
      ? await renderBilingualInvoiceToPdf(invoice)
      : await handleExportPDF(invoice, true);

  if (blob && cacheKey) {
    invoicePdfCacheRef.current.set(cacheKey, blob);
  }
  return blob;
};

// Must be invalidated any time an invoice's underlying elements/amount
// change — otherwise a later Bills-tab Download/Print after an edit
// would silently return the stale pre-edit PDF from cache instead of
// re-rendering. Called from handleUpdateInvoice below.
const invalidateInvoicePdfCache = (invoiceId: string) => {
  invoicePdfCacheRef.current.delete(invoiceId);
};

// Used by QuickInvoiceView's onExportPDF (auto-download on save) so it
// produces the exact same PDF as WhatsApp/Print instead of the old
// wide-table handleExportPDF renderer. Same signature as handleExportPDF
// so it's a drop-in swap — QuickInvoiceView doesn't need any changes.
const handleExportPDFViaSharedRenderer = async (
  record?: InvoiceRecord,
  returnBlobInsteadOfDownload = false
) => {
  if (!record) {
    return handleExportPDF(record, returnBlobInsteadOfDownload);
  }

  const blob = await getInvoicePdfBlob(record);
  if (!blob) return null;
  if (returnBlobInsteadOfDownload) return blob;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${record.invoiceNumber}_${record.clientName.replace(/\s+/g, '_')}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};


const renderBilingualInvoiceToPdf = async (invoice: InvoiceRecord): Promise<Blob | null> => {
  try {
    const els = invoice.elements || [];
    const tableEl = els.find((el: any) => el.type === 'table');
    const isThermal = (invoice.pageSize || '').startsWith('THERMAL');
    const receiptWidth = PAGE_PRESETS[invoice.pageSize as PageSizePreset]?.width || (isThermal ? 302 : 794);
    const invoiceNum = invoice.invoiceNumber || '';

    // QuickInvoiceView bakes a standalone 'pdf-amount-in-words' text
    // element into invoice.elements at save time, positioned by an
    // ESTIMATED y (calcTableHeight()'s formula, not a real DOM
    // measurement). BilingualInvoiceRenderer has its own dedicated,
    // properly-boxed Amount-in-Words slot right under TOTAL — driven by
    // the showAmountInWords/amountInWordsTranslated props below — but if
    // this stray element is left in the element list it also gets
    // rendered a second time as an ordinary footer text block at that
    // drifted estimated position, which is why it was showing up after
    // Notes/QR instead of under the total. Pull its content out, then
    // exclude it from what gets rendered as a positional element.
    const amountWordsEl = els.find((el: any) => el.id === 'pdf-amount-in-words');
    // Prefer the value QuickInvoiceView resolved live at save time
    // (amountInWordsAr) — edit-mode never injects a 'pdf-amount-in-words'
    // element the way create-mode does, so scanning els for it silently
    // came up empty on every "convert to bilingual then save" edit,
    // dropping the Arabic line from the auto-downloaded PDF even though
    // the live preview showed it correctly.
    const amountInWordsTranslatedFromEls = (invoice as any).amountInWordsAr || amountWordsEl?.arabicContent || '';

    const patchedEls = els
      .filter((el: any) => el.id !== 'pdf-amount-in-words')
      .map((el: any) => {
        if (el.type === 'text' && el.arabicContent?.includes('{invoice_number}')) {
          return { ...el, arabicContent: el.arabicContent.replace(/\{invoice_number\}/g, invoiceNum) };
        }
        return el;
      });

    // Rebuild lineItems from the saved table rows
    const rawSavedBuyerTaxId =
      (invoice as any).buyerTaxId ||
      (invoice as any).buyer_tax_id ||
      (invoice as any).clientGSTIN ||
      (tableEl as any)?.buyerTaxId ||
      (tableEl as any)?.buyer_tax_id ||
      '';
    const isB2B = Boolean(rawSavedBuyerTaxId && rawSavedBuyerTaxId.trim().length > 0);

    const lineItems = (tableEl?.rows || []).map((r: any) => ({
      // every saved value on the row, so any custom column the user added comes through
      ...r.values,
      id: r.id,
      description: r.values.description || '',
      qty: parseFloat(String(r.values.qty)) || 0,
      rate: parseFloat(String(r.values.rate)) || 0,
      arabicName: r.values.arabicDescription || undefined,
      taxRate: parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0,
      taxName: r.values.taxName || undefined,
      b2bTaxRate: isB2B ? parseFloat(String(r.values.b2bTaxRate ?? r.values.b2b_tax_rate ?? r.values.taxRate ?? 0)) : undefined,
      b2bTaxName: r.values.b2bTaxName || undefined,
      taxInclusive: !!(r.values.taxInclusive ?? r.values.tax_inclusive),
      // translated custom-column values saved as arabic_<key> / arabicContent_<key>
      __customAr: Object.fromEntries(
        Object.entries(r.values)
          .filter(([k]) => k.startsWith('arabic_') || k.startsWith('arabicContent_'))
          .map(([k, v]) => [k.replace(/^(arabic_|arabicContent_)/, ''), v])
      ),
    }));

    const dynAr = (invoice as any).dynamic_values_ar || (invoice as any).dynamicValuesAr || {};
    const dynFieldLabelsAr = (invoice as any).dynamic_field_labels_ar || (invoice as any).dynamicFieldLabelsAr || {};
    const discountsArList = (tableEl as any)?.discountsAr || (invoice as any)?.discount_lines_ar || [];
    const taxesArList = (tableEl as any)?.taxesAr || [];

    const taxLines = ((tableEl as any)?.taxes || []).map((t: any, idx: number) => {
      const trans = t.translatedLabel || t.labelAr || t.arabicLabel || taxesArList[idx]?.labelAr || taxesArList[idx] || dynAr[`tax_${idx}`] || undefined;
      return { ...t, translatedLabel: trans, labelAr: trans };
    });

    // Separate auto loyalty discounts (like Loyal2) from manual discounts
    const rawDiscounts = (tableEl as any)?.discounts || [];
    const autoLoyaltyItem = rawDiscounts.find((d: any) => d._isAuto);
    const autoDiscountAmount = autoLoyaltyItem ? (autoLoyaltyItem.amount || 0) : 0;
    const autoDiscountLabel = autoLoyaltyItem?.label || undefined;
    const autoDiscountLabelAr = dynAr['label_loyalty'] || dynAr['discount_0'] || undefined;

    const discountLines = rawDiscounts.filter((d: any) => !d._isAuto).map((d: any, idx: number) => {
      const trans = d.translatedLabel || d.labelAr || d.arabicLabel || discountsArList[idx]?.labelAr || discountsArList[idx] || dynAr[`discount_${idx}`] || undefined;
      return { ...d, translatedLabel: trans, labelAr: trans };
    });

    const shippingCost = (tableEl as any)?.shippingCost || 0;
    // Named per-item tax breakdown (e.g. a custom name like "rihan"
    // instead of generic "Tax") — QuickInvoiceView bakes this onto the
    // table element as `taxBreakdown` at save time. Without reading it
    // back here, BilingualInvoiceRenderer had nothing but the generic
    // taxLines/scheme fallback, which is why the bilingual PDF showed
    // "Tax (5%)" while the non-bilingual PDF (which does read this
    // field) correctly showed "rihan (5%)".
    const taxBreakdownForPdf = (tableEl as any)?.taxBreakdown;
    // The live preview transliterates each tax component's name into
    // Arabic separately (taxBreakdownAr state) and passes it to
    // BilingualInvoiceRenderer as its own prop — taxBreakdown alone only
    // carries name/rate/amount with no Arabic. Resolve that here too,
    // cached per language exactly like every other label in this file.
    const taxBreakdownAr: Record<string, string> = {};
    if (taxBreakdownForPdf?.length) {
      const _jwtTaxAr = getStoredToken();
      const _headersTaxAr = _jwtTaxAr ? { Authorization: `Bearer ${_jwtTaxAr}` } : {};
      const detectedLangForTax =
        els.find((el: any) => el.translatedLang)?.translatedLang || 'ar';
      await Promise.all(
        taxBreakdownForPdf.map(async (t: any) => {
          if (t.nameAr) { taxBreakdownAr[t.name] = t.nameAr; return; }
          const cacheKey = `cloudairambo_value_cache_${detectedLangForTax}_${t.name}`;
          const cached = localStorage.getItem(cacheKey);
          if (cached) { taxBreakdownAr[t.name] = cached; return; }
          try {
            const res = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(t.name)}&tl=${detectedLangForTax}`, { headers: _headersTaxAr });
            const data = await res.json();
            const translit = data.transliterated || t.name;
            localStorage.setItem(cacheKey, translit);
            taxBreakdownAr[t.name] = translit;
          } catch { /* leave untranslated */ }
        })
      );
    }

    // Inspect the actual Bill To / Seller block to respect user checkbox choices
    const targetLabelRegexBill = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;
    const billToEl = els.find((el: any) =>
      el.type === 'text' && el.textContent && targetLabelRegexBill.test((el.textContent.split('\n')[0] || '').trim())
    );
    const btLines = billToEl?.textContent?.split('\n') || [];

    // A line is a real name ONLY if it's NOT an email, NOT a label with a colon, and NOT a phone number
    const isNameLine = (line: string) => {
      const trimmed = (line || '').trim();
      if (!trimmed) return false;
      if (trimmed.includes('@')) return false;
      if (trimmed.includes(':')) return false;
      if (/^[\d\s+\-().]{6,}$/.test(trimmed)) return false; // phone number
      return true;
    };

    const hasNameInBlock = btLines.length > 1 && isNameLine(btLines[1]);
    const hasEmailInBlock = btLines.some((l: string) => l.includes('@'));
    const hasPhoneInBlock = btLines.some((l: string) => /^[\d\s+\-().]{6,}$/.test(l.trim()) && !l.includes('@'));
    const hasTaxIdInBlock = btLines.some((l: string) => /^(tax\s*id|vat\s*no|gstin|trn)\s*:/i.test(l.trim()));
    const hasAddressInBlock = btLines.slice(1).some((l: string) => {
      const trimmed = l.trim();
      if (!trimmed) return false;
      if (trimmed.includes('@')) return false;
      if (/^[\d\s+\-().]{6,}$/.test(trimmed)) return false;
      if (/^(tax\s*id|vat\s*no|gstin|trn|store\s*id|period|ward|bed|appt|shift)\s*:/i.test(trimmed)) return false;
      if (hasNameInBlock && trimmed.toLowerCase() === (invoice.clientName || '').trim().toLowerCase()) return false;
      if (isNameLine(trimmed) && trimmed === btLines[1]) return false;
      return true;
    });

    const inc = (invoice as any).includeFields || (invoice as any).include_fields || {};

    const clientName = inc.name === false ? '' : (hasNameInBlock ? (invoice.clientName || '') : '');
    const clientEmail = inc.email === false ? '' : (hasEmailInBlock ? (invoice.clientEmail || '') : '');
    const clientPhone = inc.phone === false ? '' : (hasPhoneInBlock ? ((invoice as any).clientPhone || '') : '');
    const buyerTaxId = inc.taxId === false ? '' : (hasTaxIdInBlock ? rawSavedBuyerTaxId : '');
    const clientAddress = inc.address === false ? '' : (hasAddressInBlock ? ((invoice as any).clientAddress || (invoice as any).client_address || '') : '');

    const btArLines = billToEl?.arabicContent?.split('\n') || [];
    const clientNameAr = inc.name === false ? '' : (hasNameInBlock ? ((btArLines.length > 1 && isNameLine(btArLines[1])) ? btArLines[1] : ((invoice as any).dynamicValuesAr?.client_name || '')) : '');
    const clientAddressAr = inc.address === false ? '' : (hasAddressInBlock ? ((invoice as any).dynamicValuesAr?.client_address || '') : '');

    let detectedLang =
      (invoice as any).translated_lang ||
      (invoice as any).translatedLang ||
      els.find((el: any) => el.translatedLang)?.translatedLang ||
      els.flatMap((el: any) => el.columns || []).find((c: any) => c.translatedLang)?.translatedLang ||
      els.flatMap((el: any) => el.rows || []).find((r: any) => r.translatedLang)?.translatedLang ||
      'en';

    // Sanity check: If content is Devanagari (Hindi), do not treat as Arabic!
    if (detectedLang === 'ar') {
      const hasDevanagari = els.some((el: any) => /[\u0900-\u097F]/.test(el.arabicContent || '')) ||
        /[\u0900-\u097F]/.test(JSON.stringify(dynAr));
      if (hasDevanagari) detectedLang = 'hi';
    }

    // "Scan to view invoice" / "Scan to track your order" is injected by the
    // BACKEND after save (elementsForCreate strips the template's own QR +
    // caption so the backend can attach its own token-based one). Being
    // added post-save, it never passes through handleTranslateToArabic — so
    // it has no arabicContent and rendered English-only. Resolve + cache it
    // here, same pattern as every other label in this file.
    const _jwtCaptionAr = getStoredToken();
    const _headersCaptionAr = _jwtCaptionAr ? { Authorization: `Bearer ${_jwtCaptionAr}` } : {};
    const _getCachedCaptionTranslated = async (label: string): Promise<string> => {
      const cacheKey = `cloudairambo_label_cache_${detectedLang}_${label}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) return cached;
      try {
        const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(label)}&tl=${detectedLang}`, { headers: _headersCaptionAr });
        const data = await res.json();
        const translated = data.translated || label;
        localStorage.setItem(cacheKey, translated);
        return translated;
      } catch { return label; }
    };

    const fallbackInvCaption =
      (invoice as any).dynamic_values_ar?.['qr_caption_invoice'] ||
      (invoice as any).dynamicValuesAr?.['qr_caption_invoice'] ||
      await _getCachedCaptionTranslated('Scan to view invoice');

    const fallbackTrackCaption =
      (invoice as any).dynamic_values_ar?.['qr_caption_tracking'] ||
      (invoice as any).dynamicValuesAr?.['qr_caption_tracking'] ||
      await _getCachedCaptionTranslated('Scan to track your order');

    const patchedElsWithCaptionsBase = await Promise.all(
      patchedEls.map(async (el: any) => {
        if (el.type !== 'text') return el;
        const t = (el.textContent || '').trim();
        if (t === 'Scan to view invoice') {
          return { ...el, arabicContent: el.arabicContent || fallbackInvCaption, translatedLang: detectedLang };
        }
        if (t === 'Scan to track your order' || t.toLowerCase().includes('scan to track')) {
          return { ...el, arabicContent: el.arabicContent || fallbackTrackCaption, translatedLang: detectedLang };
        }
        return el;
      })
    );

    // Custom Bill To / Seller rows (STORE ID, PERIOD, ...) can come back with
    // a missing or still-English translated line — translate them here so
    // the PDF matches the live preview.
    const billToRe = /^(bill\s*to|billed\s*to|invoice\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|guest|member|subscriber|attn|ship\s*to|deliver\s*to)\s*:/i;
    const stdLineRe = /^(tax\s*id|vat\s*no|gstin|trn|phone|tel|mobile|mob|contact|cell|email|e-mail|mail|address|adress|addr|attention)\s*[:\-]/i;
    const fixBillToArabic = async (el: any) => {
      if (el.type !== 'text' || !el.textContent) return el;
      const engLines = el.textContent.split('\n');
      if (!billToRe.test(engLines[0].trim())) return el;
      const out = (el.arabicContent || '').split('\n');
      while (out.length < engLines.length) out.push('');
      const firstHasValue = /:\s*\S/.test(engLines[0]);
      for (let i = firstHasValue ? 1 : 2; i < engLines.length; i++) {
        const engLine = engLines[i].trim();
        const m = engLine.match(/^([^:@]{1,30}):\s*(\S.*)$/);
        if (!m || stdLineRe.test(engLine)) continue;
        const cur = (out[i] || '').trim();
        // already properly translated (contains non-ASCII script) — leave it
        if (cur && cur !== engLine && /[^\x00-\x7F]/.test(cur)) continue;
        const rawLabel = m[1].trim();
        const titled = rawLabel.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        const echoed = (r: string) => !r || r.trim().toLowerCase() === rawLabel.toLowerCase();
        let labelAr = await _getCachedCaptionTranslated(titled);
        if (echoed(labelAr) && /\bId\b/.test(titled)) {
          labelAr = await _getCachedCaptionTranslated(titled.replace(/\bId\b/, 'Identification Number'));
        }
        if (echoed(labelAr)) {
          try {
            const tr = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(rawLabel)}&tl=${detectedLang}`, { headers: _headersCaptionAr });
            labelAr = (await tr.json()).transliterated || labelAr;
          } catch { /* keep English */ }
        }
        const val = m[2].trim();
        const isCode = /^[A-Z0-9\-_]{4,}$/i.test(val) && /\d/.test(val);
        const isNumeric = /^[\d\s+\-().#:/\\%]+$/.test(val);
        const valAr = (isCode || isNumeric) ? val : await _getCachedCaptionTranslated(val);
        out[i] = `${labelAr}: ${valAr}`;
      }
      return { ...el, arabicContent: out.join('\n'), translatedLang: detectedLang };
    };
    const patchedElsWithCaptions = await Promise.all(patchedElsWithCaptionsBase.map(fixBillToArabic));

    // Detect if this invoice contains a tracking token or tracking QR
    const hasTrackingQR = (invoice.elements || []).some((el: any) =>
      el.id?.startsWith('token-') ||
      el._auto === 'tracking' ||
      el._auto === 'tracking_label' ||
      (el.type === 'image' && el.id?.includes('token'))
    ) || Boolean((invoice as any).show_tracking_qr || (invoice as any).showTrackingQR);

    const sym = getCurrencyDisplay(invoice.currency || activeCurrency, currencyDisplayMode);

    // 1. Mount hidden container matching exact page width
    const container = document.createElement('div');
    container.style.cssText = `position:fixed;left:0;top:0;width:${receiptWidth}px;z-index:-99999;opacity:1;pointer-events:none;background:#ffffff;`;
    document.body.appendChild(container);

    const { createRoot } = await import('react-dom/client');
    const root = createRoot(container);

    await new Promise<void>(resolve => {
      root.render(
        React.createElement(BilingualInvoiceRenderer, {
          template: { elements: patchedElsWithCaptions, pageSize: invoice.pageSize || 'A4' },
          clientName,
          clientNameAr,
          clientEmail,
          clientPhone,
          clientAddress,
          clientAddressAr,
          buyerTaxId: buyerTaxId || undefined,
          isB2B,
          invoiceDate: invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString() : '',
          invoiceTime: '',
          notes: (invoice as any).notes || '',
          lineItems,
          taxLines,
          taxBreakdown: taxBreakdownForPdf,
          taxBreakdownAr,
          discountLines,
          autoDiscountAmount,
          autoDiscountLabel,
          autoDiscountLabelAr,
          dynamicValues: (invoice as any).dynamicValues || {},
          dynamicValuesAr: dynAr,
          dynamicFieldLabelsAr: dynFieldLabelsAr,
          sym,
          shippingCost,
          invoiceStatus: invoice.status,
          isBilingual: (invoice as any).isBilingual === true || (invoice as any).is_bilingual === true,
          translateTargetLang: detectedLang,
          bilingualLayout: 'side',
          isThermal,
          currencyCode: invoice.currency || activeCurrency,
          invoiceNumber: invoiceNum,
          showAmountInWords: (invoice as any).showAmountInWords === true,
          amountInWordsTranslated: amountInWordsTranslatedFromEls,
          autoQrPosition: (invoice as any).qr_position || (invoice as any).autoQrPosition || 'center',
          savedElements: patchedElsWithCaptions,
          showTrackingQR: hasTrackingQR,
        })
      );
      setTimeout(resolve, 800);
    });

    // 2. Thermal receipts stay on the dedicated 302px pipeline
    if (isThermal) {
      const targetElement = (container.firstElementChild as HTMLElement) || container;
      const measuredContentHeight = Math.ceil(
        Math.max(
          targetElement.scrollHeight,
          targetElement.offsetHeight,
          targetElement.getBoundingClientRect().height
        )
      );
      const exactReceiptHeight = Math.max(150, measuredContentHeight + 16);

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#fff;font-family:system-ui,sans-serif;width:302px;overflow:visible;}</style></head><body>${container.innerHTML}</body></html>`;
      root.unmount();
      document.body.removeChild(container);
      const { pdfApi } = await import('./utils/api');
      return await pdfApi.generate(html, receiptWidth, exactReceiptHeight);
    }

    // 3. A4 / LETTER: Send clean BilingualInvoiceRenderer HTML to fast backend (Instant, Zero Freeze)
    try {
      const pageHeight = invoice.pageSize === 'LETTER' ? 1056 : 1123;
      const totalContentHeight = Math.max(container.scrollHeight, container.offsetHeight, pageHeight);

      const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=Noto+Sans+Arabic:wght@400;600;700;800&family=Alex+Brush&family=Dancing+Script:wght@400;700&family=Noto+Sans+Devanagari:wght@400;600;700&family=Noto+Sans+Bengali:wght@400;700&family=Noto+Sans+Tamil:wght@400;700&family=Noto+Sans+Telugu:wght@400;700&family=Noto+Sans+Kannada:wght@400;700&family=Noto+Sans+Malayalam:wght@400;700&family=Noto+Sans+Gujarati:wght@400;700&display=swap" rel="stylesheet"/>
    <style>
      * { 
        box-sizing: border-box !important; 
        -webkit-print-color-adjust: exact !important; 
        print-color-adjust: exact !important; 
      }
      
      /* 1. Generous top and bottom breathing room on EVERY page (Page 1, Page 2, Page 3) */
      /* 2. Left & right margins stay 0 so nothing clips on the right */
      @page { 
        size: ${invoice.pageSize === 'LETTER' ? 'letter' : 'a4'} portrait; 
        margin-top: 14mm !important; 
        margin-bottom: 16mm !important; 
        margin-left: 0 !important; 
        margin-right: 0 !important; 
      }

      html, body { 
        margin: 0 auto !important; 
        padding: 0 !important; 
        background: #fff !important; 
        width: ${receiptWidth}px !important; 
        max-width: ${receiptWidth}px !important;
        overflow: visible !important;
        font-family: system-ui, -apple-system, sans-serif; 
      }

      /* Nudges content ~7px to the left to be truly centered, with equal gutters */
      body > div, .bilingual-invoice-root {
        width: 100% !important;
        max-width: ${receiptWidth}px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        padding-left: 10px !important;
        padding-right: 24px !important;
        box-sizing: border-box !important;
        margin: 0 auto !important;
      }

      table { 
        page-break-inside: auto; 
        width: 100% !important; 
        border-collapse: collapse !important;
      }
      thead { display: table-header-group !important; }
      tr { break-inside: avoid !important; page-break-inside: avoid !important; }
    </style>
  </head>
  <body>${container.innerHTML}</body>
</html>`;

      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }

      const { pdfApi } = await import('./utils/api');
      return await pdfApi.generate(html, receiptWidth, pageHeight, totalContentHeight);
    } finally {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  } catch (err) {
    console.error('Bilingual PDF render failed:', err);
    return null;
  }
};

const renderPOSReceiptToPdf = async (invoice: InvoiceRecord): Promise<Blob | null> => {
  try {
    const els = invoice.elements || [];
    const tableEl = els.find((el: any) => el.type === 'table');
    const isThermal = (invoice.pageSize || '').startsWith('THERMAL');
    const receiptWidth = PAGE_PRESETS[invoice.pageSize as PageSizePreset]?.width || (isThermal ? 302 : 794);

    const _stdKeysPOS = new Set(['description', 'qty', 'rate', 'total', 'sno', 'serial', 'index']);
    const extraColumns = ((tableEl as any)?.columns || []).filter(
      (c: any) => !_stdKeysPOS.has(c.key) && !isSerialNoColumn(c)
    );
    const _isBilingualPOS = (invoice as any).isBilingual === true || (invoice as any).is_bilingual === true;
    let _detectedLangPOS =
      (invoice as any).translated_lang ||
      (invoice as any).translatedLang ||
      els.find((el: any) => el.translatedLang)?.translatedLang ||
      'en';

    if (_detectedLangPOS === 'ar') {
      const hasDevanagari = els.some((el: any) => /[\u0900-\u097F]/.test(el.arabicContent || '')) ||
        /[\u0900-\u097F]/.test(JSON.stringify((invoice as any).dynamic_values_ar || {}));
      if (hasDevanagari) _detectedLangPOS = 'hi';
    }
    const detectedLang = _detectedLangPOS;
    const _valCacheKeyPOS = (v: string) => `cloudairambo_value_cache_${_detectedLangPOS}_${v}`;
    const _jwtPOS = getStoredToken();
    const _headersPOS = _jwtPOS ? { Authorization: `Bearer ${_jwtPOS}` } : {};
    const _getCachedValueTranslatedPOS = async (value: string): Promise<string> => {
      const clean = String(value ?? '').trim();
      if (!_isBilingualPOS || !clean || /^[\d.,%\s]+$/.test(clean)) return '';
      const cached = localStorage.getItem(_valCacheKeyPOS(clean));
      if (cached) return cached;
      try {
        const res = await fetch(`${BACKEND}/translate?text=${encodeURIComponent(clean)}&tl=${_detectedLangPOS}`, { headers: _headersPOS });
        const data = await res.json();
        let translated = data.translated || clean;
        if (translated.toLowerCase() === clean.toLowerCase() && /[a-zA-Z]/.test(clean)) {
          const tRes = await fetch(`${BACKEND}/transliterate?text=${encodeURIComponent(clean)}&tl=${_detectedLangPOS}`, { headers: _headersPOS });
          const tData = await tRes.json();
          translated = tData.transliterated || translated;
        }
        localStorage.setItem(_valCacheKeyPOS(clean), translated);
        return translated;
      } catch { return ''; }
    };
    const lineItems = await Promise.all((tableEl?.rows || []).map(async (r: any) => {
      const customColAr = _isBilingualPOS
        ? Object.fromEntries(
            await Promise.all(extraColumns.map(async (col: any) => {
              const raw = r.values[col.key] ?? '';
              const ar = r.values[`arabic_${col.key}`] || r.values[`arabicContent_${col.key}`] || (await _getCachedValueTranslatedPOS(String(raw)));
              return [col.key, ar];
            }))
          )
        : undefined;
      return {
        id: r.id,
        description: r.values.description || '',
        qty: parseFloat(String(r.values.qty)) || 0,
        rate: parseFloat(String(r.values.rate)) || 0,
        arabicName: r.values.arabicName || r.values.arabicDescription || undefined,
        ...Object.fromEntries(extraColumns.map((col: any) => [col.key, r.values[col.key] ?? ''])),
        ...(customColAr ? { customColAr } : {}),
      };
    }));

    const taxLines = ((tableEl as any)?.taxes || []).map((t: any) => ({
      ...t,
      translatedLabel: t.translatedLabel || t.labelAr || t.arabicLabel || undefined
    }));
    const discountLines = ((tableEl as any)?.discounts || []).map((d: any) => ({
      ...d,
      translatedLabel: d.translatedLabel || d.labelAr || d.arabicLabel || undefined
    }));
    const shippingCost = (tableEl as any)?.shippingCost || 0;

    // Net base per row (excludes inclusive tax) — this is also what gets
    // passed to POSReceiptRenderer as netSubtotal, so the displayed
    // Subtotal and the tax computed on it always agree.
    const netSubtotal = (tableEl?.rows || []).reduce((sum: number, r: any) => {
      const qty = parseFloat(String(r.values.qty)) || 0;
      const rate = parseFloat(String(r.values.rate)) || 0;
      const lineTotal = qty * rate;
      const taxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
      const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
      const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
      return sum + base;
    }, 0);

    // Discount reduces the taxable base before product tax is calculated.
    const discTotalForProductTax = discountLines.filter((d: any) => d.rate > 0).reduce((s: number, d: any) => s + netSubtotal * (d.rate / 100), 0);
    const discountRatioForProductTax = netSubtotal > 0 ? Math.min(1, discTotalForProductTax / netSubtotal) : 0;

    // Per-item tax breakdown — mirrors buildQuickInvoicePrintJob's productTaxTotal.
    const productTaxTotal = (tableEl?.rows || []).reduce((sum: number, r: any) => {
      const qty = parseFloat(String(r.values.qty)) || 0;
      const rate = parseFloat(String(r.values.rate)) || 0;
      const lineTotal = qty * rate;
      const taxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
      if (taxRate <= 0) return sum;
      const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
      const base = inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
      return sum + base * (1 - discountRatioForProductTax) * (taxRate / 100);
    }, 0);

    // Named GST/VAT/QST breakdown (CGST/SGST/IGST/QST/etc.) — same approach
    // as handleExportPDF's table branch above. Only possible when this
    // invoice was saved with seller/buyer region context; older invoices
    // fall back to POSReceiptRenderer's blended "Total Tax" line.
    let taxBreakdown: { name: string; rate: number; amount: number }[] | undefined;
    // Trust the exact breakdown the invoice was saved with (same one the
    // live QuickInvoiceView preview showed, including any custom outlet
    // tax name like "rihan") before falling back to recomputing generically
    // from the seller's country-default scheme — which has no idea about
    // the outlet's actual selected/custom tax scheme name and would show
    // something like the raw country default ("PPN") instead.
    const savedBreakdownThermal = (tableEl as any)?.taxBreakdown;
    if (Array.isArray(savedBreakdownThermal) && savedBreakdownThermal.length > 0 && productTaxTotal > 0) {
      const savedSum = savedBreakdownThermal.reduce((s: number, t: any) => s + (t.amount || 0), 0);
      const k = savedSum > 0 ? productTaxTotal / savedSum : 1;
      taxBreakdown = savedBreakdownThermal.map((t: any) => ({
        name: t.name,
        rate: t.rate,
        amount: savedSum > 0 ? (t.amount || 0) * k : productTaxTotal / savedBreakdownThermal.length,
      }));
    }
    const sellerCountryForTax = (invoice as any).sellerCountry || (invoice as any).seller_country;
    if (!taxBreakdown && sellerCountryForTax && productTaxTotal > 0) {
      const taxScheme = getDefaultSchemeForCountryAndRegion(sellerCountryForTax, (invoice as any).sellerRegion || (invoice as any).seller_region);
      const buyerRegionForTax = (invoice as any).buyerRegion || '';
      if (taxScheme.kind === 'component') {
        const combined: Record<string, { rate: number; amount: number }> = {};
        (tableEl?.rows || []).forEach((r: any) => {
          const qty = parseFloat(String(r.values.qty)) || 0;
          const rate = parseFloat(String(r.values.rate)) || 0;
          const lineTotal = qty * rate;
          const rowTaxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
          if (rowTaxRate <= 0) return;
          const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
          const base = inclusive ? lineTotal / (1 + rowTaxRate / 100) : lineTotal;
          const discountedBase = base * (1 - discountRatioForProductTax);
          const breakdown = computeTaxBreakdown(
            taxScheme, discountedBase,
            { sellerRegion: (invoice as any).sellerRegion, buyerRegion: buyerRegionForTax }, rowTaxRate
          );
          breakdown.lines.forEach(l => {
            if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
            combined[l.name].amount += l.amount;
          });
        });
        const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
        if (entries.length > 0) taxBreakdown = entries;
      } else {
        // Simple/single-name scheme — fall back to the row's own custom
        // tax name (same fallback POSView's taxBreakdown useMemo uses),
        // instead of silently dropping to the generic blended tax line.
        const combined: Record<string, { rate: number; amount: number }> = {};
        (tableEl?.rows || []).forEach((r: any) => {
          const qty = parseFloat(String(r.values.qty)) || 0;
          const rate = parseFloat(String(r.values.rate)) || 0;
          const lineTotal = qty * rate;
          const rowTaxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
          if (rowTaxRate <= 0) return;
          const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
          const base = inclusive ? lineTotal / (1 + rowTaxRate / 100) : lineTotal;
          const discountedBase = base * (1 - discountRatioForProductTax);
          const customLabel = r.values.b2bTaxName || r.values.taxName ||
            taxScheme.components?.[0]?.name || taxScheme.label.replace(/\s*\(.*?\)/, '') || 'Tax';
          if (!combined[customLabel]) combined[customLabel] = { rate: rowTaxRate, amount: 0 };
          combined[customLabel].amount += discountedBase * (rowTaxRate / 100);
        });
        const entries = Object.entries(combined).map(([name, v]) => ({ name, rate: v.rate, amount: v.amount }));
        if (entries.length > 0) taxBreakdown = entries;
      }
    }

   
    const sym = getCurrencyDisplay(invoice.currency || activeCurrency, currencyDisplayMode);

    // Named tax-breakdown components (e.g. "rihan") are custom labels, not
    // dictionary words — translate/transliterate them the same way custom
    // column values are already handled above in this function, so the
    // Arabic subscript under "rihan (5%)" actually has something to render.
    const taxBreakdownAr: Record<string, string> = {};
    if (taxBreakdown && taxBreakdown.length > 0 && _isBilingualPOS) {
      await Promise.all(
        taxBreakdown.map(async (t) => {
          taxBreakdownAr[t.name] = await _getCachedValueTranslatedPOS(t.name);
        })
      );
    }

    const invoiceNum = invoice.invoiceNumber || '';
    const qrCaptionFallback = 
      (invoice as any).dynamic_values_ar?.['qr_caption_invoice'] ||
      (invoice as any).dynamicValuesAr?.['qr_caption_invoice'] ||
      '';

    const patchedEls = els.map((el: any) => {
      if (el.type !== 'text') return el;
      let arabic = el.arabicContent;
      if (arabic?.includes('{invoice_number}')) {
        arabic = arabic.replace(/\{invoice_number\}/g, invoiceNum);
      }
      if ((el.textContent || '').trim() === 'Scan to view invoice' && !arabic) {
        arabic = qrCaptionFallback;
      }
      return { ...el, arabicContent: arabic };
    });

    const container = document.createElement('div');
    container.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${receiptWidth}px`;
    document.body.appendChild(container);
    const { createRoot } = await import('react-dom/client');
    const root = createRoot(container);

    const POSReceiptRenderer = (await import('./components/POSReceiptRenderer')).default;

    const resolvedClientNameAr = 
      (invoice as any).clientNameAr || 
      (invoice as any).client_name_ar || 
      (invoice as any).dynamicValuesAr?.clientName || 
      (invoice as any).dynamic_values_ar?.clientName || 
      undefined;

    const inc = (invoice as any).includeFields || (invoice as any).include_fields || {};

    await new Promise<void>(resolve => {
      root.render(
        React.createElement(POSReceiptRenderer, {
          template: { elements: patchedEls, includeFields: inc, include_fields: inc },
          containerWidthProp: receiptWidth,
          clientName: inc.name === false ? '' : (invoice.clientName || ''),
          clientNameAr: inc.name === false ? undefined : resolvedClientNameAr,
          clientEmail: inc.email === false ? '' : (invoice.clientEmail || ''),
          clientPhone: inc.phone === false ? '' : ((invoice as any).clientPhone || ''),
          clientAddress: inc.address === false ? '' : ((invoice as any).clientAddress || ''),
          buyerTaxId: inc.taxId === false ? undefined : ((invoice as any).buyerTaxId || (invoice as any).buyer_tax_id || (invoice as any).clientGSTIN || undefined),
          invoiceDate: invoice.createdAt
  ? new Date(invoice.createdAt.endsWith('Z') ? invoice.createdAt : invoice.createdAt + 'Z').toLocaleDateString()
  : new Date().toLocaleDateString(),
          invoiceTime: '',
          notes: '',
          lineItems,
          extraColumns,
          taxLines,
          discountLines,
          sym,
          shippingCost,
          productTaxTotal,
          taxBreakdown,
          taxBreakdownAr,
          netSubtotal,
          invoiceStatus: invoice.status,
          translateTargetLang: detectedLang,
          isBilingual: (invoice as any).isBilingual === true,
          isThermal,
          showTokenQRPlaceholder: false,
          showWalkIn: true,
          dynamicValues: (invoice as any).dynamicValues || {},
          dynamicValuesAr: (invoice as any).dynamicValuesAr || (invoice as any).dynamic_values_ar || {},
          dynamicFieldLabelsAr: (invoice as any).dynamicFieldLabelsAr || (invoice as any).dynamic_field_labels_ar || {},
          showAmountInWords: (invoice as any).showAmountInWords === true || (invoice as any).show_amount_in_words === true,
          amountInWords: (invoice as any).amount_in_words || (invoice as any).amountInWords || amountToWords(invoice.amount || 0, invoice.currency || activeCurrency),
          amountInWordsTranslated: (invoice as any).amount_in_words_translated || (invoice as any).amountInWordsAr || (invoice as any).dynamicValuesAr?.amountInWords || '',
        })
      );
      setTimeout(resolve, 800);
    });

    const targetElement = (container.firstElementChild as HTMLElement) || container;
    const measuredContentHeight = Math.ceil(
      Math.max(
        targetElement.scrollHeight,
        targetElement.offsetHeight,
        targetElement.getBoundingClientRect().height
      )
    );
    const exactReceiptHeight = isThermal
      ? Math.max(150, measuredContentHeight + 16)
      : 1123;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#fff;font-family:system-ui,sans-serif;}</style></head><body>${container.innerHTML}</body></html>`;
    root.unmount();
    document.body.removeChild(container);

    const { pdfApi } = await import('./utils/api');
    const blob = await pdfApi.generate(html, receiptWidth, exactReceiptHeight);
    return blob;
  } catch (err) {
    console.error('POS receipt PDF render failed:', err);
    return null;
  }
};
  // --- Master App Handlers (Google Drive Sync, Clients and Bills Ledger) ---
  const handleGoogleLogin = async () => {
    try {
      const result = await googleSignIn();
      if (result) {
        setGoogleUser(result.user);
        setGoogleToken(result.accessToken);
        const updatedLogs = addLogItem(logs, 'DRIVE_AUTH_LOGIN', `Connected Google user: ${result.user.email}`);
        setLogs(updatedLogs);
        alert(`Successfully synchronized with Google Account: ${result.user.email}`);
      }
    } catch (err) {
      console.error(err);
      alert("Failed Google Drive account link attempt.");
    }
  };

  const handleGoogleLogout = async () => {
    try {
      await googleSignOut();
      setGoogleUser(null);
      setGoogleToken(null);
      const updatedLogs = addLogItem(logs, 'DRIVE_AUTH_LOGOUT', 'Signed out from Google sync account');
      setLogs(updatedLogs);
      alert("Google session logged out successfully.");
    } catch (err) {
      console.error(err);
    }
  };

  const handleRestoreBackup = (data: { bills: InvoiceRecord[]; accounts: ClientAccount[]; logs: ActivityLog[] }) => {
    setBills(data.bills);
    setAccounts(data.accounts);
    if (data.logs && data.logs.length > 0) {
      setLogs(data.logs);
    } else {
      const restoredLogs = addLogItem([], 'BACKUP_RESTORED', 'Database ledger cleanly restored from manual backup JSON file.');
      setLogs(restoredLogs);
    }
  };

  // Google Drive Invoice Backup Sync Upload
  const handleSyncInvoiceToDrive = async (billId: string) => {
    const p = getPermissions(authUser?.role, authUser?.permissions, authUser?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
    if (!p.canUseDriveSync) {
      alert('Google Drive sync is a Pro feature. Upgrade your plan to use it.');
      return;
    }
    if (!googleToken) {
      alert("Google Drive authentication token not found. Please connect your account first on the Home dashboard.");
      return;
    }

    setIsSyncingId(billId);
    try {
      const bill = bills.find(b => b.id === billId);
      if (!bill) throw new Error("Invoice record not found inside database list");

      const uLogs1 = addLogItem(logs, 'DRIVE_SYNC_START', `Rebuilding and exporting PDF Blob for backing up invoice ${bill.invoiceNumber}`);
      setLogs(uLogs1);

      // 1. Generate the PDF Blob on the fly using our adjusted compile action!
      const pdfBlob = await handleExportPDF(bill, true);
      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        throw new Error("Unable to build compiled pdf blob. Try again.");
      }

      // 2. Fetch or create app sync folder
      const folderId = await getOrCreateFolder(googleToken);
      
      // 3. Upload file to Google Drive folder
      const filename = `${bill.invoiceNumber}_${bill.clientName.replace(/\s+/g, '_')}.pdf`;
      const uploadResult = await uploadInvoicePDF(googleToken, filename, pdfBlob, folderId);

      // 4. Update the ledger invoice with drive files URL and ID!
      const updatedBills = bills.map(b => {
        if (b.id === billId) {
          return {
            ...b,
            driveFileId: uploadResult.id,
            driveFileUrl: `https://drive.google.com/file/d/${uploadResult.id}/view?usp=drivesdk`
          };
        }
        return b;
      });
      setBills(updatedBills);

      const uLogs2 = addLogItem(logs, 'DRIVE_SYNC_SUCCESS', `Uploaded ${filename} to cloudairambo Google Drive (File ID: ${uploadResult.id})`);
      setLogs(uLogs2);
      
      alert(`Backed up successfully to Google Drive!\nFile saved: ${filename}`);
    } catch (err: any) {
      console.error(err);
      const uLogsErr = addLogItem(logs, 'DRIVE_SYNC_ERROR', `Failed upload attempt: ${err?.message || String(err)}`);
      setLogs(uLogsErr);
      alert(`Google Drive Backup Error: ${err?.message || "Verify your internet or token session."}`);
    } finally {
      setIsSyncingId(null);
    }
  };

  // Manual claims status cycler (Draft -> Sent -> Paid -> Overdue -> Cancelled)
const handleToggleBillStatus = async (invoiceId: string) => {
    const nextBills = bills.map(b => {
      if (b.id === invoiceId) {
        if (b.status === 'Updated') return b; // locked — cannot change
        const curr = String(b.status || 'Draft').trim().toLowerCase();
        let nextStatus = 'Draft';
        if (curr === 'draft') nextStatus = 'Sent';
        else if (curr === 'sent' || curr === 'pending') nextStatus = 'Paid';
        else if (curr === 'paid') nextStatus = 'Overdue';
        else if (curr === 'overdue') nextStatus = 'Cancelled';
        else if (curr === 'cancelled') nextStatus = 'Draft';
        else nextStatus = 'Draft';

        const logMsg = `Invoice ${b.invoiceNumber} status changed to ${nextStatus.toUpperCase()} by ${authUser?.name || 'user'} (${authUser?.role || 'unknown'}) at ${new Date().toLocaleString()}`;
        const updatedLogs = addLogItem(logs, 'INVOICE_STATUS_CHANGED', logMsg);
        setLogs(updatedLogs);
        postLog('INVOICE_STATUS_CHANGED', logMsg);
        return { ...b, status: nextStatus };
      }
      return b;
    });
    setBills(nextBills);

    const token = getStoredToken();
    if (token && !isNaN(Number(invoiceId))) {
      const updated = nextBills.find(b => b.id === invoiceId);
      if (updated) {
        try {
  const { invoicesApi } = await import('./utils/api');
  await invoicesApi.updateStatus(Number(invoiceId), updated.status.toLowerCase());
await new Promise(resolve => setTimeout(resolve, 500));
const dbInvoices = await invoicesApi.list(selectedOutletFilter ?? undefined);
  if (dbInvoices?.length) {
    setBills(dbInvoices.map((inv: any) => ({
      id: String(inv.id),
      invoiceNumber: inv.invoice_number,
      clientName: inv.client_name || 'General Client',
      clientEmail: inv.client_email || '',
      clientPhone: inv.client_phone || '',
      clientAddress: inv.client_address || '',
      amount: inv.amount || inv.total || 0,
      status: inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : 'Draft',
      currency: inv.currency || 'USD',
      amountPaid: inv.amount_paid || 0,
      balanceDue: inv.balance_due || 0,
      paymentDate: inv.payment_date || '',
      lastEditedAt: inv.last_edited_at || '',
      editLog: inv.edit_log || [],
      createdAt: inv.created_at,
      elements: inv.elements || [],
      pageSize: inv.page_size || 'A4',
      payment_mode: inv.payment_mode || '',
      isBilingual: inv.is_bilingual || false,
      outlet_id: inv.outlet_id ?? null,
      cancellation_reason: inv.cancellation_reason || '',
      cancelled_by: inv.cancelled_by || null,
      dynamicValuesAr: inv.dynamic_values_ar || {},
      dynamicFieldLabelsAr: inv.dynamic_field_labels_ar || {},
      seller_country: inv.seller_country || undefined,
      seller_region: inv.seller_region || undefined,
      buyer_region: inv.buyer_region || undefined,
      publicToken: inv.public_token || undefined,
  showAmountInWords: inv.show_amount_in_words || false,
    })));
  }
} catch (e) {
  console.warn('DB status sync failed:', e);
}
      }
    }
  };

  // Ledger item purge
  const handleDeleteBill = async (invoiceId: string) => {
    const target = bills.find(b => b.id === invoiceId);
    const label = target ? target.invoiceNumber : invoiceId;
    const nextBills = bills.filter(b => b.id !== invoiceId);
    setBills(nextBills);

    const token = getStoredToken();
    if (token && !isNaN(Number(invoiceId))) {
      try {
        const { invoicesApi } = await import('./utils/api');
        await invoicesApi.delete(Number(invoiceId));
      } catch (e) {
        console.warn('DB delete failed:', e);
      }
    }

   const updatedLogs = addLogItem(logs, 'INVOICE_DELETED', `Claim ${label} purged from accounts ledger`);
setLogs(updatedLogs);
if (authUser) await postLog('INVOICE_DELETED', `Invoice ${label} deleted by ${authUser.name}`);
  };

  // Client directory management routines
const handleAddAccount = async (newAct: Omit<ClientAccount, 'id' | 'createdAt'>) => {
    if (!requireAtLeastOneOutlet()) return;
    const token = getStoredToken();
    if (token) {
      try {
        const { clientsApi } = await import('./utils/api');
        const saved = await clientsApi.create({
          name: newAct.name, email: newAct.email, phone: newAct.phone,
          company: newAct.company, address: newAct.address,
          visit_count: newAct.visitCount || 0,
          total_spend: newAct.totalSpend || 0,
          exempt_from_auto_discount: newAct.exemptFromAutoDiscount || false,
          outlet_id: selectedOutletFilter ?? null,
        });
        const fullAct: ClientAccount = {
          ...newAct,
          id: String(saved.id),
          createdAt: saved.created_at,
          visitCount: saved.visit_count || 0,
          totalSpend: saved.total_spend || 0,
          exemptFromAutoDiscount: saved.exempt_from_auto_discount || false,
          outletId: saved.outlet_id ?? null,
        };
        setAccounts(prev => [...prev, fullAct]);
      } catch (e) {
        const fullAct: ClientAccount = { ...newAct, id: `act-${Date.now()}`, createdAt: new Date().toISOString() };
        setAccounts(prev => [...prev, fullAct]);
      }
    } else {
      const fullAct: ClientAccount = { ...newAct, id: `act-${Date.now()}`, createdAt: new Date().toISOString() };
      setAccounts(prev => [...prev, fullAct]);
    }
    const updated = addLogItem(logs, 'ACCOUNT_CREATED', `Enrolled client: ${newAct.name}`);
    setLogs(updated);
  };

const handleUpdateAccount = async (updatedAct: ClientAccount) => {
    setAccounts(prev => prev.map(a => a.id === updatedAct.id ? updatedAct : a));
    const token = getStoredToken();
    if (token && !isNaN(Number(updatedAct.id))) {
      try {
        const { clientsApi } = await import('./utils/api');
        await clientsApi.update(Number(updatedAct.id), {
          name: updatedAct.name, email: updatedAct.email, phone: updatedAct.phone,
          company: updatedAct.company, address: updatedAct.address,
          visit_count: updatedAct.visitCount || 0,
          total_spend: updatedAct.totalSpend || 0,
          exempt_from_auto_discount: updatedAct.exemptFromAutoDiscount || false,
        });
      } catch (e) { console.warn('Client update failed:', e); }
    }
    const updated = addLogItem(logs, 'ACCOUNT_UPDATED', `Updated client: ${updatedAct.name}`);
    setLogs(updated);
  };

 const handleDeleteAccount = async (id: string) => {
    const target = accounts.find(a => a.id === id);
    setAccounts(prev => prev.filter(a => a.id !== id));
    const token = getStoredToken();
    if (token && !isNaN(Number(id))) {
      try {
        const { clientsApi } = await import('./utils/api');
        await clientsApi.delete(Number(id));
      } catch (e) { console.warn('Client delete failed:', e); }
    }
    const updated = addLogItem(logs, 'ACCOUNT_DELETED', `Deleted client: ${target?.name || id}`);
    setLogs(updated);
  };

   // Global Discount Rules management routines — Direct to PostgreSQL DB
  const handleAddDiscountRule = async (newRule: Omit<DiscountRule, 'id' | 'createdAt'>) => {
    const token = getStoredToken();
    if (!token) return alert('Please sign in to save discount rules.');

    try {
      const res = await fetch(`${BACKEND}/discount-rules/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          label: newRule.label || null,
          metric: newRule.metric,
          threshold: parseFloat(String(newRule.threshold)),
          type: newRule.type,
          value: parseFloat(String(newRule.value)),
          visible_on_bill: Boolean(newRule.visibleOnBill),
          outlet_id: newRule.outletId ? Number(newRule.outletId) : null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to create rule (${res.status})`);
      }

      const saved = await res.json();
      const fullRule: DiscountRule = {
        id: String(saved.id),
        label: saved.label || '',
        metric: saved.metric,
        threshold: saved.threshold,
        type: saved.type,
        value: saved.value,
        visibleOnBill: saved.visible_on_bill,
        createdAt: saved.created_at,
        outletId: saved.outlet_id ?? null,
      };

      setDiscountRules(prev => [...prev, fullRule]);
      const updated = addLogItem(logs, 'DISCOUNT_RULE_CREATED', `Added discount rule: ${fullRule.label || fullRule.id}`);
      setLogs(updated);
    } catch (err: any) {
      console.error('Failed to create discount rule:', err);
      alert(err.message || 'Could not save discount rule to database.');
    }
  };

  const handleUpdateDiscountRule = async (updatedRule: DiscountRule) => {
    const token = getStoredToken();
    if (!token) return alert('Please sign in to update discount rules.');

    try {
      const res = await fetch(`${BACKEND}/discount-rules/${updatedRule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          label: updatedRule.label || null,
          metric: updatedRule.metric,
          threshold: parseFloat(String(updatedRule.threshold)),
          type: updatedRule.type,
          value: parseFloat(String(updatedRule.value)),
          visible_on_bill: Boolean(updatedRule.visibleOnBill),
          outlet_id: updatedRule.outletId ? Number(updatedRule.outletId) : null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to update rule (${res.status})`);
      }

      const saved = await res.json();
      setDiscountRules(prev =>
        prev.map(r =>
          r.id === updatedRule.id
            ? {
                ...r,
                label: saved.label || '',
                metric: saved.metric,
                threshold: saved.threshold,
                type: saved.type,
                value: saved.value,
                visibleOnBill: saved.visible_on_bill,
                outletId: saved.outlet_id ?? null,
              }
            : r
        )
      );

      const updated = addLogItem(logs, 'DISCOUNT_RULE_UPDATED', `Updated discount rule: ${updatedRule.label || updatedRule.id}`);
      setLogs(updated);
    } catch (err: any) {
      console.error('Failed to update discount rule:', err);
      alert(err.message || 'Could not update discount rule in database.');
    }
  };

  const handleDeleteDiscountRule = async (id: string) => {
    const token = getStoredToken();
    if (!token) return;

    try {
      const res = await fetch(`${BACKEND}/discount-rules/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok && res.status !== 204 && res.status !== 404) {
        throw new Error(`Delete failed (${res.status})`);
      }

      setDiscountRules(prev => prev.filter(r => r.id !== id));
      const updated = addLogItem(logs, 'DISCOUNT_RULE_DELETED', `Deleted discount rule #${id}`);
      setLogs(updated);
    } catch (err: any) {
      console.error('Failed to delete discount rule:', err);
      alert(err.message || 'Could not delete discount rule from database.');
    }
  };


  const handleUpdateInvoice = async (billId: string, updatedElements: CanvasElement[], newAmount: number, updatedFields?: Partial<InvoiceRecord>) => {
    invalidateInvoicePdfCache(billId); // stale cached PDF must not survive an edit
    let backendSyncPromise: Promise<any> = Promise.resolve();
    const nextBills = bills.map(b => {
      if (b.id === billId) {
        // Let's copy and update text elements based on updated fields to make editing save correctly
        let elementsClone = JSON.parse(JSON.stringify(updatedElements)) as CanvasElement[];
        elementsClone = elementsClone.map(el => {
          if (el.type === 'qr') {
            const originalQr = b.elements.find((orig: any) => orig.id === el.id);
            if (originalQr?.qrValue) return { ...el, qrValue: originalQr.qrValue };
          }
          return el;
        });

        // Sync to backend DB — captured so callers can await real completion,
        // instead of firing this off unresolved (which let setSaved(true) in
        // QuickInvoiceView fire green before the PUT had even started).
        const token = getStoredToken();
        if (token && !isNaN(Number(billId))) {
          backendSyncPromise = import('./utils/api').then(({ invoicesApi }) => {
            const elementsForDB = JSON.parse(JSON.stringify(elementsClone)).map((el: any) => {
  const isQrImage = (el.id || '').startsWith('invoice-qr-') || (el.id || '').startsWith('token-qr-');
  if (el.type === 'image' && !isQrImage && el.textContent && el.textContent.length > 500) {
    return { ...el, textContent: '__image_stripped__' };
  }
  return el;
});
return invoicesApi.update(Number(billId), {
  amount: newAmount,
  elements: elementsForDB,
  line_items: (updatedFields as any)?.line_items,
  items: (updatedFields as any)?.line_items || (updatedFields as any)?.items,
  subtotal: (updatedFields as any)?.subtotal,
  tax_total: (updatedFields as any)?.tax_total,
  discount_total: (updatedFields as any)?.discount_total,
  translated_lang: (updatedFields as any)?.translated_lang || (updatedFields as any)?.translatedLang || null,
  is_bilingual: (updatedFields as any)?.isBilingual ?? (b as any).isBilingual ?? false,
  ...(updatedFields?.status && { status: updatedFields.status.toLowerCase() }),
  ...(updatedFields?.status === 'Updated' && { last_edited_at: new Date().toISOString(), edited_by: 'owner' }),
  client_name: updatedFields?.clientName ?? b.clientName,
  client_email: updatedFields?.clientEmail ?? b.clientEmail,
  client_phone: updatedFields?.clientPhone ?? (b as any).clientPhone,
  client_address: updatedFields?.clientAddress ?? (b as any).clientAddress,
  buyer_tax_id: (updatedFields as any)?.buyer_tax_id ?? (b as any).buyerTaxId ?? (b as any).buyer_tax_id,
  ...(updatedFields?.currency && { currency: updatedFields.currency }),
  ...((updatedFields as any)?.payment_mode && { payment_mode: (updatedFields as any).payment_mode }),
  qr_position: (updatedFields as any)?.qr_position ?? (b as any).qr_position,
  dynamic_values_ar: (updatedFields as any)?.dynamic_values_ar ?? {},
  dynamic_field_labels_ar: (updatedFields as any)?.dynamic_field_labels_ar ?? {},
}).catch(e => console.warn('DB sync failed:', e));
          });
        }

        if (updatedFields) {
          elementsClone = elementsClone.map(el => {
            if (el.type === 'text' && el.textContent) {
              let text = el.textContent;
              
              // 1. Replace Invoice Number in any text block
              if (updatedFields.invoiceNumber && b.invoiceNumber !== updatedFields.invoiceNumber) {
                const oldNum = b.invoiceNumber;
                const newNum = updatedFields.invoiceNumber;
                if (oldNum && text.includes(oldNum)) {
                  text = text.replaceAll(oldNum, newNum);
                } else if (text.toLowerCase().includes('invoice #:')) {
                  // Fallback regex substitution
                  text = text.replace(/Invoice #:\s*\S+/gi, `Invoice #: ${newNum}`);
                }
              }
              
              // 2. Replace Client Name in any text block
              if (updatedFields.clientName && b.clientName !== updatedFields.clientName) {
                const oldName = b.clientName;
                const newName = updatedFields.clientName;
                if (oldName && text.includes(oldName)) {
                  text = text.replaceAll(oldName, newName);
                } else if (text.toLowerCase().startsWith('bill to:')) {
                  const lines = text.split('\n');
                  if (lines.length > 1) {
                    lines[1] = newName;
                    text = lines.join('\n');
                  }
                }
              }

              // 3. Replace Client Email in any text block
              if (updatedFields.clientEmail && b.clientEmail !== updatedFields.clientEmail) {
                const oldEmail = b.clientEmail;
                const newEmail = updatedFields.clientEmail;
                if (oldEmail && text.includes(oldEmail)) {
                  text = text.replaceAll(oldEmail, newEmail);
                }
              }
              
              return { ...el, textContent: text };
            }
            return el;
          });
        }

        // Ensure table element carries the exact updated custom tax & discount lines
        const finalElements = elementsClone.map(el => {
          if (el.type === 'table') {
            return {
              ...el,
              taxes: (updatedFields as any)?.taxLines || (el as any).taxes,
              discounts: (updatedFields as any)?.discountLines || (el as any).discounts,
            };
          }
          return el;
        });

        return {
          ...b,
          elements: finalElements,
          amount: newAmount,
          driveFileId: null,
          driveFileUrl: null,
          ...updatedFields
        };
      }
      return b;
    });
    setBills(nextBills);

    // Refetch from backend to ensure UI is in sync
    // Skip refetch if we're just marking as Updated (new invoice was created separately)
    const token = getStoredToken();
    const isStatusOnlyUpdate = updatedFields && Object.keys(updatedFields).length === 1 && updatedFields.status === 'Updated';
    if (token && !isStatusOnlyUpdate) {
      setTimeout(async () => {
        try {
          const { invoicesApi } = await import('./utils/api');
          const dbInvoices = await invoicesApi.list(selectedOutletFilter ?? undefined);
          if (dbInvoices?.length) {
           const mapped = dbInvoices.map((inv: any) => ({
  id: String(inv.id),
  invoiceNumber: inv.invoice_number,
  clientName: inv.client_name || 'General Client',
  clientEmail: inv.client_email || '',
  clientPhone: inv.client_phone || '',
  clientAddress: inv.client_address || '',
  amount: inv.amount || inv.total || 0,
  status: inv.status ? inv.status.charAt(0).toUpperCase() + inv.status.slice(1) : 'Draft',
  currency: inv.currency || 'USD',
  amountPaid: inv.amount_paid || 0,
  balanceDue: inv.balance_due || 0,
  paymentDate: inv.payment_date || '',
  lastEditedAt: inv.last_edited_at || '',
  editLog: inv.edit_log || [],
  createdAt: inv.created_at,
  elements: inv.elements || [],
  pageSize: inv.page_size || 'A4',
  payment_mode: inv.payment_mode || '',
  isBilingual: inv.is_bilingual || false,
  translated_lang: inv.translated_lang || inv.translatedLang || undefined,
  translatedLang: inv.translated_lang || inv.translatedLang || undefined,
  line_items: inv.line_items || inv.items || [],
  outlet_id: inv.outlet_id ?? null,
  cancellation_reason: inv.cancellation_reason || '',
  cancelled_by: inv.cancelled_by || null,
  dynamicValuesAr: inv.dynamic_values_ar || {},
  dynamicFieldLabelsAr: inv.dynamic_field_labels_ar || {},
  row_labels_ar: inv.row_labels_ar || undefined,
  tax_lines_ar: inv.tax_lines_ar || undefined,
  discount_lines_ar: inv.discount_lines_ar || undefined,
  seller_country: inv.seller_country || undefined,
  seller_region: inv.seller_region || undefined,
  buyer_region: inv.buyer_region || undefined,
  buyerTaxId: inv.buyer_tax_id || inv.buyerTaxId || undefined,
  buyer_tax_id: inv.buyer_tax_id || inv.buyerTaxId || undefined,
  publicToken: inv.public_token || undefined,
  showAmountInWords: inv.show_amount_in_words || false,
  qr_position: inv.qr_position || inv.auto_qr_position || undefined,
}));
            setBills(mapped);
          }
        } catch (e) {
          console.warn('Refetch after update failed:', e);
        }
      }, 500);
    }
    
    // Log quick edit details to dyn ledger log
    const targetBill = bills.find(b => b.id === billId);
    const isUpdatedStatus = updatedFields?.status === 'Updated';
    const logMsg = isUpdatedStatus
      ? `Invoice ${targetBill?.invoiceNumber || billId} marked as UPDATED by ${authUser?.name || 'user'} (${authUser?.role || 'unknown'}) at ${new Date().toLocaleString()}`
      : `Quick updated service rows of invoice ${targetBill ? targetBill.invoiceNumber : billId}. New Total: $${newAmount.toFixed(2)}`;
    const logAction = isUpdatedStatus ? 'INVOICE_STATUS_CHANGED' : 'INVOICE_QUICK_EDIT';
    const updated = addLogItem(logs, logAction, logMsg);
    setLogs(updated);
    if (isUpdatedStatus) {
      postLog('INVOICE_STATUS_CHANGED', `Invoice ${targetBill?.invoiceNumber || billId} marked UPDATED by ${authUser?.name} (${authUser?.role}) — ${new Date().toLocaleString()}`);
    }

    await backendSyncPromise; // don't resolve until the actual PUT has finished
  };

 const handleEditInvoice = (bill: InvoiceRecord) => {
  setQuickInvoiceSelectedTemplate('');
  setCurrentTab('bills');
};

const handleReuseInvoiceLayout = (bill: InvoiceRecord) => {
    const elementsClone = JSON.parse(JSON.stringify(bill.elements));
    setElements(elementsClone);

    const targetPageSize = bill.pageSize || 'A4';
    setPageSize(targetPageSize);

    setSelectedElementId(null);
    setInlineEditingId(null);

    saveToHistory(elementsClone, targetPageSize);

    const updated = addLogItem(
      logs,
      'INVOICE_REUSED',
      `Loaded saved invoice layout (${bill.invoiceNumber}) into core Creator workspace.`
    );
    setLogs(updated);

    setCurrentTab('generator');
    alert(`Success!\nLoaded saved layout of invoice ${bill.invoiceNumber} into creator.\nYou can now edit and save. This will preserve the old invoice and write a NEW record upon saving!`);
  };

  const handleBackupAccountsToDrive = async (accountsList: ClientAccount[]) => {
    if (!googleToken) {
      alert("Please log in with Google on the Home Tab to authenticate Drive backup capability.");
      return;
    }
    try {
      // Create spreadsheet representation
      const data = accountsList.map((a, i) => ({
        "No": i + 1,
        "Client ID": a.id,
        "Contact Person": a.name,
        "Corporate Body": a.company || 'Private Contractor',
        "Email Coordinates": a.email,
        "Phone Connection": a.phone || 'N/A',
        "Registered Mailing Address": a.address || 'N/A',
        "Registration Date": new Date(a.createdAt.endsWith('Z') || a.createdAt.includes('+') ? a.createdAt : a.createdAt + 'Z').toLocaleDateString()
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Client Registry");

      // Set beautiful sizing properties
      const maxProps = [
        { wch: 10 },
        { wch: 22 },
        { wch: 25 },
        { wch: 25 },
        { wch: 25 },
        { wch: 18 },
        { wch: 45 },
        { wch: 15 }
      ];
      ws['!cols'] = maxProps;

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Fetch Drive App folder id
      const folderId = await getOrCreateFolder(googleToken);

      // Upload file to directory
      const filename = `Institutional_Accounts_Backup_${Date.now().toString().slice(-6)}.xlsx`;
      const result = await uploadExcelToDrive(googleToken, filename, excelBlob, folderId);

      const updated = addLogItem(
        logs,
        'ACCOUNTS_BACKUP',
        `Backed up institutional client matrix directory (${accountsList.length} entities) to cloud storage. Filename: ${result.name}`
      );
      setLogs(updated);

      alert(`Database snapshot synchronized to Google Drive! Saved as: ${result.name}`);
    } catch (err: any) {
      console.error(err);
      alert(`Cloud upload sync failed: ${err.message || err}`);
    }
  };

  // Load client into Canvas workspace text replacements
  const handleUseAccountInCreator = (act: ClientAccount) => {
    const CLIENT_BLOCK_PATTERNS = [
      'bill to:', 'client:', 'transferred to/bill to:', 'transferred to:',
      'from agent representative:', 'sender realty:', 'contractor registry:',
      'sender entity:', 'from:', 'sender:'
    ];

    const updatedElements = elements.map(el => {
      if (el.type === 'text' && el.textContent) {
        let txt = el.textContent;
        txt = txt.replace(/\[Client Name\]|\[CLIENT_NAME\]/gi, act.name);
        txt = txt.replace(/\[Client Company\]|\[CLIENT_COMPANY\]/gi, act.company || '');
        txt = txt.replace(/\[Client Email\]|\[CLIENT_EMAIL\]/gi, act.email || '');
        txt = txt.replace(/\[Client Phone\]|\[CLIENT_PHONE\]/gi, act.phone || '');
        txt = txt.replace(/\[Client Address\]|\[CLIENT_ADDRESS\]/gi, act.address || '');

        const lowerTxt = txt.toLowerCase();
        const matchedLabel = CLIENT_BLOCK_PATTERNS.find(p => lowerTxt.includes(p));

        if (matchedLabel) {
          // Preserve the original label line (e.g. "TRANSFERRED TO/BILL TO:")
          const originalFirstLine = txt.split('\n')[0];
          txt = `${originalFirstLine}\n${act.name}\n${act.company ? act.company + '\n' : ''}${act.phone ? act.phone + '\n' : ''}${act.address || ''}`;
        }
        return { ...el, textContent: txt };
      }
      return el;
    });
    setElements(updatedElements);
    saveToHistory(updatedElements, pageSize);
    const updated = addLogItem(logs, 'INVOICE_CLIENT_INJECTED', `Injected client contact ${act.name} details into active invoice canvas`);
    setLogs(updated);
    alert(`Successfully populated canvas templates with ${act.name}'s profile details!`);
    setCurrentTab('generator');
  };


  const handleSaveAsTemplate = async (mobileTemplateName?: string) => {
  const name = mobileTemplateName || prompt('Template name:');
  if (!name?.trim()) return;
  const newTemplate: ActiveTemplate = {
    id: `tpl-${Date.now()}`,
    name: name.trim(),
    elements: JSON.parse(JSON.stringify(elements)),
    pageSize,
    category: 'Custom',
    description: 'User saved template',
    preview: '',
  };

  setCustomTemplates(prev => {
    const next = [newTemplate, ...prev];
    saveData('cloudairambo_custom_templates', next);
    return next;
  });

  try {
    const token = getStoredToken();
    await fetch(`${BACKEND}/invoice-templates/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        name: newTemplate.name,
        business_type: newTemplate.category,
        page_size: newTemplate.pageSize,
        elements: newTemplate.elements,
      }),
    });
  
  } catch (e: any) {
  console.warn('Template backend sync failed:', e);

  if (
    isInvoiceCanvasEmbed &&
    (window as any).ReactNativeWebView?.postMessage
  ) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        action: 'template-save-error',
        title: 'Template Save Failed',
        message:
          e?.message ||
          'The template could not be synced. Please try again.',
      }),
    );
  }
}

  isResettingRef.current = true;
  localStorage.removeItem('cloudairambo_canvas_draft');
  const defaultSize = (localStorage.getItem('cloudairambo_default_page_size') as PageSizePreset) || 'A4';
  const freshDefault = JSON.parse(JSON.stringify(getFreshPresets()[0].elements));
  setElements(freshDefault);
  setPageSize(defaultSize);
  setSelectedElementId(null);
  setInlineEditingId(null);
  const serialized = JSON.stringify({ elements: freshDefault, pageSize: defaultSize });
  setHistory([serialized]);
  setHistoryPointer(0);
  setTimeout(() => {
    isResettingRef.current = false;
    localStorage.setItem('cloudairambo_canvas_draft', JSON.stringify({ elements: freshDefault, pageSize: defaultSize }));
  }, 100);

  if (
  isInvoiceCanvasEmbed &&
  (window as any).ReactNativeWebView?.postMessage
) {
  (window as any).ReactNativeWebView.postMessage(
    JSON.stringify({
      action: 'template-save-success',
      templateName: name.trim(),
      message: `Template "${name.trim()}" saved successfully.`,
    }),
  );
} else {
  alert(`Template "${name.trim()}" saved!`);
}
};

  // Convert current Canvas workspace design into a Ledger Bill claim
 const handleSaveActiveWorkspaceAsBill = async () => {
  if (!requireAtLeastOneOutlet()) return;
  let currentUser = authUser;

  // Mobile Invoice Creator may already have a valid JWT while
  // React authUser state has not finished hydrating yet.
  if (!currentUser) {
    const token = getStoredToken();

    if (token) {
      try {
      

        const res = await fetch(`${BACKEND}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          currentUser = await res.json();

          

          setAuthUser(currentUser);
          setAuthChecked(true);
          setShowLanding(false);
        } else {
          console.error(
            '[Invoice Save] /auth/me failed:',
            res.status,
          );
        }
      } catch (error) {
        console.error(
          '[Invoice Save] failed to recover authentication:',
          error,
        );
      }
    }
  }

  if (!currentUser) {
    alert('Please sign in to save invoices.');

    // Don't open the website login modal inside the mobile canvas embed.
    if (!isInvoiceCanvasEmbed) {
      setShowAuthModal(true);
    }

    return;
  }
  // If editing existing invoice, update it instead of creating new
// ✅ FIXED
// ✅ FIXED
// ✅ FIXED editingBillId block
if (editingBillId) {
  const tableEl = elements.find(el => el.type === 'table');
  const computedTotal = tableEl ? computeTableTotals(tableEl).total : 0;

  // Generate
  
  const originalBill = bills.find(b => b.id === editingBillId);

handleUpdateInvoice(editingBillId, elements, computedTotal, {
  invoiceNumber: originalBill?.invoiceNumber,
  clientName: originalBill?.clientName,
  clientEmail: originalBill?.clientEmail,
});

  // Sync to backend DB
  const token = getStoredToken();
  if (token && !isNaN(Number(editingBillId))) {
    try {
      const { invoicesApi } = await import('./utils/api');
      await invoicesApi.update(Number(editingBillId), {
  elements: JSON.parse(JSON.stringify(elements)),
  amount: computedTotal,
});
    } catch (e) {
      console.warn('DB update sync failed:', e);
    }
  }

  const updated = addLogItem(logs, 'INVOICE_UPDATED', `Updated invoice ${originalBill?.invoiceNumber}`);
setLogs(updated);
setEditingBillId(null);
alert(`Invoice updated!`);
 setCurrentTab('bills');
return;
}

const companyProfile = loadCompanyProfile();
  const tableEl = elements.find(el => el.type === 'table');
  const computedTotal = tableEl ? computeTableTotals(tableEl).total : 0;

  let detectedClient = 'General Client';
  let detectedEmail = '';

  elements.forEach(el => {
    if (el.type === 'text' && el.textContent) {
      const val = el.textContent;
      if (val.includes('@') && !val.includes('[') && val.length < 50) {
        detectedEmail = val.trim();
      }
      if (val.toLowerCase().startsWith('bill to:') || val.toLowerCase().startsWith('client:')) {
        const lines = val.split('\n');
        if (lines.length > 1) detectedClient = lines[1].trim();
      }
    }
  });

  try {
    const { invoicesApi } = await import('./utils/api');

// Auto-increment all serial elements on canvas

// Strip the template's own baked-in QR + static "Scan to view invoice"
// caption BEFORE this first save — the backend attaches its own secure,
// token-based QR (_auto: 'invoice' / 'invoice_label') right after create.
// Leaving the template's QR/caption in here means the create() call below
// persists them alongside the backend's real one, producing two
// "Scan to view invoice" blocks on the saved invoice — same bug already
// fixed in QuickInvoiceView.handleGenerate and POSView.handleSave.
const elementsForCreate = elements.filter((el: any) =>
  el.type !== 'qr' &&
  !(el.type === 'text' && (el.textContent === 'Scan to view invoice' || el.textContent === 'Scan to track your order'))
);

const saved = await invoicesApi.create({
  client_name: detectedClient,
  client_email: detectedEmail,
  amount: computedTotal > 0 ? computedTotal : 0,
  currency: effectiveCurrency,
  elements: JSON.parse(JSON.stringify(elementsForCreate)),
  page_size: pageSize,
  line_items: [],
  // Was never sent — getInvoicePdfBlob checks invoice.isBilingual to decide
  // between BilingualInvoiceRenderer (which live-patches the invoice number)
  // and the raw handleExportPDF path (which does not). Without this flag,
  // every bilingual bill saved from this screen silently fell back to the
  // renderer that never updates the invoice number at all.
  is_bilingual: bilingualMode,
});

    // Sync the backend-generated invoice number into canvas text elements
const syncedElements = JSON.parse(JSON.stringify(elements)) as CanvasElement[];
const finalInvoiceNumber = saved.invoice_number;

syncedElements.forEach(el => {
  if (el.type === 'qr') {
    el.qrValue = `${APP_URL}/invoice/view/${saved.public_token}`;
  }
  // Was never patching the real invoice number into text content at all —
  // every text element (English AND Arabic sides) kept whatever static
  // sample number ("INV-2026-904") the template shipped with, forever.
  // Mirrors the patch pattern already used in QuickInvoiceView.handleGenerate
  // and POSView.handleSave.
  if (el.type === 'text' && el.textContent) {
    let t = el.textContent as string;
    t = t.replace(/\{invoice_number\}/g, finalInvoiceNumber);
    t = t.replace(/\bINV-[\w\d]{3,}\b/g, finalInvoiceNumber);
    t = t.replace(/\bBill\s*No\.?\s*:\s*[\w\-\[\]\s#]+/gi, `Bill No: ${finalInvoiceNumber}`);
    t = t.replace(/\bReceipt\s*No\.?\s*:\s*[\w\-\[\]\s#]+/gi, `Receipt No: ${finalInvoiceNumber}`);
    t = t.replace(/\bInvoice\s*#\s*:\s*[\w\-\[\] #]+/gi, `Invoice #: ${finalInvoiceNumber}`);
    t = t.replace(/\bPENDING\b/g, finalInvoiceNumber);
    el.textContent = t;
    if ((el as any).arabicContent) {
      (el as any).arabicContent = ((el as any).arabicContent as string).replace(/\{invoice_number\}/g, finalInvoiceNumber);
    }
  }
});

const newBill: InvoiceRecord = {
  id: String(saved.id),
  invoiceNumber: saved.invoice_number,
  clientName: saved.client_name || detectedClient,
  clientEmail: saved.client_email || detectedEmail,
  amount: saved.amount || computedTotal,
  status: saved.status || 'draft',
  createdAt: saved.created_at,
  elements: syncedElements,
  pageSize: pageSize,
  currency: saved.currency || effectiveCurrency,
  ...(saved.public_token ? { publicToken: saved.public_token } : {}),
};

// Also update the live canvas so the user sees the number immediately
setElements(syncedElements);

setBills(prev => [newBill, ...prev]);

// The invoice-number patch above only exists in local React state so far —
// invoicesApi.create() was called BEFORE finalInvoiceNumber existed, so the
// backend still has the raw template's placeholder text baked into its
// stored elements. setCurrentTab('bills') below triggers a bills refetch
// effect that overwrites local state with the (still-unpatched) DB copy,
// silently reverting this entire fix. PUT the patched elements back before
// that happens.
try {
  await invoicesApi.update(Number(saved.id), {
    elements: JSON.parse(JSON.stringify(syncedElements)),
  });
} catch (e) {
  console.warn('Failed to persist patched invoice number to backend:', e);
}
// Auto-save canvas layout as Quick Invoice template
const defaultTemplateName = `Layout ${Date.now().toString().slice(-4)}`;

const autoTemplateName = isInvoiceCanvasEmbed
  ? defaultTemplateName
  : prompt(
      'Save as Quick Invoice template — enter a name:',
      defaultTemplateName
    ) || defaultTemplateName;
const templateElements = JSON.parse(JSON.stringify(elements)).map((el: any) => {
  if (el.type === 'text' && el.textContent) {
    el.textContent = el.textContent
      .replace(/INV-\d{4,}/g, 'INV-XXXXXX')
      .replace(/Invoice\s*#\s*:\s*[\w-]+/gi, 'Invoice #: PENDING')
      .replace(/PO\s*#\s*:\s*[\w-]+/gi, 'PO #: PENDING');
  }
  return el;
});
const autoTemplate: ActiveTemplate = {
  id: `tpl-auto-${Date.now()}`,
  name: autoTemplateName,
  elements: templateElements,
  pageSize,
  category: 'Custom',
  description: 'Auto-saved from Invoice Creator',
  preview: '',
};
setCustomTemplates(prev => {
  const next = [autoTemplate, ...prev];
  saveData('cloudairambo_custom_templates', next);
  return next;
});
try {
  const token = getStoredToken();
  await fetch(`${BACKEND}/invoice-templates/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      name: autoTemplate.name,
      business_type: autoTemplate.category,
      page_size: autoTemplate.pageSize,
      elements: autoTemplate.elements,
    }),
  });
} catch (e) {
  console.warn('Auto-template backend sync failed:', e);
}
setQuickInvoiceSelectedTemplate(autoTemplate.id);
    const updated = addLogItem(logs, 'INVOICE_SAVED', `Saved invoice ${saved.invoice_number} to database`);
    setLogs(updated);
await postLog('INVOICE_SAVED', `Invoice ${saved.invoice_number} saved for client ${detectedClient} by ${currentUser?.name || 'User'}`);
if (
  isInvoiceCanvasEmbed &&
  (window as any).ReactNativeWebView?.postMessage
) {
  (window as any).ReactNativeWebView.postMessage(
    JSON.stringify({
      action: 'invoice-save-success',
      invoiceId: saved.id,
      invoiceNumber: saved.invoice_number,
      message: `Invoice ${saved.invoice_number} saved successfully.`,
    }),
  );
} else {
  setCurrentTab('bills');
}

  } catch (err: any) {
  console.error('[Invoice Save] failed:', err);

  if (
    isInvoiceCanvasEmbed &&
    (window as any).ReactNativeWebView?.postMessage
  ) {
    (window as any).ReactNativeWebView.postMessage(
      JSON.stringify({
        action: 'invoice-save-error',
        title: 'Unable to save invoice',
        message: 'The invoice could not be saved. Please try again.',
      }),
    );
  } else {
    alert('Unable to save invoice. Please try again.');
  }
}
};

 
     

  const handleDownloadInvoicePDF = async (bill: InvoiceRecord) => {
    // Route through the same renderer WhatsApp/Print already use
    // (getInvoicePdfBlob → renderPOSReceiptToPdf for thermal receipts,
    // renderBilingualInvoiceToPdf for bilingual, handleExportPDF otherwise)
    // instead of calling handleExportPDF directly. That raw-table export
    // path renders totals differently from POSReceiptRenderer, which is
    // why Download never matched what WhatsApp/Print produced.
    const blob = await getInvoicePdfBlob(bill);
    if (!blob) {
      alert('Could not prepare the PDF for download.');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${bill.invoiceNumber}_${bill.clientName.replace(/\s+/g, '_')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrintInvoicePDF = async (bill: InvoiceRecord) => {
    try {
      const useUSB = localStorage.getItem('cloudairambo_qi_usb_print') === 'true';
      const useBT = localStorage.getItem('cloudairambo_qi_bt_print') === 'true';
      const isThermalBill = (bill.pageSize || '').startsWith('THERMAL');

      if (isThermalBill && (useUSB || useBT)) {
        const printJob = await buildQuickInvoicePrintJob(bill);
        console.log('[print] invoiceQR =', printJob.invoiceQR, '| cols =', printJob.tableColumns?.length);
        if (useBT) {
          const { printReceiptBT } = await import('./utils/bluetoothPrinter');
          const btPrinted = await printReceiptBT(printJob);
          if (btPrinted) return;
        }
        if (useUSB) {
          const usbPrinted = await printReceiptUSB(printJob);
          if (usbPrinted) return;
        }
        // fall through to browser print if the connected printer failed
      }

      const blob = await getInvoicePdfBlob(bill);
      if (!blob) {
        alert('Could not prepare the PDF for printing.');
        return;
      }
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      iframe.src = url;
      document.body.appendChild(iframe);

      let printFired = false;
      const firePrint = () => {
        if (printFired) return;
        printFired = true;
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };

      iframe.onload = () => setTimeout(firePrint, 300);
      setTimeout(firePrint, 1200);

      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        URL.revokeObjectURL(url);
      }, 30000);
    } catch (err) {
      console.error('[Invoice Print] failed:', err);
      alert('Could not prepare the PDF for printing.');
    }
  };

  const handleExportMergedPDF = async (selectedRecords: InvoiceRecord[]) => {
    if (selectedRecords.length === 0) return;
    try {
     const { jsPDF } = await import('jspdf');

const currencySymbol = getCurrencyDisplay(activeCurrency, currencyDisplayMode);
const needsUnicodeFont = !isAsciiSafeSymbol(currencySymbol);

const amiriFontUrl = 'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/amiri/Amiri-Regular.ttf';
let arabicFontBase64 = '';
try {
  const fontRes = await fetch(amiriFontUrl, { signal: AbortSignal.timeout(3000) });
  const fontBuffer = await fontRes.arrayBuffer();
  const fontBytes = new Uint8Array(fontBuffer);
  let binary = '';
  fontBytes.forEach(b => binary += String.fromCharCode(b));
  arabicFontBase64 = btoa(binary);
} catch(e) { console.warn('Arabic font load failed:', e); }

let unicodeFontBase64 = '';
if (needsUnicodeFont) {
  try {
    const notoUrl = 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf';
    const fontRes = await fetch(notoUrl, { signal: AbortSignal.timeout(5000) });
    const fontBuffer = await fontRes.arrayBuffer();
    const fontBytes = new Uint8Array(fontBuffer);
    let binary = '';
    fontBytes.forEach(b => binary += String.fromCharCode(b));
    unicodeFontBase64 = btoa(binary);
  } catch(e) { console.warn('Noto Sans load failed:', e); }
}

const setDocFont = (style: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal') => {
  if (unicodeFontBase64) {
    doc.setFont('NotoSans', (style === 'bold' || style === 'bolditalic') ? 'bold' : 'normal');
  } else {
    doc.setFont('helvetica', style);
  }
};

const pdfCurrencySymbol = unicodeFontBase64
  ? currencySymbol.trim()
  : (isAsciiSafeSymbol(currencySymbol)
    ? currencySymbol
    : getCurrencyFallbackName(activeCurrency)).trim();
const mergedPdfLabels: Record<string, {subtotal:string;total:string;tax:string;discount:string;shipping:string}> = {
  ar:{subtotal:'المجموع الفرعي',total:'الإجمالي الكلي',tax:'ضريبة',discount:'خصم',shipping:'الشحن'},
  ko:{subtotal:'소계',total:'합계',tax:'세금',discount:'할인',shipping:'배송비'},
  hi:{subtotal:'उप-कुल',total:'कुल',tax:'कर',discount:'छूट',shipping:'शिपिंग'},
  fr:{subtotal:'Sous-total',total:'Total',tax:'Taxe',discount:'Remise',shipping:'Livraison'},
  de:{subtotal:'Zwischensumme',total:'Gesamt',tax:'Steuer',discount:'Rabatt',shipping:'Versand'},
  es:{subtotal:'Subtotal',total:'Total',tax:'Impuesto',discount:'Descuento',shipping:'Envío'},
  ja:{subtotal:'小計',total:'合計',tax:'税',discount:'割引',shipping:'配送'},
  zh:{subtotal:'小计',total:'总计',tax:'税',discount:'折扣',shipping:'运费'},
  ur:{subtotal:'ذیلی کل',total:'کل',tax:'ٹیکس',discount:'رعایت',shipping:'شپنگ'},
  tr:{subtotal:'Ara Toplam',total:'Toplam',tax:'Vergi',discount:'İndirim',shipping:'Kargo'},
  ru:{subtotal:'Промежуточный итог',total:'Итого',tax:'Налог',discount:'Скидка',shipping:'Доставка'},
};

const isRtlTarget = translateTargetLang === 'ar' || translateTargetLang === 'ur';

const setMergedTranslatedFont = () => {
  if (isRtlTarget && arabicFontBase64) {
    try {
      doc.addFileToVFS('Amiri-normal.ttf', arabicFontBase64);
      doc.addFont('Amiri-normal.ttf', 'Amiri', 'normal');
    } catch (e) {}
    doc.setFont('Amiri', 'normal');
  } else if (!isRtlTarget && unicodeFontBase64) {
    try {
      doc.addFileToVFS('NotoSans-Regular.ttf', unicodeFontBase64);
      doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
    } catch (e) {}
    doc.setFont('NotoSans', 'normal');
  } else {
    doc.setFont('helvetica', 'normal');
  }
};


const targetPageSize = pageSize;
const pageConfig = PAGE_PRESETS[targetPageSize];
      
      let doc: any = null;
      let isFirstDocPage = true;

      for (const record of selectedRecords) {
        const targetElements = record.elements;
        let finalHeight = pageBehavior === 'auto_expand' ? computedHeight : pageConfig.height;
        if (pageBehavior === 'auto_expand') {
          const maxBottom = targetElements.reduce((max, el) => Math.max(max, el.y + el.height), 0);
          finalHeight = Math.max(pageConfig.height, Math.ceil(maxBottom + 60));
        }

        // Group elements by page index if multi-page mode is enabled
        const sortedElements = [...targetElements].sort((a, b) => a.zIndex - b.zIndex);
        const elementsByPage: Record<number, CanvasElement[]> = {};

        if (pageBehavior === 'multi_page') {
          sortedElements.forEach(el => {
            const pageIndex = Math.floor(el.y / pageConfig.height);
            if (!elementsByPage[pageIndex]) {
              elementsByPage[pageIndex] = [];
            }
            elementsByPage[pageIndex].push(el);
          });
        } else {
          elementsByPage[0] = sortedElements;
        }

        const pageKeys = Object.keys(elementsByPage).map(Number).sort((a, b) => a - b);
        const activePages = pageKeys.length > 0 ? pageKeys : [0];
        const maxPageNum = Math.max(0, ...activePages);

        if (!doc) {
          doc = new jsPDF({
            orientation: pageConfig.width > finalHeight ? 'landscape' : 'portrait',
            unit: 'px',
            format: [pageConfig.width, pageBehavior === 'auto_expand' ? finalHeight : pageConfig.height]
          });
        }

        for (let pIdx = 0; pIdx <= maxPageNum; pIdx++) {
          if (!isFirstDocPage) {
            doc.addPage([pageConfig.width, pageBehavior === 'auto_expand' ? finalHeight : pageConfig.height]);
          }
          isFirstDocPage = false;

          const pageElements = elementsByPage[pIdx] || [];

          for (let i = 0; i < pageElements.length; i++) {
            const el = pageElements[i];
            const opacityRatio = (el.opacity ?? 100) / 100;

            try {
              doc.saveGraphicsState();
              // @ts-ignore
              doc.setGState(new doc.GState({ opacity: opacityRatio }));
            } catch (e) {}

            const x = el.x;
            const y = pageBehavior === 'multi_page' ? (el.y % pageConfig.height) : el.y;
            const w = el.width;
            const h = el.height;

            if (el.type === 'shape') {
              const sType = el.shapeType || 'rectangle';
              const bg = el.backgroundColor || 'transparent';
              const bColor = el.borderColor || '#000000';
              const bW = el.borderWidth || 0;
              const hasBg = el.backgroundColor && el.backgroundColor !== 'transparent';
              const hasBorder = bW > 0 && el.borderColor && el.borderColor !== 'transparent';

              if (sType === 'circle') {
                const rx = w / 2;
                const ry = h / 2;
                const cx = x + rx;
                const cy = y + ry;
                if (hasBg) {
                  doc.setFillColor(bg);
                  doc.ellipse(cx, cy, rx, ry, 'F');
                }
                if (hasBorder) {
                  doc.setDrawColor(bColor);
                  doc.setLineWidth(bW);
                  doc.ellipse(cx, cy, rx, ry, 'D');
                }
              } else if (sType === 'triangle') {
                const pts = [
                  { x: x + w / 2, y: y },
                  { x: x + w, y: y + h },
                  { x: x, y: y + h }
                ];
                if (hasBg) {
                  doc.setFillColor(bg);
                  doc.triangle(pts[0].x, pts[0].y, pts[1].x, pts[1].y, pts[2].x, pts[2].y, 'F');
                }
                if (hasBorder) {
                  doc.setDrawColor(bColor);
                  doc.setLineWidth(bW);
                  doc.triangle(pts[0].x, pts[0].y, pts[1].x, pts[1].y, pts[2].x, pts[2].y, 'D');
                }
              } else if (sType === 'line') {
                doc.setDrawColor(bColor || bg);
                doc.setLineWidth(Math.max(1, bW));
                doc.line(x, y + h / 2, x + w, y + h / 2);
              } else if (sType === 'star') {
                const peaks = [
                  { x: 0.5, y: 0.02 }, { x: 0.64, y: 0.36 }, { x: 1.0, y: 0.36 },
                  { x: 0.71, y: 0.57 }, { x: 0.81, y: 0.91 }, { x: 0.5, y: 0.70 },
                  { x: 0.19, y: 0.91 }, { x: 0.29, y: 0.57 }, { x: 0.0, y: 0.36 },
                  { x: 0.36, y: 0.36 }
                ];
                const mapped = peaks.map(p => ({ x: x + p.x * w, y: y + p.y * h }));
                if (hasBg) {
                  doc.setFillColor(bg);
                  // @ts-ignore
                  doc.polygon(mapped, 'F');
                }
                if (hasBorder) {
                  doc.setDrawColor(bColor);
                  doc.setLineWidth(bW);
                  // @ts-ignore
                  doc.polygon(mapped, 'D');
                }
              } else if (sType === 'arrow') {
                const arrowLines = [
                  { x: 0.05, y: 0.40 }, { x: 0.70, y: 0.40 }, { x: 0.70, y: 0.15 }, { x: 0.95, y: 0.50 },
                  { x: 0.70, y: 0.85 }, { x: 0.70, y: 0.60 }, { x: 0.05, y: 0.60 }
                ];
                const mapped = arrowLines.map(p => ({ x: x + p.x * w, y: y + p.y * h }));
                if (hasBg) {
                  doc.setFillColor(bg);
                  // @ts-ignore
                  doc.polygon(mapped, 'F');
                }
                if (hasBorder) {
                  doc.setDrawColor(bColor);
                  doc.setLineWidth(bW);
                  // @ts-ignore
                  doc.polygon(mapped, 'D');
                }
              } else {
                if (hasBg) {
                  doc.setFillColor(bg);
                  if (el.borderRadius && el.borderRadius > 0) {
                    doc.roundedRect(x, y, w, h, el.borderRadius, el.borderRadius, 'F');
                  } else {
                    doc.rect(x, y, w, h, 'F');
                  }
                }
                if (hasBorder) {
                  doc.setDrawColor(bColor);
                  doc.setLineWidth(bW);
                  if (el.borderRadius && el.borderRadius > 0) {
                    doc.roundedRect(x, y, w, h, el.borderRadius, el.borderRadius, 'D');
                  } else {
                    doc.rect(x, y, w, h, 'D');
                  }
                }
              }
            } 
            else if (el.type === 'drawing' && el.drawingPoints) {
              const points = el.drawingPoints;
              const strokeColor = el.color || '#4f46e5';
              const strokeW = el.borderWidth || 3;
              
              doc.setDrawColor(strokeColor);
              doc.setLineWidth(strokeW);
              doc.setLineCap('round');
              doc.setLineJoin('round');

              for (let sIdx = 0; sIdx < points.length - 1; sIdx++) {
                const pt1 = points[sIdx];
                const pt2 = points[sIdx + 1];
                doc.line(
                  x + pt1.x,
                  y + pt1.y,
                  x + pt2.x,
                  y + pt2.y
                );
              }
            } 
            else if (el.type === 'text') {
              doc.setFontSize(el.fontSize || 12);
              const font = (el.fontFamily === 'JetBrains Mono' || el.fontFamily === 'Space Grotesk' || el.fontFamily === 'Courier New') ? 'courier' : 'helvetica';
              const style = el.fontWeight === 'bold' ? (el.fontStyle === 'italic' ? 'bolditalic' : 'bold') : (el.fontStyle === 'italic' ? 'italic' : 'normal');
              doc.setFont(font, style);
              
              if (el.color) doc.setTextColor(el.color);

              const rawText = el.textContent || '';
              const lines = doc.splitTextToSize(rawText, w);
              
              const align = el.textAlign || 'left';
              const lineH = (el.fontSize || 12) * (el.lineHeight || 1.3);

              const halfW = bilingualMode && el.arabicContent ? w / 2 : w;
const engLines = doc.splitTextToSize(rawText, halfW);
engLines.forEach((line: string, index: number) => {
  let writeX = x;
  if (align === 'right') writeX = x + halfW;
  if (align === 'center') writeX = x + (halfW / 2);
  doc.text(line, writeX, y + (el.fontSize || 12) * 0.85 + (index * lineH), { align });;
});
        

              // Arabic text below - only if bilingual mode is on
              if (bilingualMode && el.arabicContent) {
                const arabicFontSize = Math.round((el.fontSize || 12) * 0.85);
                doc.setFontSize(arabicFontSize);
                doc.setFont('helvetica', 'normal');
                if (el.color) doc.setTextColor(el.color);

                const halfW = w / 2;
const arabicLines = doc.splitTextToSize(el.arabicContent, halfW);
const arabicStartY = y + (el.fontSize || 12);

doc.setTextColor(el.color || '#000000');

arabicLines.forEach((line: string, index: number) => {
  doc.text(line, x + w, arabicStartY + (index * arabicFontSize * 1.7), { align: 'right' });
});
              }
            }
            else if (el.type === 'stamp') {
              const stampText = el.stampText || "APPROVED";
              const colorHex = el.color || "#dc2626";
              doc.setDrawColor(colorHex);
              doc.setTextColor(colorHex);
              doc.setFont("helvetica", "bold");
              
              const baseSize = el.fontSize || 18;
              doc.setFontSize(baseSize);

              doc.setLineWidth(3);
              if (el.stampShape === 'circle' || el.stampShape === 'oval') {
                const cx = x + (w / 2);
                const cy = y + (h / 2);
                const rx = w / 2;
                const ry = h / 2;
                doc.ellipse(cx, cy, rx, ry, 'D');
                doc.setLineWidth(1);
                doc.ellipse(cx, cy, rx - 4, ry - 4, 'D');
              } else if (el.stampShape === 'pill') {
                doc.roundedRect(x, y, w, h, h / 2, h / 2, 'D');
                doc.setLineWidth(1);
                doc.roundedRect(x + 4, y + 4, w - 8, h - 8, (h - 8) / 2, (h - 8) / 2, 'D');
              } else if (el.stampShape === 'badge') {
                doc.roundedRect(x, y, w, h, 8, 8, 'D');
                doc.setLineWidth(1);
                doc.roundedRect(x + 3, y + 3, w - 6, h - 6, 6, 6, 'D');
              } else {
                doc.rect(x, y, w, h, 'D');
                doc.setLineWidth(1);
                doc.rect(x + 4, y + 4, w - 8, h - 8, 'D');
              }

              doc.text(stampText.toUpperCase(), x + (w / 2), y + (h / 2) + (baseSize / 3.5), {
                align: 'center'
              });
            }
            else if (el.type === 'serial') {
              doc.setFontSize(el.fontSize || 12);
              
              let font = 'courier';
              let style = 'bold';
              let textColor = el.color || '#ea580c';
              let useBg = false;

              if (el.serialStyle === 'classic_lcd') {
                font = 'courier';
                style = 'bold';
                textColor = '#22c55e';
                useBg = true;
              } else if (el.serialStyle === 'handwritten') {
                font = 'times';
                style = 'italic';
              } else if (el.serialStyle === 'badge_pill') {
                font = 'helvetica';
                style = 'bold';
              }

              if (useBg) {
                doc.setFillColor('#0f172a');
                doc.rect(x, y, w, h, 'F');
              }

              doc.setTextColor(textColor);
              doc.setFont(font, style);
              
              const formatted = formatSerialNumber(el.serialSettings);
              doc.text(formatted, x + 8, y + (h / 2) + 3);
            }
            else if (el.type === 'signature') {
              doc.setFontSize(el.fontSize || 22);
              doc.setFont("times", "italic");
              if (el.color) doc.setTextColor(el.color);
              doc.text(el.signatureName || "Johnathan Doe", x + 10, y + h - 22);

              doc.setDrawColor('#94a3b8');
              doc.setLineWidth(0.5);
              doc.line(x, y + h - 14, x + w, y + h - 14);

              doc.setFontSize(8);
              doc.setFont("helvetica", "normal");
              doc.setTextColor('#475569');
              doc.text(el.textContent || "Authorized Signature", x + (w / 2), y + h - 2, { align: 'center' });
            }
            else if (el.type === 'image' && el.textContent) {
              try {
                doc.addImage(el.textContent, 'PNG', x, y, w, h);
              } catch (e) {
                doc.setFontSize(10);
                doc.setTextColor('#ef4444');
                doc.text("[Image Load Error]", x + 10, y + 20);
              }
            }
            else if (el.type === 'qr' && el.qrValue) {
  try {
    const qrDataUrl = await QRCode.toDataURL(el.qrValue, {
      errorCorrectionLevel: 'L',
      width: 300,
      margin: 2,
    });
    doc.addImage(qrDataUrl, 'PNG', x, y, w, h);
  } catch(e) {
    console.error('QR PDF error:', e);
  }
}
           
            else if (el.type === 'table' && el.columns && el.rows) {
              const columns = el.columns;
              const rows = el.rows;
              const rowH = 22;
              const tablePrimaryColor = el.color || "#4f46e5";

              doc.setFillColor(tablePrimaryColor);
              doc.rect(x, y, w, rowH, 'F');

              if (tablePrimaryColor.toLowerCase() === '#ffffff') {
                doc.setDrawColor('#cbd5e1');
                doc.setLineWidth(0.5);
                doc.rect(x, y, w, rowH, 'D');
              }

              doc.setFont('helvetica', 'bold');
              doc.setFontSize(el.fontSize || 11);
              doc.setTextColor(tablePrimaryColor.toLowerCase() === '#ffffff' ? '#1e293b' : '#ffffff');

              let accumLeftPct = 0;
              columns.forEach(col => {
                const colW = w * (col.width / 100);
                const textX = x + accumLeftPct + (col.align === 'right' ? colW - 6 : col.align === 'center' ? colW / 2 : 6);
                doc.text(col.header, textX, y + rowH * 0.62, { align: col.align });

                if (bilingualMode && col.arabicHeader) {
                  doc.setFontSize((el.fontSize || 11) * 0.75);
                  doc.text(col.arabicHeader, x + accumLeftPct + colW - 6, y + 18, { align: 'right' });
                  doc.setFontSize(el.fontSize || 11);
                }

                accumLeftPct += colW;
              });

              let currentY = y + rowH;
              doc.setFont('helvetica', 'normal');
              
              rows.forEach((row, rIdx) => {
                if (rIdx % 2 === 1) {
                  doc.setFillColor('#f8fafc');
                  doc.rect(x, currentY, w, rowH, 'F');
                } else {
                  doc.setFillColor('#ffffff');
                  doc.rect(x, currentY, w, rowH, 'F');
                }

                doc.setDrawColor('#e2e8f0');
                doc.setLineWidth(0.5);
                doc.line(x, currentY + rowH, x + w, currentY + rowH);

                doc.setTextColor('#1e293b');
                let itemAccumPct = 0;
                columns.forEach(col => {
                  const colW = w * (col.width / 100);
                  const cellVal = row.values[col.key] ?? "";
                  
                  let displayVal = String(cellVal);
                  if (isSerialNoColumn(col)) {
                    displayVal = String(rIdx + 1);
                  } else if (col.key === 'total') {
  const q = parseFloat(String(row.values.qty)) || 0;
  const r = parseFloat(String(row.values.rate)) || 0;
  displayVal = `${pdfCurrencySymbol}${(q * r).toFixed(2)}`;
                  } else if (col.key === 'rate') {
  const numeric = parseFloat(displayVal) || 0;
  displayVal = `${pdfCurrencySymbol}${numeric.toFixed(2)}`;
}

                  const cellX = x + itemAccumPct + (col.align === 'right' ? colW - 6 : col.align === 'center' ? colW / 2 : 6);
                  doc.text(displayVal, cellX, currentY + rowH * 0.62, { align: col.align });

                  if (bilingualMode && col.key === 'description' && row.values.arabicDescription) {
  doc.setFontSize((el.fontSize || 11) * 0.78);
  if (arabicFontBase64) {
    try {
      doc.addFileToVFS('Amiri-normal.ttf', arabicFontBase64);
      doc.addFont('Amiri-normal.ttf', 'Amiri', 'normal');
    } catch(e) {}
    doc.setFont('Amiri', 'normal');
  } else {
    doc.setFont('helvetica', 'normal');
  }
                    doc.setTextColor('#64748b');
                    doc.text(
                      String(row.values.arabicDescription),
                      x + itemAccumPct + colW - 6,
                      currentY + 18,
                      { align: 'right' }
                    );
                    doc.setTextColor('#1e293b');
                    doc.setFontSize(el.fontSize || 11);
                  }

                  itemAccumPct += colW;
                });

                currentY += rowH;
              });

               

              const calculated = computeTableTotals(el);
              const totalsBoxY = y + rowH + (rows.length * rowH) + 10;
              
              const totalsX = x + (w * 0.6);
              const totalsW = w * 0.4;
              
              // Collect rows to draw in jspdf box
              const jspdfRows: { label: string; valStr: string; color?: string }[] = [];
              jspdfRows.push({ label: 'Subtotal:', valStr: `${pdfCurrencySymbol}${calculated.subtotal.toFixed(2)}` });
              if (calculated.productTax > 0) {
                jspdfRows.push({ label: 'Product Tax:', valStr: `${pdfCurrencySymbol}${calculated.productTax.toFixed(2)}`, color: '#7c3aed' });
              }
            ((el as any).taxes || []).forEach((t: any) => {
  if (t.rate > 0) jspdfRows.push({
    label: `${t.label} (${t.rate}%):`,
    valStr: `${pdfCurrencySymbol}${calculated.tax.toFixed(2)}`
  });
});

((el as any).discounts || []).forEach((d: any) => {
  if (d.rate > 0) jspdfRows.push({
    label: `${d.label} (-${d.rate}%):`,
    valStr: `-${pdfCurrencySymbol}${calculated.discount.toFixed(2)}`,
    color: '#ef4444'
  });
});
              if ((el.shippingCost || 0) > 0) {
                jspdfRows.push({ label: 'Shipping:', valStr: `${pdfCurrencySymbol}${calculated.shipping.toFixed(2)}` });
              }
              
              const jspdfRowHeight = 13;
              const jspdfTopPadding = 12;
              const jspdfDividerSpace = 6;
              const jspdfTotalRowHeight = 15;
              const jspdfTotalBoxH = jspdfTopPadding + (jspdfRows.length * jspdfRowHeight) + jspdfDividerSpace + jspdfTotalRowHeight;
              
              doc.setDrawColor('#cbd5e1');
              doc.setLineWidth(1);
              doc.roundedRect(totalsX, totalsBoxY, totalsW, jspdfTotalBoxH, 4, 4, 'D');

              doc.setFontSize(10);
              doc.setTextColor('#64748b');
              
              let currentJspdfOffset = totalsBoxY + jspdfTopPadding;
              jspdfRows.forEach(item => {
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(item.color || '#64748b');
  doc.text(item.label, totalsX + 8, currentJspdfOffset);
  doc.text(item.valStr, totalsX + totalsW - 8, currentJspdfOffset, { align: 'right' });

  if (bilingualMode) {
    const langLabels = mergedPdfLabels[translateTargetLang];
    if (langLabels) {
      const taxMatch = item.label.match(/Tax \((.+)%\):/);
      const discMatch = item.label.match(/Discount \(-(.+)%\):/);
      let translatedLabel = '';
      if (item.label === 'Subtotal:') translatedLabel = `${langLabels.subtotal}:`;
      else if (item.label === 'Shipping:') translatedLabel = `${langLabels.shipping}:`;
      else if (taxMatch) translatedLabel = `${langLabels.tax} (${taxMatch[1]}%):`;
      else if (discMatch) translatedLabel = `${langLabels.discount} (-${discMatch[1]}%):`;

      if (translatedLabel) {
        setMergedTranslatedFont();
        doc.setTextColor(item.color || '#64748b');
        doc.setFontSize(8);
        doc.text(translatedLabel, totalsX + 8, currentJspdfOffset + 7, { align: 'left' });
        doc.setFontSize(10);
      }
    }
  }

  currentJspdfOffset += bilingualMode ? jspdfRowHeight + 6 : jspdfRowHeight;
});
              
              // Grand Total
              doc.setFont('helvetica', 'bold');
              doc.setTextColor(tablePrimaryColor);
              doc.setFontSize(11);
              const jspdfLineY = currentJspdfOffset - jspdfRowHeight + jspdfDividerSpace;
              doc.line(totalsX, jspdfLineY, totalsX + totalsW, jspdfLineY);
              doc.text("Grand Total:", totalsX + 8, jspdfLineY + 11);
doc.text(`${pdfCurrencySymbol}${calculated.total.toFixed(2)}`, totalsX + totalsW - 8, jspdfLineY + 11, { align: 'right' });

if (bilingualMode) {
  const langLabels = mergedPdfLabels[translateTargetLang];
  if (langLabels) {
    setMergedTranslatedFont();
    doc.setFontSize(8);
    doc.setTextColor(tablePrimaryColor);
    doc.text(`${langLabels.total}:`, totalsX + 8, jspdfLineY + 20);
  }
}
            }

            try {
              doc.restoreGraphicsState();
            } catch (e) {}
          }
        }
      }

      if (doc) {
        const filename = `Merged_Claims_Report_${Date.now().toString().slice(-6)}.pdf`;
        doc.save(filename);
        const updatedLogs = addLogItem(logs, 'MERGED_PDF_EXPORT', `Exported ${selectedRecords.length} claims as single merged PDF file`);
        setLogs(updatedLogs);
      }
    } catch (err) {
      console.error("Merged PDF generation issue:", err);
    }
  };
const handleTranslateToArabic = async () => {
  setIsTranslating(true);
  const isContactInfo = (text: string) => {
  const trimmed = text.trim();
  const contactChars = trimmed.replace(/[\+\d\(\)\s\-\.@a-zA-Z:]/g, '');
  return /[\+\d]{5,}/.test(trimmed) || trimmed.includes('@') && contactChars.length < 3;
};
  // Helper using Google Translate unofficial API (free, no key, no limit)
  const translateToAr = async (text: string, tl: string = 'ar'): Promise<string> => {
    if (!text?.trim()) return '';
    try {
      const token = getStoredToken();
      const res = await fetch(
        `${BACKEND}/translate?text=${encodeURIComponent(text.trim())}&tl=${tl}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await res.json();
      return data.translated || '';
    } catch {
      return '';
    }
  };

  try {
    const nextElements = [...elements];

    for (const el of nextElements) {
  const idx = nextElements.findIndex(e => e.id === el.id);

  if (['qr', 'image', 'shape', 'serial', 'drawing', 'barcode'].includes(el.type)) continue;

  // Translate text elements
  if (el.type === 'text' && el.textContent?.trim()) {
  // Clear old translation if language changed
  if ((el as any).translatedLang && (el as any).translatedLang !== translateTargetLang) {
    nextElements[idx] = { ...nextElements[idx], arabicContent: undefined, translatedLang: undefined } as any;
  }
  if (el.arabicContent && (el as any).translatedLang === translateTargetLang) continue;
  
  const lines = el.textContent.split('\n');
  const translatedLines = await Promise.all(
    lines.map(async (line) => {
      if (isContactInfo(line)) return line;
      return await translateToAr(line, translateTargetLang);
    })
  );
  const arabic = translatedLines.join('\n');
  nextElements[idx] = { ...nextElements[idx], arabicContent: arabic, translatedLang: translateTargetLang } as any;
}
if (el.type === 'signature' && el.textContent?.trim()) {
  if ((el as any).translatedLang && (el as any).translatedLang !== translateTargetLang) {
    nextElements[idx] = { ...nextElements[idx], arabicContent: undefined, translatedLang: undefined } as any;
  }
  if ((el as any).arabicContent && (el as any).translatedLang === translateTargetLang) continue;
  const arabicCaption = await translateToAr(el.textContent, translateTargetLang);
  nextElements[idx] = { ...nextElements[idx], arabicContent: arabicCaption, translatedLang: translateTargetLang } as any;
}

      // Translate table columns and rows
      if (el.type === 'table') {
  const translatedColumns = [];
  for (const col of (el.columns || [])) {
    // Skip already translated columns
    if ((col as any).translatedLang && (col as any).translatedLang !== translateTargetLang) { translatedColumns.push({ ...col, arabicHeader: undefined, translatedLang: undefined }); continue; }
    if (col.arabicHeader && (col as any).translatedLang === translateTargetLang) { translatedColumns.push(col); continue; }
          const arabicHeader = await translateToAr(col.header, translateTargetLang);
          translatedColumns.push({ ...col, arabicHeader, translatedLang: translateTargetLang });
        }

        const translatedRows = [];
        for (const row of (el.rows || [])) {
  // Skip already translated rows
  if ((row as any).translatedLang && (row as any).translatedLang !== translateTargetLang) { translatedRows.push({ ...row, translatedLang: undefined, values: { ...row.values, arabicDescription: undefined } }); continue; }
  if (row.values.arabicDescription && (row as any).translatedLang === translateTargetLang) { translatedRows.push(row); continue; }
  const desc = String(row.values.description || '');
  const arabicDescription = desc.trim() ? await translateToAr(desc, translateTargetLang) : '';
          translatedRows.push({
            ...row,
            translatedLang: translateTargetLang,
            values: { ...row.values, arabicDescription }
          });
        }

        nextElements[idx] = {
          ...nextElements[idx],
          columns: translatedColumns,
          rows: translatedRows
        };
      }
    }

 
    // Auto-expand element heights to fit Arabic content
   const expandedElements = nextElements.map(el => {
  if (el.type === 'text' && el.arabicContent) {
    const englishLines = (el.textContent || '').split('\n').length;
const arabicLines = el.arabicContent.split('\n').length;
const fs = el.fontSize || 14;
const lh = el.lineHeight || 1.3;
const maxLines = Math.max(englishLines, arabicLines);
const newHeight = Math.max(el.height, Math.ceil(maxLines * fs * lh) + 10);
const newWidth = Math.min(
  Math.max(el.width * 1.6, 220),
  pageConfig.width - el.x - 10
);
// Clamp x so element doesn't overflow right edge of page
const maxX = pageConfig.width - newWidth - 10;
const newX = Math.max(0, Math.min(el.x, maxX));
return { 
  ...el, 
  height: newHeight, 
  width: newWidth, 
  x: newX,
};
}
if (el.type === 'table') {
  const alreadyTranslated = el.rows?.some((r: any) => r.values?.arabicDescription);
  if (alreadyTranslated) return el;
  const extraPerRow = 18;
  const rowCount = el.rows?.length || 0;
  const newHeight = el.height + (rowCount * extraPerRow);
  return { ...el, height: newHeight };
}
if (el.type === 'shape' && el.borderWidth && el.borderWidth > 0) {
  // Find text elements that overlap or are inside this shape box
  const overlappingTexts = nextElements.filter(t => 
    t.type === 'text' &&
    t.x >= el.x - 20 && 
    t.x <= el.x + el.width + 20 &&
    t.y >= el.y - 20 && 
    t.y <= el.y + el.height + 20
  );
  
  if (overlappingTexts.length > 0) {
    // Find the rightmost and bottommost edge of all overlapping text elements
    const maxRight = Math.max(...overlappingTexts.map(t => t.x + t.width));
    const maxBottom = Math.max(...overlappingTexts.map(t => t.y + t.height));
    
    const newWidth = Math.max(el.width, maxRight - el.x + 10);
const newHeight = Math.max(el.height, maxBottom - el.y + 10);
    
    return { ...el, width: newWidth, height: newHeight };
  }
}
return el;
    });

    setElements(expandedElements);
setBilingualMode(true);
saveToHistory(expandedElements, pageSize);

    const updatedLogs = addLogItem(logs, 'ARABIC_TRANSLATE',
      `Translated all elements to Arabic and enabled bilingual layout`
    );
    setLogs(updatedLogs);

  } catch (err) {
    console.error('Translation failed:', err);
    alert('Translation failed. Check internet connection.');
  } finally {
    setIsTranslating(false);
  }
};
const generateQRDataUrl = async (value: string, elementId: string) => {
  try {
    const dataUrl = await QRCode.toDataURL(value, {
      errorCorrectionLevel: 'M',
      width: 300,
      margin: 2,
    });
    setQrDataUrls(prev => ({ ...prev, [elementId]: dataUrl }));
  } catch (e) {
    console.error('QR generation failed:', e);
  }
};
 
const handleGenerateBarcodeLink = () => {
  const barcodeEl = elements.find(el => el.type === 'barcode');
  const value = barcodeEl?.barcodeValue;
const scanValue = (barcodeEl as any)?.barcodeShareId || value;
const displayLabel = value;

  if (!value) {
    alert('No barcode found on canvas. Add a barcode element first, then Save & Track to generate a real invoice number.');
    return;
  }

  navigator.clipboard.writeText(value).catch(() => {});

  const { stripes, width: barsWidth } = generateCode39Bars(scanValue);
  const barH = 80;
  const totalW = barsWidth + 20;
  const totalH = barH + 28;

  const svgBars = stripes
    .map(s => s.isBlack
      ? `<rect x="${s.x}" y="0" width="${s.width}" height="${barH}" fill="#000"/>`
      : ''
    ).join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}">
    <rect width="${totalW}" height="${totalH}" fill="white"/>
    ${svgBars}
    <text x="${totalW / 2}" y="${barH + 18}" text-anchor="middle" font-family="monospace" font-size="13">${displayLabel}</text>
  </svg>`;

  const dataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;

  const win = window.open('', '_blank', 'width=500,height=280,toolbar=no,menubar=no');
  if (win) {
    win.document.write(`<!DOCTYPE html><html><head><title>Barcode: ${value}</title>
    <style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;background:#f8fafc;font-family:sans-serif;gap:12px;}
    img{border:1px solid #e2e8f0;background:white;padding:16px;border-radius:8px;}
    .val{font-size:18px;font-weight:bold;color:#1e293b;font-family:monospace;}
    button{padding:8px 20px;background:#2563eb;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;}</style>
    </head><body>
    <div class="val">${displayLabel}</div>
    <img src="${dataUrl}" width="400"/>
    <button onclick="window.close()">Close</button>
    </body></html>`);
    win.document.close();
  } else {
    alert(`Barcode value: ${value}\n\n(Copied to clipboard)`);
  }
};
 const handleGenerateShareLink = async () => {
  const essentialElements = elements.map(el => {
    const base: any = {
      id: el.id, type: el.type,
      x: el.x, y: el.y,
      width: el.width, height: el.height,
      zIndex: el.zIndex, opacity: el.opacity,
      color: el.color, rotation: el.rotation,
    };
    if (el.type === 'text') { base.textContent = el.textContent; base.fontSize = el.fontSize; base.fontFamily = el.fontFamily; base.fontWeight = el.fontWeight; base.textAlign = el.textAlign; base.lineHeight = el.lineHeight; }
    if (el.type === 'shape') { base.backgroundColor = el.backgroundColor; base.borderColor = el.borderColor; base.borderWidth = el.borderWidth; base.borderRadius = el.borderRadius; base.shapeType = el.shapeType; }
    if (el.type === 'table') { base.columns = el.columns; base.rows = el.rows; base.taxRate = el.taxRate; base.discountRate = el.discountRate; base.shippingCost = el.shippingCost; base.fontSize = el.fontSize; }
    if (el.type === 'stamp') { base.stampText = el.stampText; base.stampShape = el.stampShape; base.stampDraft = el.stampDraft; base.fontSize = el.fontSize; }
    if (el.type === 'signature') { base.signatureName = el.signatureName; base.fontFamily = el.fontFamily; base.fontSize = el.fontSize; }
    if (el.type === 'qr') { base.qrValue = el.qrValue; }

    if (el.type === 'serial') { base.serialSettings = el.serialSettings; base.serialStyle = el.serialStyle; base.fontSize = el.fontSize; }
    if (el.type === 'image') return null;
    return base;
  }).filter(Boolean);

  const invoiceData = { elements: essentialElements, pageSize, generatedAt: new Date().toISOString() };
  
  // Save to server
let shareUrl = '';
try {
  const response = await fetch(`${BACKEND}/invoices/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(invoiceData)
  });
  const { id } = await response.json();
  shareUrl = `${APP_URL}/?i=${id}`;
} catch (err) {
  alert('Share server error. Make sure your FastAPI backend has /api/invoice endpoint.');
  return;
}
  const nextElements = elements.map(el =>
    el.type === 'qr' ? { ...el, qrValue: shareUrl } : el
  );
  setElements(nextElements);
  saveToHistory(nextElements, pageSize);

  // Generate proper QR
  const qrElements = nextElements.filter(el => el.type === 'qr');
  qrElements.forEach(el => generateQRDataUrl(el.qrValue || '', el.id));

  navigator.clipboard.writeText(shareUrl).catch(() => {});
  alert(`Done! Short link: ${shareUrl}\n\nQR updated — scan on same WiFi to view invoice!`);
};



  const handlePortalUpdateBill = (billId: string, updatedFields: Partial<InvoiceRecord>) => {
    const original = bills.find(b => b.id === billId);
    if (!original) return;
    const elementsClone = JSON.parse(JSON.stringify(original.elements));
    const newAmount = updatedFields.amount !== undefined ? updatedFields.amount : original.amount;
    handleUpdateInvoice(billId, elementsClone, newAmount, updatedFields);
  };

  const handlePortalAddLog = (action: string, details: string) => {
    const nextLogs = addLogItem(logs, action, details);
    setLogs(nextLogs);
  };

  const handleAddGeneratedInvoice = (newInvoice: InvoiceRecord) => {
    const nextBills = [newInvoice, ...bills];
    setBills(nextBills);
  };
const handleResetAllDatabase = () => {
    if (window.confirm("Are you sure you want to delete all saved invoices, client accounts, custom templates, and start 100% clean?")) {
      setBills([]);
      setAccounts([]);
      setCustomTemplates([]);

      const startLog = addLogItem([], 'SYSTEM_RESET', 'All customer accounts, templates, and saved bills cleared by operator.');
      setLogs(startLog);
      saveData(StorageKeys.INVOICES, []);
      saveData(StorageKeys.CLIENTS, []);
      saveData(StorageKeys.LOGS, startLog);
    
      saveData('cloudairambo_custom_templates', []);
      alert("All database records cleared!");
    }
  };
const refreshUserAndModules = async (token: string, fallbackUser?: AuthUser) => {
  let freshUser: AuthUser | undefined = fallbackUser;
  try {
    const res = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      freshUser = await res.json();
      saveSession(token, '', freshUser as AuthUser);
      setAuthUser(freshUser as AuthUser);
    } else if (fallbackUser) {
      setAuthUser(fallbackUser);
    }
  } catch {
    if (fallbackUser) setAuthUser(fallbackUser);
  }

  try {
    const modRes = await fetch(`${BACKEND}/billing/modules/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (modRes.ok) {
      const modData = await modRes.json();
      if (modData?.statuses) setModuleStatuses(modData.statuses);
      if (modData?.trial_end_dates) setModuleTrialEndDates(modData.trial_end_dates);
      if (modData?.period_end_dates) setModulePeriodEndDates(modData.period_end_dates);
      if (modData?.outlet_counts) setModuleOutletCounts(modData.outlet_counts);
      if (modData?.seats_included && modData?.seat_counts) {
        const limits: Record<string, number> = {};
        for (const id of Object.keys(modData.seats_included)) {
          limits[id] = (modData.seats_included[id] ?? 0) + (modData.seat_counts[id] ?? 0);
        }
        setModuleSeatLimits(limits);
      }
      if (modData?.suite) {
        setSuiteSeatLimit((modData.suite.seats_included ?? 0) + (modData.suite.seat_count ?? 0));
      } else {
        setSuiteSeatLimit(null);
      }
      return { freshUser, moduleStatuses: modData?.statuses || {} };
    }
  } catch (e) {
    console.warn('Module status refresh failed:', e);
  }
  return { freshUser, moduleStatuses: {} };
};

if (!authChecked && !showLanding) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

  if (window.location.pathname === '/wait') {
  return <TokenWaitPage />;
}
if (window.location.pathname === '/queue/display') {
  return <QueueDisplayPage />;
}
if (window.location.pathname === '/clinic/wait') {
  return <ClinicWaitPage />;
}
if (window.location.pathname === '/queue/desk') {
  return <QueueDeskPage />;
}
if (window.location.pathname === '/phone-scan') {
  const sessionId = new URLSearchParams(window.location.search).get('session') || '';
  return <ScanSessionPage sessionId={sessionId} />;
}
if (window.location.pathname === '/queue/track') {
  return <QueueTrackPage />;
}
if (window.location.pathname === '/kitchen') {
  return <KitchenDashboard />;
}
if (window.location.pathname === '/mobile/invoice-preview') {
  return <MobileInvoicePreview />;
}
if (window.location.pathname.startsWith('/print/invoice/')) {
  return <PrintInvoicePage />;
}
if (window.location.pathname.startsWith('/remote-attendance')) {
  return <RemoteAttendancePage />;
}
if (window.location.pathname.startsWith('/print/invoice/')) {
  const PrintInvoicePage = require('./components/PrintInvoicePage').default;
  return <PrintInvoicePage />;
}
if (window.location.pathname === '/privacy') {
  return <PrivacyPage />;
}
if (window.location.pathname === '/terms') {
  return <TermsPage />;
}
if (window.location.pathname === '/about') {
  return <AboutPage />;
}
if (window.location.pathname === '/pricing') {
  // Logged-in users already have the in-app plans screen
  if (authUser) {
    window.location.pathname = '/';
    return null;
  }
  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px', height: 60, borderBottom: '1px solid #e5e7eb' }}>
        <a href="/" style={{ fontWeight: 800, fontSize: 17, color: '#111827', textDecoration: 'none' }}>CloudAIRambo</a>
        <a href="/?login=1" style={{ fontSize: 14, color: '#4f46e5', fontWeight: 700, textDecoration: 'none' }}>Sign in</a>
      </div>
      <div style={{ padding: '40px 24px' }}>
        <SubscriptionPlansView
          embedded
          currentPlan="basic"
          onLoginRequired={() => { window.location.href = '/?login=1'; }}
        />
      </div>
      <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', padding: '0 0 40px' }}>
        <a href="/terms" style={{ color: '#6366f1' }}>Terms</a> · <a href="/privacy" style={{ color: '#6366f1' }}>Privacy</a> · <a href="/about" style={{ color: '#6366f1' }}>About</a>
      </div>
    </div>
  );
}
if (window.location.pathname === '/display') {
  return <DisplayPage />;
}
if (window.location.pathname === '/kiosk/gym') {
  return <GymKiosk />;
}
if (window.location.pathname === '/kiosk/employee') {
  return <EmployeeKiosk />;
}
if (window.location.pathname.startsWith('/remote-enroll')) {
  return <RemoteEnrollPage />;
}
if (window.location.pathname.startsWith('/invoice/view/')) {
  const token = window.location.pathname.split('/invoice/view/')[1];
  return <PublicInvoiceView token={token} />;
}
if (window.location.pathname.startsWith('/pay/bill/')) {
  return <PayBillPage />;
}
// Team login route  ← ADD FROM HERE
if (window.location.pathname === '/team-login') {
  if (authUser) {
    window.location.pathname = '/';
    return null;
  }
  return (
    <TeamLoginScreen
      onAuth={async (user, token) => {
  saveSession(token, '', user);
  await refreshUserAndModules(token, user);
        window.location.pathname = '/';
      }}
    />
  );
}
if (showLanding && !showAuthModal) {
  return <LandingPage onEnterApp={() => { setShowLanding(false); if (!authUser) setShowAuthModal(true); }} />;
}
// ← TO HERE
if (showStorageOptions && !authUser) {
  return (
    <div className="fixed inset-0 z-50 bg-white">
      <StorageOptionScreen
        onClose={() => setShowStorageOptions(false)}
        onSelect={(option) => {
          setStorageOption(option);
          if (option === 'local') {
            setShowStorageOptions(false);
          } else if (option === 'drive') {
            setShowStorageOptions(false);
            handleGoogleLogin();
          } else if (option === 'cloud') {
            setShowStorageOptions(false);
            setShowAuthModal(true);
          }
        }}
      />
    </div>
  );
}

if (showAuthModal && !authUser) {
  return (
    <AuthScreen
     onAuth={async (user, token) => {
  saveSession(token, '', user);
  const { freshUser, moduleStatuses: freshModuleStatuses } = await refreshUserAndModules(token, user);
  const effectiveUser = freshUser || user;

  setShowAuthModal(false);
  setShowStorageOptions(false);
  if ((effectiveUser.role === 'owner' || effectiveUser.role === 'individual') && !effectiveUser.onboarding_completed) {
    setShowOnboarding(true);
  }
// Set default tab based on permissions

const p = getPermissions(effectiveUser.role, effectiveUser.permissions, (effectiveUser as any)?.plan, (effectiveUser as any)?.plan_tier, (effectiveUser as any)?.plan_status, freshModuleStatuses);
if (!p.canAccessGenerator) {
  if (effectiveUser.role === 'queue_member')    setCurrentTab('clinic');
  else if (effectiveUser.role === 'ledger_member')   setCurrentTab('ledger');
  else if (effectiveUser.role === 'employee_member') setCurrentTab('employees');
  else if (effectiveUser.role === 'gym_member')      setCurrentTab('fitness');
  else if (effectiveUser.role === 'canteen_member')  setCurrentTab('food');
  else if (effectiveUser.role === 'pos_member')      setCurrentTab('pos');
  else if (p.canAccessBills)    setCurrentTab('bills');
  else if (p.canAccessProducts) setCurrentTab('products');
  else if (p.canAccessAccounts) setCurrentTab('accounts');
}
}}
      onClose={() => {
        setShowAuthModal(false);
        setShowStorageOptions(false);
      }}
    />
  );
}



if (showOnboarding && authUser) {
  return (
    <OnboardingPage
      token={getStoredToken() || ''}
      authUser={authUser}
      onComplete={() => {
  setShowOnboarding(false);
  setShowWelcomeTrial(true);
}}

    />
  );
}

if (
  !isAnyMobileEmbed &&
  authUser && authChecked && outletsChecked && !showOnboarding &&
  (authUser.role === 'owner' || authUser.role === 'individual') &&
  !outletsData.some(o => (o.modules || []).includes('suite') || (o as any).suite_tier)
) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 360, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ fontSize: 36, marginBottom: 10, textAlign: 'center' }}>🏪</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6, textAlign: 'center' }}>Create your first outlet</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
          You need at least one outlet before you can use the app.
        </div>
        <input
          value={newOutletName}
          onChange={e => setNewOutletName(e.target.value)}
          placeholder="Outlet name, e.g. Main Branch"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Country</label>
            <select
              value={newOutletCountry}
              onChange={e => {
                const c = e.target.value;
                setNewOutletCountry(c);
                setNewOutletCurrency(getCountryCurrency(c));
              }}
              style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, boxSizing: 'border-box', background: '#fff', color: '#111827' }}
            >
              {Object.keys(COUNTRY_CODES).sort().map(c => (
                <option key={c} value={c}>{c} (+{COUNTRY_CODES[c]})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>Currency</label>
            <select
              value={newOutletCurrency}
              onChange={e => setNewOutletCurrency(e.target.value)}
              style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, boxSizing: 'border-box', background: '#fff', color: '#111827' }}
            >
              {Object.keys(CURRENCY_MAP).sort().map(cur => (
                <option key={cur} value={cur}>{cur} — {CURRENCY_MAP[cur]}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={handleCreateFirstOutlet}
          disabled={creatingOutlet}
          style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: creatingOutlet ? 'not-allowed' : 'pointer', opacity: creatingOutlet ? 0.6 : 1 }}
        >
          {creatingOutlet ? 'Creating...' : 'Create Outlet & Continue'}
        </button>
      </div>
    </div>
  );
}

if (
  !isAnyMobileEmbed &&
  authUser && authChecked && outletsChecked && !showOnboarding &&
  moduleOutletGateFor
) {
  const moduleGateLabels: Record<string, { icon: string; label: string }> = {
    ledger: { icon: '📒', label: 'Credit Book' },
    queue: { icon: '🎫', label: 'Queue' },
    employees: { icon: '👥', label: 'Employees' },
    gym: { icon: '🏋️', label: 'Fitness Center' },
    canteen: { icon: '🍽️', label: 'Meal Plans & Dining' },
    pos: { icon: '🧾', label: 'POS' },
  };
  const gateInfo = moduleGateLabels[moduleOutletGateFor] || { icon: '🧩', label: moduleOutletGateFor };
  const otherMissing = moduleOutletGateMissingAll.filter(m => m !== moduleOutletGateFor);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 380, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ fontSize: 36, marginBottom: 10, textAlign: 'center' }}>{gateInfo.icon}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6, textAlign: 'center' }}>Set up an outlet for {gateInfo.label}</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
          You just activated {gateInfo.label}. Create a dedicated outlet for it now, or skip and set it up later from Team settings.
        </div>

        <input
          value={newModuleOutletName}
          onChange={e => setNewModuleOutletName(e.target.value)}
          placeholder={`e.g. ${gateInfo.label} — Main Branch`}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: otherMissing.length > 0 ? 12 : 16, boxSizing: 'border-box' }}
        />

        {otherMissing.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Also set up on this outlet? (optional)
            </div>
            {otherMissing.map(modId => {
              const info = moduleGateLabels[modId as string] || { icon: '🧩', label: modId as string };
              const checked = selectedGateModules.includes(modId);
              return (
                <label key={modId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#334155', marginBottom: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setSelectedGateModules(prev =>
                      checked ? prev.filter(m => m !== modId) : [...prev, modId]
                    )}
                  />
                  {info.icon} {info.label}
                </label>
              );
            })}
          </div>
        )}

        <button
          onClick={handleCreateModuleOutlet}
          disabled={creatingModuleOutlet}
          style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: creatingModuleOutlet ? 'not-allowed' : 'pointer', opacity: creatingModuleOutlet ? 0.6 : 1, marginBottom: 8 }}
        >
          {creatingModuleOutlet ? 'Creating...' : `Create Outlet for ${gateInfo.label}`}
        </button>

        <button
          onClick={handleSkipModuleOutletGate}
          disabled={creatingModuleOutlet}
          style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #e5e7eb', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

if (
  !isAnyMobileEmbed &&
  authUser && authChecked && outletsChecked && !showOnboarding &&
  tabOutletGateFor
) {
  const tabGateLabels: Record<string, { icon: string; label: string }> = {
    ledger: { icon: '📒', label: 'Credit Book' },
    queue: { icon: '🎫', label: 'Queue' },
    employees: { icon: '👥', label: 'Employees' },
    gym: { icon: '🏋️', label: 'Fitness Center' },
    canteen: { icon: '🍽️', label: 'Meal Plans & Dining' },
    pos: { icon: '🧾', label: 'POS' },
  };
  const gateInfo = tabGateLabels[tabOutletGateFor.moduleId] || { icon: '🧩', label: tabOutletGateFor.moduleId };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 380, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ fontSize: 36, marginBottom: 10, textAlign: 'center' }}>{gateInfo.icon}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6, textAlign: 'center' }}>Set up an outlet for {gateInfo.label}</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
          {gateInfo.label} needs an outlet before you can open it. Create one now to continue.
        </div>

        <input
          value={tabGateOutletName}
          onChange={e => setTabGateOutletName(e.target.value)}
          placeholder={`e.g. ${gateInfo.label} — Main Branch`}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }}
        />

        <button
          onClick={handleCreateTabGateOutlet}
          disabled={creatingTabGateOutlet}
          style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: creatingTabGateOutlet ? 'not-allowed' : 'pointer', opacity: creatingTabGateOutlet ? 0.6 : 1, marginBottom: 8 }}
        >
          {creatingTabGateOutlet ? 'Creating...' : `Create Outlet & Continue`}
        </button>

        <button
          onClick={() => { setTabOutletGateFor(null); setTabGateOutletName(''); }}
          disabled={creatingTabGateOutlet}
          style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid #e5e7eb', background: 'transparent', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

  if (sharedInvoiceData) {
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-start py-10 px-4 overflow-x-hidden">
      <div className="mb-6 text-center">
        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-extrabold text-sm mx-auto mb-2">CA</div>
        <h1 className="text-xl font-extrabold text-slate-800">CloudAI<span className="text-blue-600">Rambo</span></h1>
        <p className="text-sm text-slate-500 mt-1">Shared Invoice — Read Only</p>
      </div>
     <div
  className="bg-white shadow-xl border border-slate-200 relative overflow-hidden"
  style={{
    width: PAGE_PRESETS[sharedInvoiceData.pageSize]?.width || 794,
    minHeight: PAGE_PRESETS[sharedInvoiceData.pageSize]?.height || 1123,
    transform: `scale(${Math.min(1, (window.innerWidth - 32) / (PAGE_PRESETS[sharedInvoiceData.pageSize]?.width || 794))})`,
    transformOrigin: 'top left',
    marginBottom: `calc((${PAGE_PRESETS[sharedInvoiceData.pageSize]?.height || 1123}px * ${Math.min(1, (window.innerWidth - 32) / (PAGE_PRESETS[sharedInvoiceData.pageSize]?.width || 794))}) - ${PAGE_PRESETS[sharedInvoiceData.pageSize]?.height || 1123}px)`,
  }}
>
        {sharedInvoiceData.elements
          .sort((a: any, b: any) => a.zIndex - b.zIndex)
          .map((el: any) => (
            <div
              key={el.id}
              style={{
                position: 'absolute',
                left: el.x, top: el.y,
                width: el.width, height: el.height,
                zIndex: el.zIndex,
                opacity: (el.opacity ?? 100) / 100,
              }}
            >
              {el.type === 'text' && (
                <div style={{
                  fontSize: el.fontSize ? `${el.fontSize}px` : '14px',
                  fontFamily: el.fontFamily || 'Inter',
                  color: el.color || '#000',
                  fontWeight: el.fontWeight || 'normal',
                  fontStyle: el.fontStyle || 'normal',
                  textAlign: el.textAlign || 'left',
                  whiteSpace: 'pre-wrap',
                  lineHeight: el.lineHeight || 1.3,
                  padding: '2px 4px',
                }}>
                  {el.textContent}
                </div>
              )}
              {el.type === 'shape' && (
                <div style={{
                  width: '100%', height: '100%',
                  backgroundColor: el.backgroundColor || 'transparent',
                  border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor}` : 'none',
                  borderRadius: el.borderRadius ? `${el.borderRadius}px` : '0'
                }} />
              )}
              {el.type === 'image' && el.textContent && (
                <img src={el.textContent} className="w-full h-full object-contain" />
              )}
              {el.type === 'stamp' && el.visible !== false && (() => {
  const status = (sharedInvoiceData.status || '').toLowerCase();
  if (!['paid', 'overdue', 'cancelled', 'unpaid'].includes(status)) return null;
  const stampText = status === 'paid' ? 'PAID IN FULL'
    : status === 'overdue' ? 'OVERDUE'
    : status === 'cancelled' ? 'CANCELLED'
    : 'UNPAID';
  const stampColor = status === 'paid' ? '#10b981'
    : status === 'overdue' ? '#ef4444'
    : status === 'cancelled' ? '#6b7280'
    : '#f59e0b';
  return (
    <div style={{
      width: '100%', height: '100%',
      color: stampColor, borderColor: stampColor,
      fontSize: el.fontSize ? `${el.fontSize}px` : '18px',
      transform: `rotate(${el.rotation || 0}deg)`,
    }} className="border-4 border-double flex items-center justify-center font-black tracking-widest uppercase rounded">
      {stampText}
    </div>
  );
})()}
              {el.type === 'signature' && (
                <div style={{ color: el.color || '#0f172a' }} className="w-full h-full flex flex-col justify-end items-center p-1 pb-1.5">
                  <span style={{ fontFamily: el.fontFamily || 'Alex Brush', fontSize: el.fontSize ? `${el.fontSize}px` : '24px' }} className="tracking-wide text-center w-full truncate">
                    {el.signatureName || 'Authorized'}
                  </span>
                  <div className="w-full h-px bg-slate-400/80 my-1" />
                  <span className="text-[9px] text-slate-500 font-bold uppercase text-center w-full truncate">
                    {el.textContent || 'Authorized Signature'}
                  </span>
                </div>
              )}
              {el.type === 'qr' && (
                <div className="w-full h-full bg-white p-1 grid" style={{ gridTemplateColumns: `repeat(${generateQRMatrix(el.qrValue || '').length}, minmax(0,1fr))` }}>
                  {generateQRMatrix(el.qrValue || '').map((row: boolean[], rIdx: number) =>
                    row.map((isDark: boolean, cIdx: number) => (
                      <div key={`${rIdx}-${cIdx}`} style={{ backgroundColor: isDark ? '#000' : '#fff' }} />
                    ))
                  )}
                </div>
              )}
             
              {el.type === 'table' && el.columns && el.rows && (
                <div className="w-full h-full bg-slate-50 border border-slate-200 flex flex-col rounded p-1" style={{ fontFamily: el.fontFamily || 'Inter' }}>
                  <div style={{ backgroundColor: el.color || '#1e293b', color: '#fff' }} className="flex text-xs font-bold p-2.5 rounded-t shrink-0">
                    {el.columns.map((col: any) => (
                      <div key={col.id} style={{ width: `${col.width}%` }} className="px-2 truncate">{col.header}</div>
                    ))}
                  </div>
                  <div className="flex-1 overflow-hidden text-xs text-slate-800">
                    {el.rows.map((row: any, rIdx: number) => (
                      <div key={row.id} className={`flex p-2.5 items-center ${rIdx % 2 === 1 ? 'bg-blue-50/25' : 'bg-white'}`}>
                        {el.columns.map((col: any) => {
                          let display = String(row.values[col.key] ?? '');
                          if (col.key === 'total') {
                            const q = parseFloat(String(row.values.qty)) || 0;
                            const r = parseFloat(String(row.values.rate)) || 0;
                            display = `$${(q * r).toFixed(2)}`;
                          } else if (col.key === 'rate') {
                            display = `$${(parseFloat(display) || 0).toFixed(2)}`;
                          }
                          return <div key={col.id} style={{ width: `${col.width}%` }} className="px-2 truncate">{display}</div>;
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
      <p className="mt-6 text-xs text-slate-400">Generated with CloudAIRambo · {(() => {
        const raw = sharedInvoiceData.created_at || sharedInvoiceData.createdAt || sharedInvoiceData.generatedAt || '';
        if (!raw) return '';
        return new Date(raw.endsWith('Z') || raw.includes('+') ? raw : raw + 'Z').toLocaleDateString();
      })()}</p>
    </div>
  );
}
const planStatus = (authUser as any)?.plan_status;
const hasActiveModule = Object.values(moduleStatuses || {}).some(s => s === 'active' || s === 'trial');
const isFullyLocked = authUser && (planStatus === 'expired' || planStatus === 'cancelled') && (authUser as any)?.plan !== 'basic' && !hasActiveModule;

return (
  <div className="h-screen w-full bg-slate-50 text-slate-800 flex flex-col font-sans select-none overflow-hidden">
  {isFullyLocked && !showPlans && !isAnyMobileEmbed && (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowPlans(true)}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '36px 44px', textAlign: 'center', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 10 }}>Plan Expired</div>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 24 }}>
          Your <strong>{((authUser as any)?.plan || 'Pro').toUpperCase()}</strong> plan has expired.<br/>
          Subscribe to continue accessing all features and your data.
        </div>
        <button onClick={() => setShowPlans(true)} style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg,#6366f1,#7c3aed)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: 10 }}>
          💳 Subscribe Now
        </button>
        <button onClick={() => { clearSession(); setAuthUser(null); setShowLanding(true); }} style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer' }}>
          Sign out instead
        </button>
      </div>
    </div>
  )}
      
      {!isAnyMobileEmbed && (
<Navbar
  currentTab={currentTab}
  planBadge={(authUser as any)?.plan || null}
  moduleStatuses={moduleStatuses}
  moduleTrialEndDates={moduleTrialEndDates}
  modulePeriodEndDates={modulePeriodEndDates}
  onOutletFilterChange={(outletId) => setSelectedOutletFilter(outletId)}
  onTrialExpired={async () => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    if (!jwt) return;
    try {
      const res = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } });
      let fresh: any = null;
      if (res.ok) {
        fresh = await res.json();
        setAuthUser(fresh);
      }

      let hasActiveModule = false;
      const modRes = await fetch(`${BACKEND}/billing/modules/status`, { headers: { Authorization: `Bearer ${jwt}` } });
      if (modRes.ok) {
        const modData = await modRes.json();
        if (modData?.statuses) {
          setModuleStatuses(modData.statuses);
          hasActiveModule = Object.values(modData.statuses).some((s: any) => s === 'active' || s === 'trial');
        }
        if (modData?.trial_end_dates) setModuleTrialEndDates(modData.trial_end_dates);
        if (modData?.period_end_dates) setModulePeriodEndDates(modData.period_end_dates);
        if (modData?.outlet_counts) setModuleOutletCounts(modData.outlet_counts);
        if (modData?.seats_included && modData?.seat_counts) {
          const limits: Record<string, number> = {};
          for (const id of Object.keys(modData.seats_included)) {
            limits[id] = (modData.seats_included[id] ?? 0) + (modData.seat_counts[id] ?? 0);
          }
          setModuleSeatLimits(limits);
        }
        if (modData?.suite) {
          setSuiteSeatLimit((modData.suite.seats_included ?? 0) + (modData.suite.seat_count ?? 0));
        } else {
          setSuiteSeatLimit(null);
        }
      }

      if (fresh) {
        const prevPlan = authUserRef.current?.plan;
        const wasPaid = prevPlan === 'pro' || prevPlan === 'ultra';
        if (wasPaid && fresh.plan === 'basic' && !hasActiveModule) {
          setPlanExpiredLock(true);
          setShowPlans(true);
        }
      }
    } catch (e) {
      console.warn('Immediate expiry refresh failed:', e);
    }
  }}
        setCurrentTab={(tab) => {
  if (currentTab === 'generator' && tab !== 'generator' && elements.length > 0) {
    const confirmed = window.confirm(
      'You have unsaved work in the Invoice Creator. Leave without saving?'
    );
    if (!confirmed) return;
  }
  if (!requireAtLeastOneOutlet()) return;

  const requiredModuleId = TAB_MODULE_MAP[tab];
  if (
    requiredModuleId &&
    (authUser?.role === 'owner' || authUser?.role === 'individual') &&
    outletsChecked &&
    !outletsData.some(o => (o.modules || []).includes(requiredModuleId))
  ) {
    setTabOutletGateFor({ tab, moduleId: requiredModuleId });
    setTabGateOutletName('');
    return; // block the switch — the gate screen takes over from here
  }

  localStorage.setItem('cloudairambo_last_tab', tab);
  setCurrentTab(tab as any);
  setSelectedElementId(null);
}}
        authUser={authUser}
        onSignIn={() => setShowAuthModal(true)}
        onSignOut={() => { clearSession(); window.location.href = '/'; }}
      />
      )}


      {currentTab === 'bills' && (
        <BillsView
  activeCurrency={effectiveCurrency}
  userRole={authUser?.role}
  bills={bills}
  setBills={setBills}
  authUserPerms={authUser?.permissions}
  authUserPlan={(authUser as any)?.plan}
          onToggleStatus={handleToggleBillStatus}
          onDeleteBill={handleDeleteBill}
          onSyncToDrive={handleSyncInvoiceToDrive}
          onDownloadPDF={handleDownloadInvoicePDF}
          onPrintPDF={handlePrintInvoicePDF}
          onExportMergedPDF={handleExportMergedPDF}
          onUpdateInvoice={handleUpdateInvoice}
          onReuseInvoiceLayout={handleReuseInvoiceLayout}
          onEditInvoice={handleEditInvoice}
          googleToken={googleToken}
          isSyncingId={isSyncingId}
          onGoogleLogin={handleGoogleLogin}
          onAddLog={handlePortalAddLog}
          onGetPdfUrl={async (bill: InvoiceRecord) => {
            try {
              // Was calling handleExportPDF directly — the old wide-table
              // renderer, different from what WhatsApp/Print produce via
              // getInvoicePdfBlob. Route through the same shared function
              // so every surface (Download, preview URL, Print, WhatsApp)
              // renders identically.
              const blob = await getInvoicePdfBlob(bill);
              if (!blob) return null;
              return URL.createObjectURL(blob as Blob);
            } catch (e) {
              return null;
            }
          }}
          
        onCreateUpdatedInvoice={async (invoice) => {
            try {
              const { invoicesApi } = await import('./utils/api');
              const elementsForDB = JSON.parse(JSON.stringify(invoice.elements || [])).map((el: any) => {
                if (el.type === 'image' && el.textContent && el.textContent.length > 500)
                  return { ...el, textContent: '__image_stripped__' };
                return el;
              });
              const saved = await invoicesApi.create({
                client_name: invoice.clientName,
                client_email: invoice.clientEmail,
                client_phone: invoice.clientPhone,
                client_address: invoice.clientAddress,
                amount: invoice.amount,
                currency: invoice.currency,
                status: (invoice.status || 'draft').toLowerCase(),
                elements: elementsForDB,
                page_size: (invoice as any).pageSize || 'A4',
                line_items: [],
                outlet_id: selectedOutletFilter ?? null,
              });
              const newBill: InvoiceRecord = {
                id: String(saved.id),
                invoiceNumber: saved.invoice_number,
                clientName: saved.client_name || invoice.clientName || '',
                clientEmail: saved.client_email || invoice.clientEmail || '',
                clientPhone: saved.client_phone || (invoice as any).clientPhone || '',
                clientAddress: saved.client_address || (invoice as any).clientAddress || '',
                amount: saved.amount || invoice.amount || 0,
                status: saved.status || invoice.status || 'draft',
                currency: saved.currency || invoice.currency || 'USD',
                amountPaid: invoice.amountPaid || 0,
                balanceDue: invoice.balanceDue || 0,
                createdAt: saved.created_at,
                elements: invoice.elements || [],
                pageSize: (invoice as any).pageSize || 'A4',
                ...(saved.public_token ? { publicToken: saved.public_token } : {}),
              };
              setBills(prev => [newBill, ...prev]);
            } catch (err: any) {
              console.warn('Create updated invoice failed:', err);
            }
          }}
          onSendInvoiceWhatsApp={async (invoice, phone) => {
            try {
              const token = getStoredToken() || '';
              const pdfRes = await fetch(`${BACKEND}/invoices/${invoice.id}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (!pdfRes.ok) throw new Error('PDF fetch failed');
              const pdfBlob = await pdfRes.blob();

              const base64: string = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                reader.onerror = () => reject(new Error('Could not read the generated PDF.'));
                reader.readAsDataURL(pdfBlob);
              });

              const digits = phone.replace(/\D/g, '');
              const fullPhone = digits; // PhoneInput already includes country code
 
              const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  phone: fullPhone,
                  base64,
                  filename: `Invoice_${invoice.invoiceNumber}.pdf`,
                  caption: `📄 Invoice ${invoice.invoiceNumber}\n— CloudAIRambo`,
                }),
              });
              const data = await res.json().catch(() => ({}));
             
              if (!res.ok || data?.success === false) {
                throw new Error(data?.error || data?.detail || 'WhatsApp send failed');
              }
            } catch (err) {
              console.error('WhatsApp PDF send failed:', err);
              throw err;
            }
          }}
        />
      )}

    
{currentTab === 'pos' && (() => {
  const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  if (!p.canAccessPOS) return <UpgradeGate feature="POS Billing" />;
  return <POSView
    authUser={authUser}
    outletId={selectedOutletFilter}
    onExportPDF={handleExportPDF}
    moduleStatuses={moduleStatuses}
    accounts={accounts}
    bills={bills}
    setBills={setBills}
    customTemplates={customTemplates}
    activeCurrency={effectiveCurrency}
    onAddLog={handlePortalAddLog}
    onAddAccount={handleAddAccount}
    discountRules={discountRules}
    onUpdateAccount={handleUpdateAccount}
    initialCart={pendingTableOrder || undefined}
    onPostSave={(invoice) => {
      if (pendingTableOrder?.tableId) {
        clearTableOrderInStorage(pendingTableOrder.tableId);
      }
      setPendingTableOrder(null);
  // POS handles its own print/toast — no modal needed
}}
    onSendInvoiceWhatsApp={async (invoice, phone) => {
      const blob = await getInvoicePdfBlob(invoice);
      if (!blob) throw new Error('Could not prepare the receipt PDF.');

      const base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read the generated PDF.'));
        reader.readAsDataURL(blob as Blob);
      });

      const digits = phone.replace(/\D/g, '');
      const fullPhone = digits;
      const token = getStoredToken() || '';
  
      const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          phone: fullPhone,
          base64,
          filename: `Receipt_${invoice.invoiceNumber}.pdf`,
          caption: `🧾 Receipt ${invoice.invoiceNumber}\n— CloudAIRambo`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || data?.detail || `WhatsApp send failed (${res.status})`);
      }
    }}
  />;
})()}
      {currentTab === 'accounts' && (
        <AccountsView
  userRole={authUser?.role}
  accounts={accounts}
  authUserPerms={authUser?.permissions}
  moduleStatuses={moduleStatuses}
  authUserPlan={(authUser as any)?.plan}
  authUserPlanTier={(authUser as any)?.plan_tier}
  authUserPlanStatus={(authUser as any)?.plan_status}
          onAddAccount={handleAddAccount}
          onUpdateAccount={handleUpdateAccount}
          onDeleteAccount={handleDeleteAccount}
          onUseAccountInInvoice={handleUseAccountInCreator}
          googleToken={googleToken}
          onBackupAccountsToDrive={handleBackupAccountsToDrive}
          discountRules={discountRules}
          onAddDiscountRule={handleAddDiscountRule}
          onUpdateDiscountRule={handleUpdateDiscountRule}
          onDeleteDiscountRule={handleDeleteDiscountRule}
          onSendPromoCampaign={handleSendPromoCampaign}
        />
      )}

      {currentTab === 'tables' && (() => {
        const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
        return (
          <TableLayoutView
            authUser={authUser}
            outletId={selectedOutletFilter}
            sym={getCurrencyDisplay(effectiveCurrency, currencyDisplayMode)}

            canAccessKitchenDashboard={p.canAccessKitchenDashboard}
            canEditTableLayout={p.canEditTableLayout}
            canManageFloors={p.canManageFloors}
            onCheckout={(payload) => {
              setPendingTableOrder({ tableId: payload.tableId, tableNumber: payload.tableNumber, items: payload.items });
              setCurrentTab('pos');
            }}
          />
        );
      })()}

     {currentTab === 'logs' && (
  <LogsView
  userRole={authUser?.role}
  authUserPerms={authUser?.permissions}
  outletId={selectedOutletFilter}
/>
      )} 
  {currentTab === 'products' && (
  <div style={{ flex: 1, overflowY: 'auto' }}>
    <ProductCatalogView 
      userRole={authUser?.role}
      authUserPerms={authUser?.permissions}
      authUserPlan={(authUser as any)?.plan}
      outletId={selectedOutletFilter}
    />
  </div>
)}
{currentTab === 'profile' && (() => {
  const isOwnerOrAdmin = authUser?.role === 'owner' || authUser?.role === 'admin' || authUser?.role === 'individual';
  if (!isOwnerOrAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af', fontSize: 14 }}>
        You don't have permission to access Profile.
      </div>
    );
  }
  return (
    <ProfileView
      authUser={authUser}
      googleUser={googleUser}
      googleToken={googleToken}
      activeCurrency={effectiveCurrency}
      storageOption={storageOption}
      outletId={selectedOutletFilter}
      onCurrencyChange={handleCurrencyChange}
      onStorageChange={(mode) => setStorageOption(mode)}
      onLogin={() => setShowAuthModal(true)}
      onLogout={() => { clearSession(); window.location.href = '/'; }}
      onGoogleLogin={handleGoogleLogin}
      onGoogleLogout={handleGoogleLogout}
      onShowAuth={() => setShowAuthModal(true)}
      onUpgrade={(updatedUser) => setAuthUser(updatedUser)}
      onShowPlans={() => setShowPlans(true)}
      moduleStatuses={moduleStatuses}
      moduleTrialEndDates={moduleTrialEndDates}
      modulePeriodEndDates={modulePeriodEndDates}
    />
  );
})()}
{currentTab === 'ledger' && (() => {
  const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  if (!p.canAccessLedger) return <UpgradeGate feature="Ledger (Udhar Khata)" />;
 return <LedgerView authUser={authUser} defaultTab="ledger" moduleStatuses={moduleStatuses} activeCurrency={effectiveCurrency} accounts={accounts} onAddAccount={handleAddAccount} onUpdateAccount={handleUpdateAccount} outletId={selectedOutletFilter} />;
})()}

{currentTab === 'clinic' && (() => {
  const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  if (!p.canAccessClinic) return <UpgradeGate feature="Clinic / Queue" />;
  return <ClinicView authUser={authUser} moduleStatuses={moduleStatuses} accounts={accounts} onAddAccount={handleAddAccount} onUpdateAccount={handleUpdateAccount} outletId={selectedOutletFilter} />;
})()}
{currentTab === 'employees' && (() => {
  const p = getPermissions(authUser?.role, authUser?.permissions, authUser?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  if (!p.canAccessEmployees) return (
    <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af', fontSize: 14 }}>
      You don't have permission to access Employees.
    </div>
  );
 return <EmployeesView authUser={authUser} moduleStatuses={moduleStatuses} outletId={selectedOutletFilter} />;
})()}
{currentTab === 'inventory' && (() => {
  const p = getPermissions(authUser?.role, authUser?.permissions, authUser?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
  if (!p.canAccessInventory) return (
    <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af', fontSize: 14 }}>
      You don't have permission to access Inventory.
    </div>
  );
  return <InventoryView authUser={authUser} outletId={selectedOutletFilter} moduleStatuses={moduleStatuses} />;
})()}
{currentTab === 'iam' && (() => {
  const hasModuleAccess = Object.values(moduleStatuses || {}).some(s => s === 'active' || s === 'trial');
if (!hasModuleAccess) return <UpgradeGate feature="Team / IAM" />;
return <IAMView authUser={authUser} moduleStatuses={moduleStatuses} moduleOutletCounts={moduleOutletCounts} moduleSeatLimits={moduleSeatLimits} suiteSeatLimit={suiteSeatLimit} />;
})()}
{currentTab === 'fitness' && (() => {
const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
if (!p.canAccessFitness) return <UpgradeGate feature="Gym / Fitness" />;
return <LedgerView authUser={authUser} defaultTab="gym" moduleStatuses={moduleStatuses} activeCurrency={effectiveCurrency} accounts={accounts} onAddAccount={handleAddAccount} onUpdateAccount={handleUpdateAccount} outletId={selectedOutletFilter} />;
})()}
{currentTab === 'food' && (() => {
const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
if (!p.canAccessFood) return <UpgradeGate feature="Food & Dining" />;
return <LedgerView authUser={authUser} defaultTab="canteen" moduleStatuses={moduleStatuses} activeCurrency={effectiveCurrency} accounts={accounts} onAddAccount={handleAddAccount} onUpdateAccount={handleUpdateAccount} outletId={selectedOutletFilter} />;
})()}
{currentTab === 'dashboard' && (() => {
const p = getPermissions(authUser?.role, authUser?.permissions, (authUser as any as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
if (!p.canAccessLogs) return <UpgradeGate feature="Analytics Dashboard" />;
return <SuperAdminDashboard authUser={authUser} />;
})()}
{currentTab === 'batch_invoice' && (
  <div style={{ flex: 1, overflowY: 'auto' }}>
    <BatchInvoiceGenerator
      customTemplates={customTemplates}
      activeCurrency={effectiveCurrency}
      onAddLog={handlePortalAddLog}
      onClose={() => setCurrentTab('quick_invoice')}
      outletId={selectedOutletFilter}
    />
  </div>
)}
{currentTab === 'quick_invoice' && (
  <QuickInvoiceView
    authUser={authUser}
    outletId={selectedOutletFilter}
    moduleStatuses={moduleStatuses}
    accounts={accounts}
    bills={bills}
    setBills={setBills}
    customTemplates={customTemplates}
    activeCurrency={effectiveCurrency}
    onAddLog={handlePortalAddLog}
    onAddAccount={handleAddAccount}
    discountRules={discountRules}
    onUpdateAccount={handleUpdateAccount}
    defaultTemplateId={quickInvoiceSelectedTemplate}
    onExportPDF={handleExportPDFViaSharedRenderer}
    editInvoice={editingQuickInvoice}
    onUpdateInvoice={handleUpdateInvoice}
    onEditClose={() => setEditingQuickInvoice(null)}
    onRemoveTemplate={(id) => {
      setCustomTemplates(prev => prev.filter(t => t.id !== id));
    }}
    onPostSave={(invoice) => {
      setPostSaveModal({
        invoice,
        clientEmail: invoice.clientEmail || '',
        clientPhone: (invoice as any).clientPhone || '',
      });
    }}
    onSendInvoiceWhatsApp={async (invoice: InvoiceRecord, phone: string) => {
      const blob = await getInvoicePdfBlob(invoice);
      if (!blob) throw new Error('Could not prepare the invoice PDF.');

      const base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read the generated PDF.'));
        reader.readAsDataURL(blob as Blob);
      });

      const digits = phone.replace(/\D/g, '');
      const fullPhone = digits;
      const token = getStoredToken() || '';
      // Recompute from invoice.elements — same source of truth the PDF
      // itself was just built from — instead of the stored invoice.amount
      // field, which can drift out of sync with the elements and, even
      // when it doesn't, is a raw unformatted float (floating-point
      // residue like 76.06700000000001) with no toFixed applied.
      const _tableEl = (invoice.elements || []).find((el: any) => el.type === 'table');
      let _displayAmount = invoice.amount || 0;
      if (_tableEl) {
        let _subtotal = 0;
        const _lineBases: { base: number; taxRate: number }[] = [];
        (_tableEl.rows || []).forEach((r: any) => {
          const qty = parseFloat(String(r.values.qty)) || 0;
          const rate = parseFloat(String(r.values.rate)) || 0;
          const lineTotal = qty * rate;
          const taxRate = parseFloat(String(r.values.taxRate ?? r.values.tax_rate ?? 0)) || 0;
          const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
          const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
          _lineBases.push({ base, taxRate });
          _subtotal += base;
        });
        const _discount = ((_tableEl as any).discounts || []).reduce((s: number, d: any) => s + _subtotal * (d.rate / 100), 0);
        const _discountRatio = _subtotal > 0 ? Math.min(1, _discount / _subtotal) : 0;
        const _productTax = _lineBases.reduce((s, lb) => s + (lb.base * (1 - _discountRatio)) * (lb.taxRate / 100), 0);
        const _taxableValue = _subtotal - _discount;
        const _tax = ((_tableEl as any).taxes || []).reduce((s: number, t: any) => s + _taxableValue * (t.rate / 100), 0);
        const _shipping = _tableEl.shippingCost || 0;
        _displayAmount = _taxableValue + _productTax + _tax + _shipping;
      }
      const _sym = getCurrencyDisplay(invoice.currency || activeCurrency, currencyDisplayMode);
  
      const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          phone: fullPhone,
          base64,
          filename: `Invoice_${invoice.invoiceNumber}.pdf`,
          caption: `📄 Invoice ${invoice.invoiceNumber}\nAmount: ${_sym}${_displayAmount.toFixed(2)}\n\n— CloudAIRambo`,
        }),
      });
      const data = await res.json().catch(() => ({}));
   
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || data?.detail || `WhatsApp send failed (${res.status})`);
      }
    }}
  />
)}
      {currentTab === 'generator' && (
        <div className="flex-1 overflow-hidden flex flex-col relative">
          
          {/* HEADER BAR */}
     {editingBillId && (
  <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between shrink-0">
    <div className="flex items-center gap-2 text-amber-800 text-xs font-bold">
      <span>✏️ Editing saved invoice — changes will update the existing record when you save.</span>
    </div>
    <button
      onClick={() => {
        setEditingBillId(null);
        const elementsClone = JSON.parse(JSON.stringify(getFreshPresets()[0].elements));
        setElements(elementsClone);
        setPageSize('A4');
        saveToHistory(elementsClone, 'A4');
      }}
      className="text-xs font-bold text-amber-700 hover:text-amber-900 underline cursor-pointer"
    >
      Cancel Edit
    </button>
  </div>
)}
     {(!isAnyInvoiceEmbed || isInvoiceHeaderEmbed) && (
<GeneratorHeader
  userRole={authUser?.role}
  authUser={authUser}
  authUserPerms={authUser?.permissions}
  moduleStatuses={moduleStatuses}  
  onOpenScanner={() => setShowScannerModal(true)}
  gridSnapping={gridSnapping}
  setGridSnapping={setGridSnapping}
  showGridLines={showGridLines}
  setShowGridLines={setShowGridLines}
  historyPointer={historyPointer}
  historyLength={history.length}
  handleUndo={handleUndo}
  handleRedo={handleRedo}
  elements={elements}
  handleAutoAlign={handleAutoAlign}
  isTranslating={isTranslating}
  bilingualMode={bilingualMode}
          translateTargetLang={translateTargetLang}
  setBilingualMode={setBilingualMode}
  bilingualDirection={bilingualDirection}
  setBilingualDirection={setBilingualDirection}
  handleTranslateToArabic={handleTranslateToArabic}
  handleRestackBilingual={() => {
    const sorted = [...elements].sort((a, b) => a.y - b.y);
    const restacked = [...sorted];
    for (let i = 1; i < restacked.length; i++) {
      const prev = restacked[i - 1];
      const curr = restacked[i];
      if (curr.y < prev.y + prev.height + 10)
        restacked[i] = { ...curr, y: prev.y + prev.height + 10 };
    }
    const final = elements.map(el => restacked.find(r => r.id === el.id) || el);
    setElements(final);
    saveToHistory(final, pageSize);
  }}
  handleGenerateShareLink={handleGenerateShareLink}

  handleExportPDF={() => handleExportPDF()}
  handleSaveActiveWorkspaceAsBill={handleSaveActiveWorkspaceAsBill}
  onSaveAsTemplate={handleSaveAsTemplate}
  onResetCanvas={() => {
  if (window.confirm('Reset canvas? All elements will be cleared.')) {
    isResettingRef.current = true;
    localStorage.removeItem('cloudairambo_canvas_draft');
    const defaultSize = (localStorage.getItem('cloudairambo_default_page_size') as PageSizePreset) || 'A4';
    const fresh = JSON.parse(JSON.stringify(getFreshPresets()[0].elements));
    setElements(fresh);
    setPageSize(defaultSize);
    setSelectedElementId(null);
    setInlineEditingId(null);
    setBilingualMode(false);
    setBilingualDirection('en-ar');
    setTranslateTargetLang('ar');
    setGridSnapping(false);
    setShowGridLines(false);
    const serialized = JSON.stringify({ elements: fresh, pageSize: defaultSize });
    setHistory([serialized]);
    setHistoryPointer(0);
    setTimeout(() => {
  isResettingRef.current = false;
  localStorage.setItem('cloudairambo_canvas_draft', JSON.stringify({ elements: fresh, pageSize: defaultSize }));
}, 100);
  }
}}
 

handleSplitLayout={() => {
    const halfW = Math.floor(pageConfig.width / 2) - 10;
    const next = elements.map(el => {
      if (el.type === 'text' && el.arabicContent) {
        return {
          ...el,
          width: halfW,
          x: Math.min(el.x, halfW - 10),
        };
      }
      return el;
    });
    setElements(next);
    saveToHistory(next, pageSize);
    setBilingualMode(true);
    setBilingualDirection('en-ar');
  }}
  setIsPrintPreviewOpen={setIsPrintPreviewOpen}
  setPrintSelectedElementId={setPrintSelectedElementId}
  setTranslateTargetLang={setTranslateTargetLang}
/>
)}
{!isInvoiceHeaderEmbed && (
<>
<style>{`
  .mobile-fab { display: none; }
  @media (max-width: 768px) {
    .mobile-panel-hidden { display: none !important; }
    .mobile-panel-overlay {
      position: fixed !important;
      top: 65px; left: 0; right: 0; bottom: 0;
      z-index: 200;
      width: 100vw !important;
      display: flex !important;
      flex-direction: column;
      overflow-y: auto !important;
      background: #fff;
    }
    .mobile-panel-overlay > aside {
      height: 100% !important;
      width: 100% !important;
      overflow-y: auto !important;
    }
    .mobile-fab { display: flex !important; }
  }
`}</style>
<div className="flex-1 flex overflow-hidden">
      {/* WORKSPACE & SIDEBAR MAIN DIVIDER */}
   
     {!isInvoiceCanvasEmbed && !isInvoiceRightEmbed && (
<div
  className={
    isInvoiceLeftEmbed
      ? 'flex w-full h-full overflow-y-auto bg-white'
      : mobileLeftOpen
        ? 'mobile-panel-overlay'
        : 'mobile-panel-hidden md:!flex md:!static md:!w-auto'
  }
>
{mobileLeftOpen && (
  <button onClick={() => setMobileLeftOpen(false)} className="md:hidden"
    style={{ position: 'fixed', top: 75, right: 12, zIndex: 250, width: 36, height: 36, borderRadius: '50%', background: '#111827', color: '#fff', border: 'none', fontSize: 16 }}>
    ✕
  </button>
)}
     <LeftSidebar
  activeTab={activeTab}
  setActiveTab={setActiveTab}
  openPanels={openPanels}
  togglePanel={togglePanel}
  corporateColor={corporateColor}
  setCorporateColor={setCorporateColor}
  applyThemeColor={applyThemeColor}
  applyThemeColorDebounced={applyThemeColorDebounced}
  activeMarketplaceProfile={activeMarketplaceProfile}
  setActiveMarketplaceProfile={setActiveMarketplaceProfile}
  marketplaceProfiles={marketplaceProfiles}
  updateMarketplaceProfile={updateMarketplaceProfile}
  applyMarketplaceBranding={applyMarketplaceBranding}
  setSelectedTemplateCategory={setSelectedTemplateCategory}
  selectedTemplateCategory={selectedTemplateCategory}
  customTemplates={customTemplates}
  setCustomTemplates={setCustomTemplates}
  loadTemplate={loadTemplate}
  handleLoadTemplateObj={handleLoadTemplateObj}
  industrySearchQuery={industrySearchQuery}
  setIndustrySearchQuery={setIndustrySearchQuery}
  selectedIndustryGroup={selectedIndustryGroup}
  setSelectedIndustryGroup={setSelectedIndustryGroup}
  filtered180Niches={filtered180Niches}
  generateIndustryTemplate={generateIndustryTemplate}
  setActiveIndustryGroup={setActiveIndustryGroup}
  setActiveIndustryName={setActiveIndustryName}
  activeIndustryName={activeIndustryName}
  handleAddElement={handleAddElement}
  fileInputRef={fileInputRef}
  handleImageUpload={handleImageUpload}
  isDrawingMode={isDrawingMode}
  setIsDrawingMode={setIsDrawingMode}
  setSelectedElementId={setSelectedElementId}
  currentPenColor={currentPenColor}
  setCurrentPenColor={setCurrentPenColor}
  currentPenWidth={currentPenWidth}
  setCurrentPenWidth={setCurrentPenWidth}
  pageSize={pageSize}
  setPageSize={setPageSize}
  pageConfig={pageConfig}
  pageBehavior={pageBehavior}
  setPageBehavior={setPageBehavior}
  gridSnapping={gridSnapping}
  computedHeight={computedHeight}
  elements={elements}
  setElements={setElements}
  saveToHistory={saveToHistory}
  setZoom={setZoom}
  activeCurrency={effectiveCurrency}
  currencyDisplayMode={currencyDisplayMode}
  handleCurrencyChange={handleCurrencyChange}
  handleCurrencyDisplayModeChange={handleCurrencyDisplayModeChange}
/>
</div>
)}
 {!isInvoiceLeftEmbed && !isInvoiceRightEmbed && (
 <CanvasWorkspace
          pageConfig={pageConfig}
          computedHeight={computedHeight}
          pageCount={pageCount}
          pageBehavior={pageBehavior}
          setPageBehavior={setPageBehavior}
          zoom={zoom}
          setZoom={setZoom}
          showGridLines={showGridLines}
          elements={elements}
          selectedElementId={selectedElementId}
          inlineEditingId={inlineEditingId}
          inlineEditText={inlineEditText}
          setInlineEditText={setInlineEditText}
          lockedElementIds={lockedElementIds}
          dragState={dragState}
          qrDataUrls={qrDataUrls}
          isDrawingMode={isDrawingMode}
          isDrawingActive={isDrawingActive}
          currentPathPoints={currentPathPoints}
          currentPenColor={currentPenColor}
          currentPenWidth={currentPenWidth}
          bilingualMode={bilingualMode}
          bilingualDirection={bilingualDirection}
          translateTargetLang={translateTargetLang}
          activeCurrency={effectiveCurrency}
          currencyDisplayMode={currencyDisplayMode}
          computeTableTotals={computeTableTotals}
          formatSerialNumber={formatSerialNumber}
          canvasRef={canvasRef}
          handleMouseDown={handleMouseDown}
          handleDeleteElement={handleDeleteElement}
          handleInlineEditStart={handleInlineEditStart}
          handleElementDoubleClick={handleElementDoubleClick}
          handleInlineEditSave={handleInlineEditSave}
          commitCurrentState={commitCurrentState}
          updateElementProps={updateElementProps}
          handleCanvasMouseDown={handleCanvasMouseDown}
          handleCanvasMouseMove={handleCanvasMouseMove}
          handleCanvasMouseUp={handleCanvasMouseUp}
          onBackgroundClick={() => {
            setSelectedElementId(null);
            setInlineEditingId(null);
            setRightSidebarTab('layers');
          }}
        />
)}
{!isAnyInvoiceEmbed && (
<>
       <div className="mobile-fab" style={{ position: 'fixed', bottom: 90, left: 12, zIndex: 150, flexDirection: 'column', gap: 8 }}>
  <button
    onClick={() => setMobileLeftOpen(true)}
    style={{ width: 44, height: 44, borderRadius: '50%', background: '#4f46e5', color: '#fff', border: 'none', fontSize: 18, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
  >
    ➕
  </button>
</div>
<div className="mobile-fab" style={{ position: 'fixed', bottom: 90, right: 12, zIndex: 150, flexDirection: 'column', gap: 8 }}>
  <button
    onClick={() => setMobileRightOpen(true)}
    style={{ width: 44, height: 44, borderRadius: '50%', background: '#4f46e5', color: '#fff', border: 'none', fontSize: 18, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
  >
    ⚙️
  </button>
</div>


</>
)}

    {!isInvoiceCanvasEmbed && !isInvoiceLeftEmbed && (
<div
  className={
    isInvoiceRightEmbed
      ? 'flex w-full h-full overflow-y-auto bg-white'
      : mobileRightOpen
        ? 'mobile-panel-overlay'
        : 'mobile-panel-hidden md:!flex md:!static md:!w-auto'
  }
>
{mobileRightOpen && (
  <button onClick={() => setMobileRightOpen(false)} className="md:hidden"
    style={{ position: 'fixed', top: 75, right: 12, zIndex: 250, width: 36, height: 36, borderRadius: '50%', background: '#111827', color: '#fff', border: 'none', fontSize: 16 }}>
    ✕
  </button>
)}
<RightSidebar
  rightSidebarTab={rightSidebarTab}
  setRightSidebarTab={setRightSidebarTab}
  elements={elements}
  selectedElementId={selectedElementId}
  setSelectedElementId={setSelectedElementId}
  selectedElement={selectedElement}
  lockedElementIds={lockedElementIds}
  setLockedElementIds={setLockedElementIds}
  handleDeleteElement={handleDeleteElement}
  handleDuplicateElement={handleDuplicateElement}
  handleZIndex={handleZIndex}
  updateElementProps={updateElementProps}
  commitCurrentState={commitCurrentState}
  saveToHistory={saveToHistory}
  pageSize={pageSize}
  inlineEditingId={inlineEditingId}
  handleInlineEditSave={handleInlineEditSave}
  triggerIncrementSerial={triggerIncrementSerial}
  formatSerialNumber={formatSerialNumber}
  authUserId={authUser?.id || 'default'}
  accounts={accounts}
  rightClientSearch={rightClientSearch}
  setRightClientSearch={setRightClientSearch}
  handleUseAccountInCreator={handleUseAccountInCreator}
  logs={logs}
  computeTableTotals={computeTableTotals}
  activeIndustryGroup={activeIndustryGroup}
  activeIndustryName={activeIndustryName}
  getFinancialSuggestions={getFinancialSuggestions}
  setLogs={setLogs}
  addLogItem={addLogItem}
  draggedColumnIdx={draggedColumnIdx}
  setDraggedColumnIdx={setDraggedColumnIdx}
  draggedOverColumnIdx={draggedOverColumnIdx}
  setDraggedOverColumnIdx={setDraggedOverColumnIdx}
  bilingualMode={bilingualMode}
/>
</div>
)}
</div>  {/* closes flex-1 flex overflow-hidden */}
</>
)}

      <PrintPreviewModal
        isPrintPreviewOpen={isPrintPreviewOpen}
        setIsPrintPreviewOpen={setIsPrintPreviewOpen}
        elements={elements}
        pageConfig={pageConfig}
        computedHeight={computedHeight}
        pageBehavior={pageBehavior}
        bilingualMode={bilingualMode}
        translateTargetLang={translateTargetLang}
        activeCurrency={effectiveCurrency}
        currencyDisplayMode={currencyDisplayMode}
        hiddenElementIds={hiddenElementIds}
        setHiddenElementIds={setHiddenElementIds}
        printSelectedElementId={printSelectedElementId}
        setPrintSelectedElementId={setPrintSelectedElementId}
        qrDataUrls={qrDataUrls}
        handleExportPDF={handleExportPDF}
        handleZIndex={handleZIndex}
        computeTableTotals={computeTableTotals}
        formatSerialNumber={formatSerialNumber}
        isSerialNoColumn={isSerialNoColumn}
      />
      {showScannerModal && (
  <BarcodeScannerModal
    onClose={() => setShowScannerModal(false)}
    onProductScanned={(product) => {
      handleProductScanned(product);
    }}
  />
  
)}
    </div>   
  )}
{authUser && !isAnyInvoiceEmbed && (() => {
  // Same friendly names used everywhere else (SubscriptionPlansView's
  // MODULE_DEFS) — this pill previously title-cased the raw internal id
  // (e.g. "ledger" -> "Ledger") instead of looking up the real module
  // name, so it showed "Ledger" while every other screen said "Credit Book".
  const MODULE_DISPLAY_NAMES: Record<string, string> = {
    ledger: 'Credit Book',
    employees: 'Staff & Attendance',
    queue: 'Queue / Tokens',
    gym: 'Fitness Center',
    canteen: 'Meal Plans & Dining',
    pos: 'POS / Billing',
  };

  const plan = (authUser as any)?.plan || 'basic';
  const planStatus = (authUser as any)?.plan_status || 'free';
  const hasActiveModule = Object.values(moduleStatuses || {}).some(s => s === 'active' || s === 'trial');
  const lockedModuleIds = Object.entries(moduleStatuses || {}).filter(([, s]) => s === 'locked').map(([id]) => id);
  const suitePlanExpired = (planStatus === 'expired' || planStatus === 'cancelled') && plan !== 'basic';

  // Nothing wrong — a paid/trial module is active and nothing is locked or
  // expired. Don't show a confirmation pill for something that's just
  // working correctly; only surface this when the person needs to act.
  const nothingToWarnAbout = lockedModuleIds.length === 0 && !suitePlanExpired && (hasActiveModule || plan === 'basic');
  if (nothingToWarnAbout) return null;

  const label = lockedModuleIds.length > 0
    ? `🔒 ${lockedModuleIds.map(id => MODULE_DISPLAY_NAMES[id] || (id.charAt(0).toUpperCase() + id.slice(1))).join(', ')} · Renew`
    : planStatus === 'expired'
      ? `🔒 ${plan.toUpperCase()} · Expired — Renew`
      : `🔒 ${plan.toUpperCase()} · Cancelled — Resubscribe`;

  return (
    <div style={{
      position: 'fixed', bottom: 16, right: 16, zIndex: 999,
      background: '#fef2f2',
      color: '#dc2626',
      border: '1px solid #fecaca',
      borderRadius: 99, padding: '6px 14px', fontSize: 11, fontWeight: 800,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      display: 'flex', alignItems: 'center', gap: 6,
      cursor: 'pointer', userSelect: 'none' as const,
    }}
    onClick={() => setShowPlans(true)}
    >
      {label}
    </div>
  );
})()}

{showWelcomeTrial && (
  <WelcomeTrialModal
    onStart={async () => {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const res = await fetch(`${BACKEND}/billing/modules/pos/start-trial`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) throw new Error('Trial start failed');

      // Refresh auth user + module statuses so the whole app reflects trial access
      const meRes = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } });
      if (meRes.ok) setAuthUser(await meRes.json());

      const modRes = await fetch(`${BACKEND}/billing/modules/status`, { headers: { Authorization: `Bearer ${jwt}` } });
      const data = await modRes.json();
      if (data?.statuses) setModuleStatuses(data.statuses);
      if (data?.trial_end_dates) setModuleTrialEndDates(data.trial_end_dates);
      if (data?.period_end_dates) setModulePeriodEndDates(data.period_end_dates);
      if (data?.outlet_counts) setModuleOutletCounts(data.outlet_counts);
      if (data?.suite) {
        setSuiteSeatLimit((data.suite.seats_included ?? 0) + (data.suite.seat_count ?? 0));
      }
    }}
    onDone={() => setShowWelcomeTrial(false)}
  />
)}
{showPlans && (
  <SubscriptionPlansView
    currentPlan={(authUser as any)?.plan || 'basic'}
    currentUserData={authUser}
    onSilentRefresh={async () => {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      if (!jwt) return;
      try {
        const meRes = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } });
        if (meRes.ok) setAuthUser(await meRes.json());
        const modRes = await fetch(`${BACKEND}/billing/modules/status`, { headers: { Authorization: `Bearer ${jwt}` } });
        const data = await modRes.json();
        if (data?.statuses) setModuleStatuses(data.statuses);
        if (data?.trial_end_dates) setModuleTrialEndDates(data.trial_end_dates);
        if (data?.period_end_dates) setModulePeriodEndDates(data.period_end_dates);
        if (data?.outlet_counts) setModuleOutletCounts(data.outlet_counts);
        if (data?.seats_included && data?.seat_counts) {
          const limits: Record<string, number> = {};
          for (const id of Object.keys(data.seats_included)) {
            limits[id] = (data.seats_included[id] ?? 0) + (data.seat_counts[id] ?? 0);
          }
          setModuleSeatLimits(limits);
        }
        if (data?.suite) {
          setSuiteSeatLimit((data.suite.seats_included ?? 0) + (data.suite.seat_count ?? 0));
        } else {
          setSuiteSeatLimit(null);
        }
      } catch (e) {
        console.warn('Silent refresh failed:', e);
      }
    }}
    forcedReason={
      planExpiredLock
        ? 'Your free trial has ended. Choose a plan to continue using Pro/Ultra features.'
        : justFinishedOnboarding
          ? '🎉 Your Free Trial Starts Now! No credit card required — enjoy full access to every feature for 7 days.'
          : undefined
    }
    onSelectPlan={async (plan, cycle) => {
      setShowPlans(false);
      setJustFinishedOnboarding(false);
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      try {
        const res = await fetch(`${BACKEND}/auth/plan/upgrade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ plan, plan_tier: 'individual', billing_cycle: cycle }),
        });
        if (!res.ok) throw new Error('Upgrade failed');

        const meRes = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } });
const me = await meRes.json();
setAuthUser(me);
setPlanExpiredLock(false);

// Refresh module statuses after plan change
fetch(`${BACKEND}/billing/modules/status`, {
  headers: { Authorization: `Bearer ${jwt}` },
})
  .then(r => r.json())
  .then(data => {
    if (data?.statuses) setModuleStatuses(data.statuses);
    if (data?.trial_end_dates) setModuleTrialEndDates(data.trial_end_dates);
    if (data?.period_end_dates) setModulePeriodEndDates(data.period_end_dates);
    if (data?.outlet_counts) setModuleOutletCounts(data.outlet_counts);
    if (data?.seats_included && data?.seat_counts) {
      const limits: Record<string, number> = {};
      for (const id of Object.keys(data.seats_included)) {
        limits[id] = (data.seats_included[id] ?? 0) + (data.seat_counts[id] ?? 0);
      }
      setModuleSeatLimits(limits);
    }
    if (data?.suite) {
      setSuiteSeatLimit((data.suite.seats_included ?? 0) + (data.suite.seat_count ?? 0));
    } else {
      setSuiteSeatLimit(null);
    }
  })
  .catch(() => {});

alert(`✅ Upgraded to ${plan} (${cycle})! Features unlocked. Re-login if anything looks stale.`);
      } catch (e) {
        alert('Upgrade failed. Try again.');
      }
    }}
    onClose={() => {
      setJustFinishedOnboarding(false);
      if (planExpiredLock) {
        alert('Your trial has ended. Please pick a plan to continue, or you\'ll stay on Basic.');
        setShowPlans(false);
        setPlanExpiredLock(false); // let them dismiss to Basic — they're already auto-downgraded
        return;
      }
      setShowPlans(false);
    }}
  />
)}


  {postSaveModal && (() => {
  const profile = loadCompanyProfile();
  return (
    <>
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 250, background: 'rgba(15,23,42,0.5)', animation: 'fadeIn 0.2s ease-out' }}
    />
    <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 251, width: 480, maxWidth: 'calc(100vw - 32px)' }}>
      <div className="relative bg-white rounded-3xl shadow-2xl p-8 space-y-6 border border-slate-100" onClick={e => e.stopPropagation()} style={{ animation: 'popIn 0.25s cubic-bezier(0.16,1,0.3,1)' }}>
        <style>{`
          @keyframes popIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
        {/* Header */}
        <div className="text-center pt-1">
          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold px-2.5 py-1 rounded-full mb-2">
            <CheckCircle size={11} /> Invoice Saved
          </div>
          <div className="text-2xl font-black tracking-tight leading-none bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
            🎉 {postSaveModal.invoice.invoiceNumber}
          </div>
        </div>

        {/* Inputs — always editable now; previously hidden entirely unless
            the org's notifyEmail/notifyWhatsApp company-profile flags were
            on, which is why this modal often showed nothing but "Skip". */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              Client Email
            </label>
            <input
              type="email"
              value={postSaveModal.clientEmail}
              onChange={(e) => setPostSaveModal(prev => prev ? { ...prev, clientEmail: e.target.value } : null)}
              placeholder="client@email.com"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-wider text-slate-500">
              Client WhatsApp Number
            </label>
            <input
              type="tel"
              value={postSaveModal.clientPhone}
              onChange={(e) => setPostSaveModal(prev => prev ? { ...prev, clientPhone: e.target.value } : null)}
              placeholder="+91 98765 43210"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </div>
        
        </div>

        {/* Auto-download notice — the PDF is already being generated and
            saved automatically by handleGenerate right after save. */}
        <div className="text-[10px] text-slate-400 text-center -mt-1">
          Your invoice PDF is downloading automatically...
        </div>

                {/* Actions */}
        <div className="space-y-2 pt-1">
          {(postSaveModal.clientPhone.replace(/\D/g, '').length >= 7 || postSaveModal.clientEmail.includes('@')) && (
            <div className="flex gap-2">
              {postSaveModal.clientPhone.replace(/\D/g, '').length >= 7 && (
                <button
                  onClick={async () => {
                    const digits = postSaveModal.clientPhone.replace(/\D/g, '');
                    if (digits.length < 7) return;
                    const blob = await getInvoicePdfBlob(postSaveModal.invoice);
                    if (!blob) return;
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                      const base64 = (reader.result as string).split(',')[1];
                      const fullPhone = digits.length === 10 ? `91${digits}` : digits;
                      const token = getStoredToken() || '';
                      const res = await fetch(`${BACKEND}/whatsapp/send-pdf`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          phone: fullPhone,
                          base64,
                          filename: `Invoice_${postSaveModal.invoice.invoiceNumber}.pdf`,
                          caption: `📄 Invoice ${postSaveModal.invoice.invoiceNumber} for ${postSaveModal.invoice.clientName}\n\n— CloudAIRambo`,
                        }),
                      });
                      const data = await res.json().catch(() => ({}));
                      if (!res.ok || data?.success === false) {
                        alert(`WhatsApp send failed: ${data?.error || data?.detail || res.status}`);
                      }
                      setPostSaveModal(null);
                    };
                    reader.readAsDataURL(blob as Blob);
                  }}
                  className="flex-1 h-11 text-[13px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Send on WhatsApp
                </button>
              )}
              {postSaveModal.clientEmail.includes('@') && (
                <button
                  onClick={async () => {
                    try {
                      const blob = await getInvoicePdfBlob(postSaveModal.invoice);
                      if (!blob) throw new Error('PDF build failed');
                      const pdfBase64: string = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob as Blob);
                      });
                      const token = getStoredToken();
                      await fetch(`${BACKEND}/email/send-invoice`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify({
                          invoice_id: Number(postSaveModal.invoice.id),
                          to_email: postSaveModal.clientEmail,
                          attach_pdf: true,
                          pdf_base64: pdfBase64,
                          pdf_filename: `${postSaveModal.invoice.invoiceNumber}.pdf`,
                          message: loadCompanyProfile()?.emailMessage || '',
                        }),
                      });
                      alert(`Email sent to ${postSaveModal.clientEmail}!`);
                    } catch (e) {
                      alert('Email send failed.');
                    }
                    setPostSaveModal(null);
                  }}
                  className="flex-1 h-11 text-[13px] font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Email
                </button>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setShowQIPrinterPicker(true)}
              className="flex-1 h-10 text-[12px] font-bold bg-slate-800 hover:bg-slate-900 text-white rounded-xl cursor-pointer transition-all flex items-center justify-center gap-1"
            >
              <Printer size={13} /> Print <ChevronDown size={12} strokeWidth={2.5} />
            </button>

            <button
              onClick={() => {
                if (!postSaveModal) return;
                setEditingQuickInvoice(postSaveModal.invoice);
                setPostSaveModal(null);
              }}
              className="flex-1 h-10 text-[12px] font-bold text-amber-700 hover:bg-amber-100 rounded-xl border border-amber-200 bg-amber-50 cursor-pointer transition-all flex items-center justify-center gap-1"
            >
              <Plus size={13} /> Add Item
            </button>
          </div>

          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent('quick-invoice-reset'));
              setPostSaveModal(null);
            }}
            className="w-full h-10 text-[12px] font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl border border-slate-200 cursor-pointer transition-all"
          >
            Done
          </button>
        </div>

      </div>
    </div>
    </>
  );
})()}

{showQIPrinterPicker && postSaveModal && (
  <div onClick={() => setShowQIPrinterPicker(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: '24px 22px', maxWidth: 340, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Print Receipt</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>Choose a connection, or use the dialog instead.</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <button onClick={() => { setPostSaveUseUSB(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false'); }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 6px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700,
            background: !postSaveUseUSB ? '#eef2ff' : '#fff', borderColor: !postSaveUseUSB ? '#6366f1' : '#e5e7eb', color: !postSaveUseUSB ? '#4f46e5' : '#6b7280' }}>
          <span style={{ fontSize: 18 }}>📶</span> WiFi
        </button>
        <button onClick={() => { setPostSaveUseUSB(true); localStorage.setItem('cloudairambo_qi_usb_print', 'true'); }}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 6px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 11, fontWeight: 700,
            background: postSaveUseUSB ? '#eef2ff' : '#fff', borderColor: postSaveUseUSB ? '#6366f1' : '#e5e7eb', color: postSaveUseUSB ? '#4f46e5' : '#6b7280' }}>
          <span style={{ fontSize: 18 }}>🔌</span> USB
        </button>
      </div>

      {postSaveUseUSB && (
        <>
          <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
            {postSaveUsbConnected ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected'}
          </div>
          {!postSaveUsbConnected && (
            <button onClick={async () => { const ok = await connectUSBPrinter(); setPostSaveUsbConnected(ok); }}
              style={{ width: '100%', fontSize: 11, fontWeight: 700, padding: '8px 8px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', marginBottom: 10 }}>
              🔌 Connect USB Printer
            </button>
          )}
        </>
      )}

      <button onClick={async () => {
        setShowQIPrinterPicker(false);
        if (!postSaveModal) return;
        try {
          const printInvoice = {
            ...postSaveModal.invoice,
            elements: (postSaveModal.invoice.elements || []).map((el: any) => {
              if (el.type !== 'text') return el;
              return {
                ...el,
                textContent: (el.textContent || '').replace(/\{invoice_number\}/gi, postSaveModal.invoice.invoiceNumber || ''),
                arabicContent: (el.arabicContent || '').replace(/\{invoice_number\}/gi, postSaveModal.invoice.invoiceNumber || ''),
              };
            }),
          };
          if (postSaveUseUSB) {
            const printJob = await buildQuickInvoicePrintJob(printInvoice);
            const printed = await printReceiptUSB(printJob);
            if (printed) return;
            alert("Could not reach the USB printer — check it's connected, or switch to WiFi.");
            return;
          }
          const blob = await getInvoicePdfBlob(postSaveModal.invoice);
          if (!blob) return alert('Could not prepare the PDF for printing.');
          const url = URL.createObjectURL(blob);
          const iframe = document.createElement('iframe');
          iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
          iframe.src = url;
          document.body.appendChild(iframe);
          let printFired = false;
          const firePrint = () => {
            if (printFired) return;
            printFired = true;
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          };
          iframe.onload = () => setTimeout(firePrint, 300);
          setTimeout(firePrint, 1200);
          setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 30000);
        } catch (err) {
          console.error('[Invoice Print] failed:', err);
          alert('Printing failed. Please check the printer connection.');
        }
      }}
        style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', marginBottom: 8 }}>
        🖨 Print
      </button>
      <button onClick={async () => {
        setShowQIPrinterPicker(false);
        if (!postSaveModal) return;
        const blob = await getInvoicePdfBlob(postSaveModal.invoice);
        if (!blob) return alert('Could not prepare the PDF for printing.');
        const url = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        iframe.src = url;
        document.body.appendChild(iframe);
        let printFired = false;
        const firePrint = () => {
          if (printFired) return;
          printFired = true;
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        };
        iframe.onload = () => setTimeout(firePrint, 300);
        setTimeout(firePrint, 1200);
        setTimeout(() => {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 30000);
      }}
        style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
        Print via Dialog instead
      </button>
    </div>
  </div>
)}
    {/* ─── Global Session Kick Modal (Renders on top of all pages) ─── */}
      {sessionTerminated && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999999,
          background: 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 24,
            padding: '36px 28px',
            maxWidth: 380,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.35)',
            border: '1px solid #fee2e2',
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: '#fef2f2', border: '1.5px solid #fecaca',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', color: '#ef4444',
            }}>
              <Smartphone size={30} />
            </div>

            <h3 style={{ margin: '0 0 8px 0', fontSize: 19, fontWeight: 900, color: '#0f172a' }}>
              Logged Out
            </h3>

            <p style={{ margin: '0 0 24px 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              Your account was just logged in from <strong>another device or browser</strong>. For security and team seat limits, this session has been ended.
            </p>

            <button
              onClick={() => {
                setSessionTerminated(false);
                clearSession();
                window.location.href = '/';
              }}
              style={{
                width: '100%', padding: '12px 16px',
                borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                color: '#ffffff', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8,
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
              }}
            >
              <LogIn size={16} /> Log In on This Device
            </button>
          </div>
        </div>
      )}
    </div>    
  );
}


